import { useState, useEffect, useRef, useCallback } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus, Trash2, RefreshCw, Play, Send, Download, X, Eye,
  Search, Mail, Star, BookOpen, Video, Filter, FileSpreadsheet, FileText,
  CreditCard, Check, AlertTriangle, Clock, Upload, KeyRound,
  CheckCircle, XCircle, ExternalLink,
} from "lucide-react";
import {
  exportDonationsExcel, exportDonationsCSV, exportDonationsPDF,
  exportMobileMoneyExcel, exportMobileMoneyCSV, exportMobileMoneyPDF,
  exportSubscriptionsExcel, exportSubscriptionsCSV, exportSubscriptionsPDF,
} from "../lib/exports.js";
import {
  modules as modulesApi, videos as videosApi,
  announcements as annApi, subscriptions as subsApi,
  askGad as askGadApi, donations as donationsApi,
  payments as paymentsApi, admin as adminApi, auth, manualPayments as manualPaymentsApi,
} from "../lib/api.js";
import { useApi, useDebounce } from "../hooks/index.js";
import { Badge, Spinner, Empty, CloseX, Pagination } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";

// ─── SHARED: DRAG-AND-DROP SORTABLE LIST ─────────────────────────────────────

function SortableItem({ id, children, type = "row" }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const handleSpan = (
    <span {...attributes} {...listeners} style={{ cursor: "grab", color: "var(--ghost)", fontSize: 16, userSelect: "none", touchAction: "none", display: "block", lineHeight: 1, flexShrink: 0 }} title="Drag to reorder">⠿</span>
  );
  if (type === "div") {
    return (
      <div ref={setNodeRef} style={{ ...style, display: "flex", alignItems: "center", gap: 6 }}>
        {handleSpan}
        {children}
      </div>
    );
  }
  return (
    <tr ref={setNodeRef} style={style} onClick={children?.props?.onClick}>
      <td style={{ width: 24, paddingRight: 0 }}>{handleSpan}</td>
      {children?.props?.children}
    </tr>
  );
}

function useSortableList(initialItems, onReorder) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [items, setItems] = useState(initialItems);

  useEffect(() => { setItems(initialItems); }, [initialItems?.map?.(i => i.id).join(",")]);

  const handleDragEnd = useCallback(async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex(i => i.id === active.id);
    const newIdx = items.findIndex(i => i.id === over.id);
    const reordered = arrayMove(items, oldIdx, newIdx).map((item, idx) => ({ ...item, sortOrder: idx }));
    setItems(reordered);
    await onReorder(reordered.map(({ id, sortOrder }) => ({ id, sortOrder })));
  }, [items, onReorder]);

  return { sensors, items, handleDragEnd };
}

function PermanentDeleteModal({ title, description, onConfirm, onClose, loading }) {
  const [step, setStep]           = useState("confirm");
  const [password, setPassword]   = useState("");
  const [showPwd, setShowPwd]     = useState(false);
  const [pwdError, setPwdError]   = useState("");
  const [verifying, setVerifying] = useState(false);

  const handleDelete = async () => {
    if (!password) { setPwdError("Enter your password to confirm."); return; }
    setVerifying(true);
    setPwdError("");
    try {
      await auth.verifyPassword(password);
      await onConfirm();
    } catch (e) {
      if (e.status === 422) setPwdError("Incorrect password. Try again.");
      else setPwdError(e.message ?? "Verification failed.");
    } finally { setVerifying(false); }
  };

  const busy = loading || verifying;

  const S = {
    overlay: { position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(2px)" },
    modal:   { width: "100%", maxWidth: 400, borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)", boxShadow: "0 24px 64px rgba(0,0,0,.4)", background: "var(--card)" },
    stripe:  { height: 3, background: "var(--rose)" },
    body:    { padding: "22px 22px 22px" },
    steps:   { display: "flex", alignItems: "center", gap: 4, marginBottom: 14 },
    sp:      (active) => ({ width: 18, height: 3, borderRadius: 2, background: active ? "var(--rose)" : "var(--border)" }),
    hint:    { fontSize: 11, color: "var(--ghost)", marginLeft: "auto", fontFamily: "var(--fm)" },
    chip:    { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, letterSpacing: ".04em", color: "var(--rose)", background: "var(--rose-lo)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 4, padding: "3px 8px", marginBottom: 12 },
    ttl:     { fontSize: 16, fontWeight: 700, color: "var(--ink)", lineHeight: 1.3, marginBottom: 4 },
    sub:     { fontSize: 13, color: "var(--ink2)", lineHeight: 1.55, marginBottom: 16 },
    divider: { border: "none", borderTop: "1px solid var(--border)", margin: "14px 0" },
    infoBox: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "11px 13px", marginBottom: 14 },
    infoRow: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: "var(--ink2)", lineHeight: 1.5 },
    warnBox: { background: "var(--rose-lo)", border: "1px solid rgba(239,68,68,.18)", borderRadius: 6, padding: "9px 11px", marginBottom: 18, display: "flex", alignItems: "flex-start", gap: 7 },
    warnTxt: { fontSize: 12, color: "var(--rose)", fontWeight: 500, lineHeight: 1.5 },
    btnRow:  { display: "flex", gap: 8 },
    btnOut:  { flex: 1, height: 36, borderRadius: 7, fontSize: 13.5, fontWeight: 500, cursor: "pointer", background: "transparent", border: "1px solid var(--border)", color: "var(--ink2)" },
    btnDel:  (dis) => ({ flex: 1, height: 36, borderRadius: 7, fontSize: 13.5, fontWeight: 600, cursor: dis ? "default" : "pointer", background: "var(--rose)", color: "#fff", border: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: dis ? .6 : 1 }),
    fldLbl:  { fontSize: 11.5, fontWeight: 500, color: "var(--ink2)", marginBottom: 5, letterSpacing: ".02em" },
    fldWrap: { position: "relative" },
    fld:     (err) => ({ width: "100%", height: 38, borderRadius: 7, background: "var(--surface)", border: `1px solid ${err ? "var(--rose)" : "var(--border)"}`, color: "var(--ink)", fontSize: 13.5, padding: "0 38px 0 12px", outline: "none", fontFamily: "inherit", transition: "border-color .15s" }),
    eye:     { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", fontSize: 15, display: "flex", alignItems: "center", padding: 2 },
    errTxt:  { fontSize: 11.5, color: "var(--rose)", marginTop: 5, display: "flex", alignItems: "center", gap: 4 },
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && !busy && onClose()}>
      <div style={S.modal} role="dialog" aria-modal="true">
        <div style={S.stripe} />
        <div style={S.body}>

          {/* Step indicators */}
          <div style={S.steps}>
            <div style={S.sp(true)} />
            <div style={S.sp(step === "password")} />
            <span style={S.hint}>Step {step === "confirm" ? 1 : 2} of 2</span>
          </div>

          {step === "confirm" ? (
            <>
              <div style={S.chip}>⚠ Destructive action</div>
              <div style={S.ttl}>Delete permanently</div>
              <div style={S.sub}>This action removes all data with no way to recover it.</div>
              <hr style={S.divider} />
              <div style={S.infoBox}>
                <div style={S.infoRow}>{description}</div>
              </div>
              <div style={S.warnBox}>
                <span style={{ color: "var(--rose)", fontSize: 13, flexShrink: 0, marginTop: 1 }}>ⓘ</span>
                <p style={S.warnTxt}>All files and records will be permanently erased from storage.</p>
              </div>
              <div style={S.btnRow}>
                <button style={S.btnOut} onClick={onClose}>Cancel</button>
                <button style={S.btnDel(false)} onClick={() => setStep("password")}>
                  Continue →
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={S.chip}>🔒 Identity verification</div>
              <div style={S.ttl}>Confirm your identity</div>
              <div style={{ ...S.sub, marginBottom: 18 }}>Enter your password to authorise this permanent deletion.</div>
              <div style={{ marginBottom: 18 }}>
                <div style={S.fldLbl}>Your password</div>
                <div style={S.fldWrap}>
                  <input
                    style={S.fld(!!pwdError)}
                    type={showPwd ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setPwdError(""); }}
                    onKeyDown={e => e.key === "Enter" && !busy && handleDelete()}
                    autoFocus
                    disabled={busy}
                    aria-label="Password"
                  />
                  <button style={S.eye} onClick={() => setShowPwd(v => !v)} tabIndex={-1} aria-label={showPwd ? "Hide password" : "Show password"}>
                    {showPwd ? "🙈" : "👁"}
                  </button>
                </div>
                {pwdError && (
                  <div style={S.errTxt}>
                    <span style={{ fontSize: 12 }}>⚠</span>
                    {pwdError}
                  </div>
                )}
              </div>
              <div style={S.btnRow}>
                <button style={S.btnOut} onClick={onClose} disabled={busy}>Cancel</button>
                <button style={S.btnDel(busy || !password)} onClick={handleDelete} disabled={busy || !password}>
                  {busy ? "Verifying…" : "🗑 Delete forever"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── SHARED: INFO GRID ────────────────────────────────────────────────────────

function InfoGrid({ items, cols = 2 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8, marginBottom: 12 }}>
      {items.map(([label, value]) => (
        <div key={label} style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 12px", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 3 }}>{label}</div>
          <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

function UserCard({ user }) {
  if (!user) return null;
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", padding: 14, background: "var(--panel-dark)", borderRadius: 12, marginBottom: 14 }}>
      {user.avatarUrl ? (
        <img src={user.avatarUrl} alt={user.name} style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg, var(--teal), #086b47)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "white", fontWeight: 900, flexShrink: 0 }}>
          {user.name?.[0]?.toUpperCase() ?? "?"}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 15, color: "white" }}>{user.name}</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginTop: 2 }}>{user.email}</div>
        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ background: "rgba(13,184,126,.3)", color: "var(--teal)", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>{user.subscriptionStatus}</span>
          {user.childProfile && (
            <span style={{ background: "rgba(255,255,255,.1)", color: "rgba(255,255,255,.7)", fontSize: 10, padding: "2px 8px", borderRadius: 20 }}>
              🧒 {user.childProfile.childName}, {user.childProfile.ageMonths}mo
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ASK DR. GAD ─────────────────────────────────────────────────────────────

function GadVideoPlayer({ url, label }) {
  const videoRef = useRef(null);
  const [ended, setEnded] = useState(false);
  const [error, setError] = useState(false);

  if (!url) return null;

  const replay = () => {
    setEnded(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  return (
    <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "#000", marginTop: 8, marginBottom: 4 }}>
      {error ? (
        <div style={{ padding: "24px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>⚠️</div>
          <div style={{ color: "white", fontFamily: "var(--fd)", fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Could not load video</div>
          <a href={url} target="_blank" rel="noreferrer" className="btn btn-teal btn-sm" style={{ display: "inline-flex" }}>⬆ Open file directly</a>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            src={url}
            controls
            playsInline
            style={{ display: "block", width: "100%", maxHeight: 320, background: "#000", outline: "none" }}
            onEnded={() => setEnded(true)}
            onPlay={() => setEnded(false)}
            onError={() => setError(true)}
          />
          {ended && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.88)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, zIndex: 10 }}>
              <div style={{ fontSize: 32 }}>🎥</div>
              <div style={{ color: "white", fontFamily: "var(--fd)", fontSize: 13, fontWeight: 700 }}>{label ?? "Video ended"}</div>
              <button className="btn btn-teal" onClick={replay} style={{ padding: "6px 22px", fontSize: 12 }}>Replay</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GadDetailModal({ sub, onClose, onRespond, hasPermission }) {
  const { t } = useLang();
  const { data, loading } = useApi(() => askGadApi.getById(sub.id), [sub.id]);
  const s = data?.submission ?? sub;
  const u = s.user ?? sub.user;

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-ttl">{t("askgad.submissionDetails")} <CloseX onClick={onClose} /></div>
        {(s.assignedTo || s.escalatedAt) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {s.assignedTo && (
              <div style={{ fontSize: 11, color: "var(--muted)" }}>
                📋 {t("askgad.assigned")}: <strong style={{ color: "var(--ink2)" }}>{s.assignedTo.name}</strong>
                {s.assignedAt && <span> · {new Date(s.assignedAt).toLocaleDateString()}</span>}
              </div>
            )}
            {s.escalatedAt && (
              <div style={{ background: "var(--rose-lo)", border: "1px solid var(--rose)", borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--rose)", textTransform: "uppercase", marginBottom: 4 }}>
                  🚨 {t("askgad.statusEscalated")} · {new Date(s.escalatedAt).toLocaleDateString()}
                </div>
                <div style={{ fontSize: 12, color: "var(--ink2)" }}>{s.escalationReason}</div>
              </div>
            )}
          </div>
        )}
        <UserCard user={u} />
        {u?.childProfile && (
          <div style={{ background: "var(--teal-dim)", border: "1px solid var(--teal-lo)", borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--teal)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 6 }}>🧒 {t("users.childProfile")}</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div><div style={{ fontSize: 10, color: "var(--muted)" }}>{t("users.childName")}</div><div style={{ fontSize: 13, fontWeight: 700 }}>{u.childProfile.childName}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--muted)" }}>{t("users.age")}</div><div style={{ fontSize: 13, fontWeight: 700 }}>{t("users.ageMonths", { n: u.childProfile.ageMonths })}</div></div>
            </div>
            {u.childProfile.challenges?.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {u.childProfile.challenges.map(c => (
                  <span key={c} style={{ background: "white", border: "1px solid var(--teal-lo)", color: "var(--teal)", fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20 }}>{c.replace(/_/g, " ")}</span>
                ))}
              </div>
            )}
          </div>
        )}
        <div style={{ background: "var(--surface)", borderRadius: 10, padding: 14, marginBottom: 12, borderLeft: "3px solid var(--rose)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", marginBottom: 6 }}>❓ Question · {new Date(s.createdAt).toLocaleDateString()}</div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink2)" }}>{s.questionText}</div>
          {s.videoR2Key && (
            loading ? (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, background: "var(--teal-dim)", borderRadius: 8, padding: "6px 10px" }}>
                <span>🎥</span><span style={{ fontSize: 11, color: "var(--teal)", fontWeight: 600 }}>Loading video…</span>
              </div>
            ) : (
              <GadVideoPlayer url={s.videoUrl} label="Video ended" />
            )
          )}
        </div>
        {s.responseText ? (
          <div style={{ background: "var(--teal-dim)", borderRadius: 10, padding: 14, marginBottom: 12, borderLeft: "3px solid var(--teal)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--teal)", textTransform: "uppercase", marginBottom: 6 }}>✅ Dr. Gad's Response · {s.respondedAt ? new Date(s.respondedAt).toLocaleDateString() : ""}</div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink2)" }}>{s.responseText}</div>
            {s.responseVideoR2Key && <GadVideoPlayer url={s.responseVideoUrl} label="Response video ended" />}
          </div>
        ) : hasPermission?.('ANSWER_QUESTIONS') ? (
          <button className="btn btn-teal" style={{ width: "100%", justifyContent: "center" }} onClick={() => { onClose(); onRespond(sub); }}>
            ✍️ {t("askgad.writeResponse")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function GadRespondModal({ sub, onClose, toast, onSuccess }) {
  const { t } = useLang();
  const [resp, setResp] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!resp.trim()) { toast("Response text is required"); return; }
    setSending(true);
    try {
      await askGadApi.respond(sub.id, { responseText: resp });
      toast(`✅ Response sent! ${sub.user?.name ?? "User"} notified via push.`);
      onSuccess(); onClose();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="modal-ov open">
      <div className="modal">
        <div className="modal-ttl">{t("askgad.respondTitle")} <CloseX onClick={onClose} /></div>
        <div style={{ background: "var(--surface)", borderRadius: 8, padding: 12, marginBottom: 14, borderLeft: "3px solid var(--rose)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", marginBottom: 4 }}>Question · {sub.user?.name}</div>
          <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.5 }}>{sub.questionText}</div>
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("askgad.yourResponse")} <span style={{ fontSize: 9, color: "var(--ghost)" }}>{t("askgad.pushNoteSent")}</span></label>
          <textarea className="f-ta" rows={7} value={resp} onChange={e => setResp(e.target.value)} placeholder={t("askgad.responsePlaceholder")} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.cancel")}</button>
          <button className="btn btn-teal" style={{ flex: 1 }} onClick={send} disabled={sending || !resp.trim()}>
            <Send size={12} /> {sending ? t("common.sending") : t("askgad.sendResponse")}
          </button>
        </div>
      </div>
    </div>
  );
}

function GadEscalateModal({ sub, onClose, toast, onSuccess }) {
  const { t } = useLang();
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!reason.trim()) { toast(t("askgad.escalationReason") + " required"); return; }
    setSending(true);
    try {
      await askGadApi.escalate(sub.id, reason.trim());
      toast(`🚨 ${t("askgad.escalateSuccess")}`);
      onSuccess(); onClose();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="modal-ov open">
      <div className="modal">
        <div className="modal-ttl">{t("askgad.escalateTitle")} <CloseX onClick={onClose} /></div>
        <div style={{ background: "var(--surface)", borderRadius: 8, padding: 12, marginBottom: 14, borderLeft: "3px solid var(--rose)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", marginBottom: 4 }}>Question · {sub.user?.name}</div>
          <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.5 }}>{sub.questionText}</div>
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("askgad.escalationReason")}</label>
          <textarea className="f-ta" rows={4} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.cancel")}</button>
          <button className="btn btn-teal" style={{ flex: 1 }} onClick={send} disabled={sending || !reason.trim()}>
            <AlertTriangle size={12} /> {sending ? t("common.sending") : t("askgad.escalate")}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignControl({ sub, onSuccess, toast }) {
  const { t } = useLang();
  const { data } = useApi(() => askGadApi.assignees(), []);
  const agents = data?.agents ?? [];
  const [busy, setBusy] = useState(false);

  const assign = async (agentId) => {
    if (!agentId) return;
    setBusy(true);
    try {
      await askGadApi.assign(sub.id, agentId);
      toast(`📋 ${t("askgad.assignSuccess")}`);
      onSuccess();
    } catch (e) { toast("Error: " + e.message); }
    finally { setBusy(false); }
  };

  return (
    <select className="f-in" style={{ fontSize: 11, padding: "4px 8px", height: "auto" }} disabled={busy}
      value={sub.assignedToId ?? ""} onChange={e => assign(e.target.value)}>
      <option value="">{t("askgad.unassigned")}</option>
      {agents.map(a => (
        <option key={a.id} value={a.id}>{a.name ?? a.email}</option>
      ))}
    </select>
  );
}

export function PageAskGad({ toast, search: externalSearch, hasPermission, me }) {
  const { t } = useLang();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState(externalSearch ?? "");
  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);
  const [selView, setSelView] = useState(null);
  const [selResp, setSelResp] = useState(null);
  const [selEscalate, setSelEscalate] = useState(null);
  const [myQueue, setMyQueue] = useState(false);

  const debSearch = useDebounce(search, 400);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [status, debSearch, myQueue]);
  const params = { ...(status && { status }), ...(debSearch && { search: debSearch }), ...(myQueue && me?.id && { assignedToId: me.id }), page, limit: 20 };
  const { data, loading, reload } = useApi(() => askGadApi.queue(params), [status, debSearch, page, myQueue]);

  const subs = data?.submissions ?? [];
  const total = data?.total ?? 0;
  const pending = subs.filter(s => s.status === "SUBMITTED").length;
  const reviewed = subs.filter(s => s.status === "UNDER_REVIEW").length;
  const responded = subs.filter(s => s.status === "RESPONDED").length;
  const escalated = subs.filter(s => s.status === "ESCALATED").length;

  const statusColors = { SUBMITTED: "var(--rose)", UNDER_REVIEW: "var(--amber)", RESPONDED: "var(--teal)", ESCALATED: "var(--rose)" };
  const statusLabels = { SUBMITTED: t("askgad.statusSubmitted"), UNDER_REVIEW: t("askgad.statusUnderReview"), RESPONDED: t("askgad.statusResponded"), ESCALATED: t("askgad.statusEscalated") };

  return (
    <div>
      {/* Clickable Stats — filter list on click */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { cls: "sc-rose", val: pending, lbl: t("askgad.pending"), sub: t("askgad.awaitingResponse"), filter: "SUBMITTED" },
          { cls: "sc-amber", val: reviewed, lbl: t("askgad.underReview"), sub: t("askgad.beingReviewed"), filter: "UNDER_REVIEW" },
          { cls: "sc-teal", val: responded, lbl: t("askgad.responded"), sub: t("askgad.completed"), filter: "RESPONDED" },
          { cls: "sc-rose", val: escalated, lbl: t("askgad.statusEscalated"), sub: "🚨", filter: "ESCALATED" },
          { cls: "sc-blue", val: total, lbl: t("askgad.total"), sub: t("askgad.allSubmissions"), filter: "" },
        ].map(s => (
          <div key={s.lbl} className={`stat-card ${s.cls}`}
            onClick={() => setStatus(status === s.filter ? "" : s.filter)}
            style={{ cursor: "pointer", outline: status === s.filter ? "2px solid white" : "none", transition: "transform .15s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}>
            <div className="sc-lbl">{s.lbl}</div>
            <div className="sc-val">{s.val}</div>
            <div className="sc-sub">{s.sub}</div>
            <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 9, color: "rgba(255,255,255,.3)", fontFamily: "var(--fm)" }}>{status === s.filter ? t("ui.active") : t("ui.clickToFilter")}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 300, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input placeholder={t("common.search")} value={search} onChange={e => setSearch(e.target.value)} style={{ border: "none", background: "none", outline: "none", fontFamily: "var(--fb)", fontSize: 13, flex: 1, color: "var(--ink)" }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)" }}><X size={12} /></button>}
        </div>
        {[["", t("filter.all")], ["SUBMITTED", t("askgad.statusSubmitted")], ["UNDER_REVIEW", t("askgad.filterReview")], ["RESPONDED", t("askgad.filterDone")], ["ESCALATED", t("askgad.statusEscalated")]].map(([v, lbl]) => (
          <button key={v} className={`btn btn-sm ${status === v ? "btn-teal" : "btn-out"}`} onClick={() => setStatus(v)}>{lbl}</button>
        ))}
        {(hasPermission?.('ANSWER_QUESTIONS') || hasPermission?.('ASSIGN_QUESTIONS')) && (
          <button className={`btn btn-sm ${myQueue ? "btn-teal" : "btn-out"}`} onClick={() => setMyQueue(v => !v)}>📋 {t("askgad.myQueue")}</button>
        )}
        <button className="btn btn-out btn-sm" onClick={() => { setStatus(""); setSearch(""); setMyQueue(false); }}><RefreshCw size={12} /></button>
      </div>

      {/* Submissions */}
      {loading ? <Spinner /> : subs.length === 0 ? <Empty icon="🎤" message={t("askgad.noMatch")} /> : subs.map(s => (
        <div key={s.id} className="card" style={{ borderLeft: `3px solid ${statusColors[s.status] ?? "var(--border)"}`, marginBottom: 12, cursor: "pointer" }}
          onClick={() => setSelView(s)}>
          <div style={{ padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, flex: 1, lineHeight: 1.5, color: "var(--ink)" }}>"{s.questionText}"</div>
              <span className={`badge ${s.status === "RESPONDED" ? "bg-green" : s.status === "UNDER_REVIEW" ? "bg-orange" : s.status === "ESCALATED" ? "bg-purple" : "bg-red"}`}>
                {statusLabels[s.status] ?? s.status}
              </span>
            </div>

            {/* User info */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              {s.user?.avatarUrl ? (
                <img src={s.user.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
              ) : (
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "var(--teal)", flexShrink: 0 }}>
                  {s.user?.name?.[0] ?? "?"}
                </div>
              )}
              <div>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink2)" }}>{s.user?.name}</span>
                <span style={{ fontSize: 11, color: "var(--ghost)", marginLeft: 8 }}>{s.user?.email}</span>
                {s.user?.childProfile && <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: 8 }}>· {s.user.childProfile.childName}, {s.user.childProfile.ageMonths}mo</span>}
              </div>
              {s.videoR2Key && <span style={{ background: "var(--teal-dim)", borderRadius: 6, padding: "2px 7px", fontSize: 10, color: "var(--teal)", marginLeft: "auto" }}>🎥 Video</span>}
            </div>

            {/* Assignment row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
              {hasPermission?.('ASSIGN_QUESTIONS') ? (
                <>
                  <span style={{ fontSize: 10, color: "var(--ghost)" }}>📋 {t("askgad.assignTo")}:</span>
                  <AssignControl sub={s} onSuccess={reload} toast={toast} />
                </>
              ) : (
                <span style={{ fontSize: 10, color: "var(--ghost)" }}>
                  📋 {s.assignedTo ? <strong style={{ color: "var(--ink2)" }}>{s.assignedTo.name}</strong> : t("askgad.unassigned")}
                </span>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>{new Date(s.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
              <div style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
                <button className="btn btn-out btn-xs" onClick={() => setSelView(s)}><Eye size={10} /> {t("users.view")}</button>
                {s.status !== "RESPONDED" && hasPermission?.('ANSWER_QUESTIONS') && <button className="btn btn-teal btn-xs" onClick={() => setSelResp(s)}>✍️ {t("askgad.writeResponse")}</button>}
                {s.status !== "ESCALATED" && hasPermission?.('ESCALATE_QUESTIONS') && <button className="btn btn-out btn-xs" onClick={() => setSelEscalate(s)}>🚨 {t("askgad.escalate")}</button>}
              </div>
            </div>

            {s.status === "RESPONDED" && s.responseText && (
              <div style={{ marginTop: 10, padding: 10, background: "var(--teal-dim)", borderRadius: 8, fontSize: 11, color: "var(--ink2)", lineHeight: 1.5 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "var(--teal)", textTransform: "uppercase", marginBottom: 3 }}>{t("askgad.drGadResponse")}</div>
                {s.responseText.length > 180 ? s.responseText.slice(0, 180) + "…" : s.responseText}
              </div>
            )}
          </div>
        </div>
      ))}

      {!loading && total > 0 && (
        <Pagination page={page} totalPages={Math.max(1, Math.ceil(total / 20))} total={total}
          onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
      )}
      {selView && <GadDetailModal sub={selView} onClose={() => setSelView(null)} onRespond={s => { setSelView(null); setSelResp(s); }} toast={toast} hasPermission={hasPermission} />}
      {selResp && <GadRespondModal sub={selResp} onClose={() => setSelResp(null)} toast={toast} onSuccess={reload} />}
      {selEscalate && <GadEscalateModal sub={selEscalate} onClose={() => setSelEscalate(null)} toast={toast} onSuccess={reload} />}
    </div>
  );
}

// ─── SUBSCRIPTIONS ────────────────────────────────────────────────────────────

const SUB_PROVIDER_LABEL = { mpesa: "M-Pesa", mtn_momo: "MTN MoMo", airtel_money: "Airtel Money", vodacom_mpesa: "Vodacom M-Pesa" };

function SubDetailModal({ sub, onClose, onCancel, onRestore, hasPermission }) {
  const { t } = useLang();
  const { data, loading } = useApi(() => subsApi.getById(sub.userId), [sub.userId]);
  const s = data?.subscription ?? sub;
  const u = s.user ?? sub.user;

  const fmtDate     = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const platColor   = PLT_COLORS_CP[s.platform] ?? "var(--ghost)";
  const platLabel   = PLT_LABELS_CP[s.platform] ?? s.platform ?? "—";

  const InfoRow = ({ label, value, mono }) => (
    <div style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 14px", border: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 700, flexShrink: 0, paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, fontFamily: mono ? "var(--fm)" : "var(--fb)", color: "var(--ink)", textAlign: "right", wordBreak: "break-all" }}>{value ?? "—"}</div>
    </div>
  );

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 10, fontWeight: 800, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: "1px", marginTop: 6, marginBottom: 4, paddingLeft: 2 }}>{children}</div>
  );

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="modal-ttl">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>{PLT_ICON[s.platform] ?? "💳"}</span>
            {t("subs.details")}
          </div>
          <CloseX onClick={onClose} />
        </div>

        {/* User card */}
        <div style={{ display: "flex", gap: 14, alignItems: "center", padding: 16, background: "var(--panel-dark)", borderRadius: 12, marginBottom: 16 }}>
          {u?.avatarUrl
            ? <img src={u.avatarUrl} alt="" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
            : <div style={{ width: 48, height: 48, borderRadius: "50%", background: `${platColor}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 22 }}>{PLT_ICON[s.platform] ?? "💳"}</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--fd)", fontSize: 15, fontWeight: 900, color: "white", marginBottom: 2 }}>{u?.name ?? "Unknown"}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>{u?.email ?? "—"}</div>
            {u?.childProfile && (
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", marginTop: 3 }}>
                👶 {u.childProfile.childName}{u.childProfile.ageMonths ? ` · ${u.childProfile.ageMonths} months` : ""}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <Badge status={s.status} />
            <span style={{ fontSize: 9, background: platColor, color: "#fff", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>{platLabel}</span>
          </div>
        </div>

        {/* Subscription */}
        <SectionLabel>Subscription</SectionLabel>
        <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Plan"     value={PLAN_LABEL(s.plan)} />
          <InfoRow label="Status"   value={s.status} />
          <InfoRow label="Platform" value={platLabel} />
          {s.amount != null && <InfoRow label="Amount Charged" value={`${s.currency ?? "USD"} ${Number(s.amount).toFixed(2)}`} />}
          {s.promoCode && <InfoRow label="Promo Code" value={s.promoCode} mono />}
          {(s.questionAddonUsd > 0) && <InfoRow label="Question Add-on" value={`+$${s.questionAddonUsd}/renewal`} />}
        </div>

        {/* Billing period */}
        <SectionLabel>Billing Period</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Period Start"  value={fmtDate(s.periodStart)} />
          <InfoRow label="Period End"    value={fmtDate(s.periodEnd)} />
          <InfoRow label="Created"       value={fmtDate(s.createdAt)} />
          {s.cancelledAt && <InfoRow label="Cancelled At" value={fmtDateTime(s.cancelledAt)} />}
        </div>

        {/* Card info */}
        {(s.cardLast4 || s.cardBrand) && (
          <>
            <SectionLabel>Card Used</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              {s.cardBrand && <InfoRow label="Card Brand"    value={s.cardBrand} />}
              {s.cardLast4 && <InfoRow label="Last 4 Digits" value={`•••• ${s.cardLast4}`} mono />}
            </div>
          </>
        )}

        {/* Mobile Money */}
        {(s.mobileMoneyProvider || s.mobileMoneyPhone) && (
          <>
            <SectionLabel>Mobile Money</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              <InfoRow label="Provider" value={SUB_PROVIDER_LABEL[s.mobileMoneyProvider] ?? s.mobileMoneyProvider} />
              <InfoRow label="Phone"    value={s.mobileMoneyPhone} mono />
            </div>
          </>
        )}

        {/* Transaction reference */}
        <SectionLabel>Transaction Reference</SectionLabel>
        <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
          {(() => { const { label, value } = getTxRef(s); return value ? <InfoRow label={label} value={value} mono /> : null; })()}
          <InfoRow label="Record ID" value={s.id} mono />
        </div>

        {/* Auto-renewal */}
        {(s.autoRenewAttemptedAt || s.autoRenewFailedAt) && (
          <>
            <SectionLabel>Auto-Renewal</SectionLabel>
            <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
              {s.autoRenewAttemptedAt && <InfoRow label="Last Attempt" value={fmtDateTime(s.autoRenewAttemptedAt)} />}
              {s.autoRenewFailedAt    && <InfoRow label="Last Failure"  value={fmtDateTime(s.autoRenewFailedAt)} />}
            </div>
          </>
        )}

        {hasPermission?.('MANAGE_SUBSCRIPTIONS') && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {(s.status === "CANCELLED" || s.status === "EXPIRED")
              ? <button className="btn btn-teal" style={{ flex: 1 }} onClick={() => { onRestore(s.userId); onClose(); }}>{t("subs.restoreBtn")}</button>
              : <button className="btn btn-danger" style={{ flex: 1 }} onClick={() => { onCancel(s.userId); onClose(); }}>{t("subs.cancelBtn")}</button>}
          </div>
        )}
      </div>
    </div>
  );
}

export function PageSubscriptions({ toast, search: externalSearch, hasPermission }) {
  const { t } = useLang();
  const [platform, setPlatform] = useState("");
  const [status, setStatus]     = useState("");
  const [plan, setPlan]         = useState("");
  const [search, setSearch]     = useState(externalSearch ?? "");
  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);
  const [page, setPage]         = useState(1);
  const [selSub, setSelSub]     = useState(null);
  const [exporting, setExporting] = useState(false);

  const debSearch = useDebounce(search, 400);
  const params    = { page, limit: 20, ...(platform && { platform }), ...(status && { status }), ...(plan && { plan }), ...(debSearch && { search: debSearch }) };
  const { data, loading, reload } = useApi(() => subsApi.list(params), [page, platform, status, plan, debSearch]);

  // Load totals by status (unfiltered) for stat cards
  const { data: totalsData } = useApi(() => subsApi.list({ limit: 1 }));
  const { data: activeTotals } = useApi(() => subsApi.list({ status: "ACTIVE", limit: 1 }));
  const { data: trialTotals } = useApi(() => subsApi.list({ status: "TRIAL", limit: 1 }));
  const { data: cancelledTotals } = useApi(() => subsApi.list({ status: "CANCELLED", limit: 1 }));
  const { data: expiredTotals } = useApi(() => subsApi.list({ status: "EXPIRED", limit: 1 }));

  const subs  = data?.subscriptions ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  const statCounts = {
    ACTIVE:    activeTotals?.total ?? 0,
    TRIAL:     trialTotals?.total ?? 0,
    CANCELLED: cancelledTotals?.total ?? 0,
    EXPIRED:   expiredTotals?.total ?? 0,
  };

  const handle = async (action, userId) => {
    try {
      action === "cancel" ? await subsApi.cancel(userId) : await subsApi.restore(userId);
      toast(action === "cancel" ? "Subscription cancelled" : "✅ Subscription restored!");
      reload();
    } catch (e) { toast("Error: " + e.message); }
  };

  const reset = () => { setPlatform(""); setStatus(""); setPlan(""); setSearch(""); setPage(1); };

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      // Fetch all matching records (up to 500) for export
      const all = await subsApi.list({ ...(platform && { platform }), ...(status && { status }), ...(plan && { plan }), ...(debSearch && { search: debSearch }), limit: 500 });
      const rows = all?.subscriptions ?? [];
      if (fmt === "xlsx") { exportSubscriptionsExcel(rows); toast("✅ Excel downloaded!"); }
      if (fmt === "csv")  { exportSubscriptionsCSV(rows);  toast("✅ CSV downloaded!"); }
      if (fmt === "pdf")  { exportSubscriptionsPDF(rows, { platform, status, plan }); toast("✅ PDF downloaded!"); }
    } catch (e) { toast("Export failed: " + e.message); }
    finally { setExporting(false); }
  };

  return (
    <div>
      {/* Stat cards — show real totals across all pages */}
      <div className="stats-grid">
        {[
          ["sc-teal",  "ACTIVE",    t("users.active").replace("🟢 ","")],
          ["sc-amber", "TRIAL",     "Trial"],
          ["sc-rose",  "CANCELLED", t("users.cancelled").replace("🔴 ","")],
          ["sc-blue",  "EXPIRED",   t("users.expired").replace("🟠 ","")],
        ].map(([cls, st, lbl]) => (
          <div key={lbl} className={`stat-card ${cls}`}
            style={{ cursor: "pointer", outline: status === st ? "2px solid rgba(255,255,255,.4)" : "none" }}
            onClick={() => { setStatus(status === st ? "" : st); setPage(1); }}>
            <div className="sc-lbl">{lbl}</div>
            <div className="sc-val">{statCounts[st]}</div>
            <div className="sc-sub">{status === st ? t("subs.filtering") : t("subs.clickToFilter")}</div>
          </div>
        ))}
      </div>

      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 280, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input placeholder={t("common.search")} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ border: "none", background: "none", outline: "none", fontFamily: "var(--fb)", fontSize: 13, flex: 1, color: "var(--ink)" }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)" }}><X size={12} /></button>}
        </div>
        <select className="f-sel" style={{ width: "auto" }} value={platform} onChange={e => { setPlatform(e.target.value); setPage(1); }}>
          <option value="">{t("subs.allPlatforms")}</option>
          <option value="apple_iap">Apple IAP</option>
          <option value="google_play">Google Play</option>
          <option value="stripe">Stripe</option>
          <option value="flutterwave">Flutterwave</option>
          <option value="scholarship">Scholarship</option>
        </select>
        <select className="f-sel" style={{ width: "auto" }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">{t("subs.allStatus")}</option>
          <option value="ACTIVE">{t("subs.statusActive")}</option>
          <option value="TRIAL">{t("subs.statusTrial")}</option>
          <option value="CANCELLED">{t("subs.statusCancelled")}</option>
          <option value="EXPIRED">{t("subs.statusExpired")}</option>
          <option value="SCHOLARSHIP">{t("subs.statusScholarship")}</option>
        </select>
        <select className="f-sel" style={{ width: "auto" }} value={plan} onChange={e => { setPlan(e.target.value); setPage(1); }}>
          <option value="">{t("subs.allPlans")}</option>
          <option value="monthly">{t("subs.planMonthly")}</option>
          <option value="annual">{t("subs.planAnnual")}</option>
          <option value="scholarship">{t("subs.planScholarship")}</option>
        </select>
        {(platform || status || plan || search) && <button className="btn btn-out btn-sm" onClick={reset}><RefreshCw size={12} /> {t("users.clear")}</button>}
      </div>

      <div className="card">
        <div className="card-hdr">
          <div className="card-ttl"><div className="card-ico">💳</div>{t("subs.title", { n: total })}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button className="btn btn-out btn-sm" onClick={() => handleExport("xlsx")} disabled={exporting}><FileSpreadsheet size={11} /> XLS</button>
            <button className="btn btn-out btn-sm" onClick={() => handleExport("csv")}  disabled={exporting}><Download size={11} /> CSV</button>
            <button className="btn btn-out btn-sm" onClick={() => handleExport("pdf")}  disabled={exporting}><FileText size={11} /> PDF</button>
          </div>
        </div>
        {loading ? <Spinner /> : subs.length === 0 ? <div style={{ padding: 24 }}><Empty icon="💳" message={t("subs.noMatch")} /></div> : (
          <div className="tbl-wrap"><table>
            <thead><tr><th>{t("tbl.user")}</th><th>{t("tbl.plan")}</th><th>{t("tbl.platform")}</th><th>{t("tbl.status")}</th><th>{t("subs.periodEnd")}</th><th>{t("tbl.action")}</th></tr></thead>
            <tbody>{subs.map(s => (
              <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => setSelSub(s)}>
                <td><div style={{ fontWeight: 700 }}>{s.user?.name ?? "—"}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{s.user?.email}</div></td>
                <td><span className="badge bg-teal">{s.plan}</span></td>
                <td><span className="badge bg-blue">{s.platform}</span></td>
                <td><Badge status={s.status} /></td>
                <td style={{ fontFamily: "var(--fm)", fontSize: 11 }}>{s.periodEnd ? new Date(s.periodEnd).toLocaleDateString() : "—"}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="btn btn-out btn-xs" onClick={() => setSelSub(s)}><Eye size={10} /></button>
                    {hasPermission?.('MANAGE_SUBSCRIPTIONS') && ((s.status === "CANCELLED" || s.status === "EXPIRED")
                      ? <button className="btn btn-teal btn-xs" onClick={() => handle("restore", s.userId)}>{t("subs.restore")}</button>
                      : <button className="btn btn-out btn-xs" style={{ color: "var(--rose)" }} onClick={() => handle("cancel", s.userId)}>{t("subs.cancel")}</button>)}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--ghost)" }}>{t("users.pageOf", { page, totalPages, total })}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-out btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ {t("common.prev")}</button>
            <button className="btn btn-teal btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t("common.next")} ›</button>
          </div>
        </div>
      </div>

      {selSub && <SubDetailModal sub={selSub} onClose={() => setSelSub(null)} onCancel={u => handle("cancel", u)} onRestore={u => handle("restore", u)} hasPermission={hasPermission} />}
    </div>
  );
}

// ─── MODULES ─────────────────────────────────────────────────────────────────

const RESOURCE_ICONS = { PDF: "📄", BOOK: "📚", LINK: "🔗", AUDIO: "🎵", WORKSHEET: "📝", IMAGE: "🖼" };
const RESOURCE_COLORS = { PDF: "rose", BOOK: "blue", LINK: "teal", AUDIO: "amber", WORKSHEET: "green", IMAGE: "gray" };

function ModuleDetailModal({ mod, onClose, onPublish, onUnpublish, onDelete, onManageResources }) {
  const { t } = useLang();
  const { data, loading } = useApi(() => modulesApi.getDetails(mod.id), [mod.id]);
  const m = data?.module ?? mod;

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-ttl">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="tc">{m.code}</span>
            <span>{m.title}</span>
          </div>
          <CloseX onClick={onClose} />
        </div>

        {loading ? <Spinner /> : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <Badge status={m.status} />
              {m.isPreview && <span className="badge bg-teal">{t("mods.freePreview")}</span>}
              <span style={{ background: "var(--surface)", border: "1px solid var(--border)", fontSize: 11, padding: "3px 8px", borderRadius: 20, color: "var(--muted)" }}>
                {m.group?.emoji} {m.group?.name}
              </span>
              {m.avgRating && (
                <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: "var(--amber)", fontWeight: 700 }}>
                  <Star size={12} fill="var(--amber)" /> {m.avgRating}
                </span>
              )}
            </div>

            {m.description && (
              <div style={{ background: "var(--surface)", borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, color: "var(--ink2)", lineHeight: 1.5, border: "1px solid var(--border)" }}>
                {m.description}
              </div>
            )}

            <InfoGrid items={[
              [t("mods.completions"), (m._count?.progress ?? 0).toLocaleString()],
              [t("mods.feedback"), m._count?.feedback ? t("mods.nReviews", { n: m._count.feedback }) : t("mods.nReviews", { n: 0 })],
              [t("mods.videos"), m.videos?.length ?? 0],
              [t("mods.sortOrder"), m.sortOrder],
              [t("mods.preview"), m.isPreview ? t("mods.freePreviewCheck") : t("mods.subscribersOnly")],
              [t("mods.createdAt"), new Date(m.createdAt).toLocaleDateString()],
            ]} />

            {m.videos?.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>🎥 Videos</div>
                {m.videos.map(v => (
                  <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                    <span className={`badge ${v.type === "PRACTICE" ? "bg-teal" : "bg-blue"}`}>{v.type}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{v.title}</span>
                    <span style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{v.durationSecs ? `${Math.floor(v.durationSecs / 60)}:${String(v.durationSecs % 60).padStart(2, "0")}` : "—"}</span>
                    <Badge status={v.status} />
                  </div>
                ))}
              </div>
            )}

            {/* Resources summary */}
            {m.resources?.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>📎 Resources ({m.resources.length})</div>
                {m.resources.map(r => (
                  <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
                    <span style={{ fontSize: 14 }}>{RESOURCE_ICONS[r.type] ?? "📄"}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{r.title}</span>
                    <span className={`badge bg-${r.type === "PDF" ? "rose" : r.type === "BOOK" ? "blue" : r.type === "LINK" ? "teal" : "gray"}`} style={{ fontSize: 9 }}>{r.type}</span>
                    <Badge status={r.status} />
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {m.status === "PUBLISHED"
                ? <button className="btn btn-out" style={{ flex: 1 }} onClick={() => { onUnpublish(m.id); onClose(); }}>{t("mods.unpublish")}</button>
                : <button className="btn btn-teal" style={{ flex: 1 }} onClick={() => { onPublish(m.id); onClose(); }}>{t("mods.publishModule")}</button>}
              <button className="btn btn-out" style={{ flex: 1 }} onClick={() => { onManageResources(m); onClose(); }}>{t("mods.manageResources")}</button>
              <button className="btn btn-danger btn-sm" onClick={() => { onDelete(m.id); onClose(); }}><Trash2 size={12} /></button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── MODULE RESOURCES MANAGER ─────────────────────────────────────────────────

const RESOURCE_TYPES = [
  { value: "PDF",       label: "PDF Document", icon: "📄", color: "#e53e3e", hint: "PDF file URL" },
  { value: "BOOK",      label: "Book / eBook",  icon: "📚", color: "#3182ce", hint: "Link or download URL" },
  { value: "LINK",      label: "Web Link",      icon: "🔗", color: "#38a169", hint: "External webpage or article" },
  { value: "AUDIO",     label: "Audio / Podcast", icon: "🎵", color: "#d69e2e", hint: "MP3 or audio stream URL" },
  { value: "WORKSHEET", label: "Worksheet",     icon: "📝", color: "#6b46c1", hint: "Printable or editable doc" },
  { value: "IMAGE",     label: "Image",         icon: "🖼", color: "#718096", hint: "JPEG, PNG or image URL" },
];

// MIME types accepted per resource type
const ACCEPT_MAP = {
  PDF: "application/pdf",
  BOOK: "application/pdf,application/epub+zip,.mobi",
  WORKSHEET: "application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,.xlsx",
  AUDIO: "audio/*",
  IMAGE: "image/*",
  LINK: null, // link type = URL only, no file upload
};

function ResourceDropZone({ type, onFile, uploadState }) {
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const accept = ACCEPT_MAP[type];
  if (!accept) return null; // LINK type uses URL field only

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  const prog = uploadState?.progress ?? 0;
  const isUploading = uploadState?.uploading;
  const isDone = uploadState?.done;
  const fileName = uploadState?.fileName;

  return (
    <div style={{ marginBottom: 14 }}>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileRef.current?.click()}
        style={{
          border: `2px dashed ${isDone ? "var(--teal)" : dragging ? "var(--teal)" : "var(--border)"}`,
          borderRadius: 12,
          padding: "20px 16px",
          textAlign: "center",
          cursor: isUploading ? "default" : "pointer",
          background: dragging ? "var(--teal-dim)" : isDone ? "var(--teal-dim)" : "var(--card)",
          transition: "all .15s",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Progress bar */}
        {isUploading && (
          <div style={{ position: "absolute", bottom: 0, left: 0, height: 3, width: `${prog}%`, background: "var(--teal)", transition: "width .2s", borderRadius: "0 0 12px 12px" }} />
        )}

        {isDone ? (
          <>
            <div style={{ fontSize: 24, marginBottom: 4 }}>✅</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)" }}>Uploaded successfully</div>
            <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 2, fontFamily: "var(--fm)" }}>{fileName}</div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>Click to replace</div>
          </>
        ) : isUploading ? (
          <>
            <div style={{ fontSize: 24, marginBottom: 4 }}>⬆️</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)" }}>Uploading… {prog}%</div>
            <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 2, fontFamily: "var(--fm)" }}>{fileName}</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 30, marginBottom: 6 }}>{dragging ? "📂" : "⬆️"}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 2 }}>
              {dragging ? "Drop it here!" : "Drag & drop or click to browse"}
            </div>
            <div style={{ fontSize: 11, color: "var(--ghost)" }}>
              {type === "PDF" ? "PDF files" : type === "BOOK" ? "PDF or ePub" : type === "AUDIO" ? "MP3, M4A, WAV, OGG" : type === "IMAGE" ? "JPG, PNG, WebP, GIF" : "Supported files"}
              {" · "}Max 100 MB
            </div>
          </>
        )}
      </div>
      <input ref={fileRef} type="file" accept={accept} style={{ display: "none" }} onChange={e => { if (e.target.files?.[0]) onFile(e.target.files[0]); e.target.value = ""; }} />
    </div>
  );
}

// ── Helpers for file auto-population ─────────────────────────────────────────

/** Detect the resource type from a file's MIME type / extension */
function detectResourceType(file, currentType) {
  const mime = file.type || "";
  const name = file.name.toLowerCase();
  if (mime === "application/pdf") return ["PDF", "BOOK"].includes(currentType) ? currentType : "PDF";
  if (mime === "application/epub+zip" || name.endsWith(".epub") || name.endsWith(".mobi")) return "BOOK";
  if (mime.startsWith("audio/")) return "AUDIO";
  if (mime.startsWith("image/")) return "IMAGE";
  if (mime.includes("word") || mime.includes("spreadsheet") || name.match(/\.(docx?|xlsx?)$/)) return "WORKSHEET";
  return currentType; // keep user's selection if we can't determine
}

/** Try to extract page count from a PDF file without any library */
async function getPdfPageCount(file) {
  try {
    const buf = await file.arrayBuffer();
    const txt = new TextDecoder("latin1").decode(new Uint8Array(buf));
    // PDF spec: /Type /Pages has a /Count entry for total pages
    const m = txt.match(/\/Type\s*\/Pages[\s\S]{0,200}?\/Count\s+(\d+)/);
    if (m) return parseInt(m[1], 10);
    // Fallback: count individual /Type /Page entries (excluding /Pages)
    const pages = txt.match(/\/Type\s*\/Page[^s]/g);
    return pages ? pages.length : null;
  } catch { return null; }
}

/** Format bytes as human-readable size */
function fmtBytes(b) {
  if (!b) return "";
  return b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
}

// Storage method options for resource upload
const STORAGE_METHODS = [
  { value: "r2",     icon: "☁️", label: "Cloudflare R2",  desc: "Scalable cloud storage (recommended)" },
  { value: "server", icon: "🗄️", label: "MinIO Storage", desc: "Stored in MinIO object storage" },
  { value: "url",    icon: "🔗", label: "External URL",   desc: "YouTube, Google Drive or any web link" },
];

/** Guess the original storage method from a resource URL */
function guessStorageMethod(url = "") {
  if (!url) return "server";
  // MinIO public URL (resources.devemm.rw or any /kunga/ path) → server (MinIO)
  if (url.includes("resources.devemm.rw") || /\/kunga\//.test(url)) return "server";
  // Legacy server-side uploads (local disk)
  if (url.includes("/uploads/resources/") || url.includes("/uploads/avatars/")) return "server";
  if (/r2\.dev|cloudflare|cdnjs/.test(url)) return "r2";
  return "url";
}

function ModuleResourcesModal({ mod, onClose, toast }) {
  const { t } = useLang();
  const { data, loading, reload } = useApi(() => modulesApi.listResources(mod.id), [mod.id]);
  const resources = data?.resources ?? [];
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const BLANK = { title: "", type: "PDF", url: "", description: "", fileSize: "", pageCount: "", status: "PUBLISHED", isPreviewClip: false };
  const [form, setForm] = useState(BLANK);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Storage method: "r2" | "server" | "url"
  const [storageMethod, setStorageMethod] = useState("server");

  // Upload state: { uploading, progress, done, fileName, fileUrl }
  const [uploadState, setUploadState] = useState(null);

  const openAdd = () => {
    setForm(BLANK); setEditing(null); setAdding(true);
    setUploadState(null); setStorageMethod("server");
  };
  const openEdit = (r) => {
    setForm({ title: r.title, type: r.type, url: r.url ?? "", description: r.description ?? "", fileSize: r.fileSize ? String(r.fileSize) : "", pageCount: r.pageCount ? String(r.pageCount) : "", status: r.status, isPreviewClip: r.isPreviewClip ?? false });
    setEditing(r);
    setAdding(true);
    setUploadState(null);
    setStorageMethod(guessStorageMethod(r.url));
  };

  const handleFile = async (file) => {
    const mime = file.type || "application/octet-stream";

    // 1. Auto-detect resource type & populate metadata BEFORE upload starts
    const detectedType = detectResourceType(file, form.type);
    const autoTitle = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ").trim();
    let autoPageCount = "";
    if (mime === "application/pdf") {
      const count = await getPdfPageCount(file);
      if (count) autoPageCount = String(count);
    }
    setForm(f => ({
      ...f,
      type: detectedType,
      title: f.title.trim() ? f.title : autoTitle,
      fileSize: String(file.size),
      ...(autoPageCount && !f.pageCount && { pageCount: autoPageCount }),
    }));
    setUploadState({ uploading: true, progress: 0, done: false, fileName: file.name, fileUrl: "" });

    try {
      let fileUrl;

      if (storageMethod === "r2") {
        // ── Upload to Cloudflare R2 via presigned PUT ──
        let uploadUrl;
        try {
          ({ uploadUrl, fileUrl } = await modulesApi.resourceUploadUrl(file.name, mime));
        } catch (e) {
          setUploadState(null);
          if (e.status === 503) {
            toast("⚙️ Cloudflare R2 is not configured. Set CLOUDFLARE_ACCOUNT_ID, CLOUDFLARE_R2_ACCESS_KEY, CLOUDFLARE_R2_SECRET_KEY in your backend .env, or choose MinIO Storage instead.");
          } else {
            toast("Could not get upload URL: " + e.message);
          }
          return;
        }
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", uploadUrl);
          xhr.setRequestHeader("Content-Type", mime);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable)
              setUploadState(s => ({ ...s, progress: Math.round((e.loaded / e.total) * 100) }));
          };
          xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`R2 upload failed (${xhr.status}) — check your bucket CORS policy.`));
          xhr.onerror = () => reject(new Error("R2 upload: network error — check your R2 CORS policy allows PUT from this origin."));
          xhr.send(file);
        });

      } else {
        // ── Upload directly to Server Storage ──
        const result = await modulesApi.resourceUploadServer(file, (pct) => {
          setUploadState(s => ({ ...s, progress: pct }));
        });
        fileUrl = result.fileUrl;
      }

      setForm(f => ({ ...f, url: fileUrl }));
      setUploadState({ uploading: false, progress: 100, done: true, fileName: file.name, fileUrl });
      toast(`✅ File uploaded to ${storageMethod === "r2" ? "Cloudflare R2" : "Server"}! Review the details and save.`);
    } catch (e) {
      setUploadState(null);
      toast("Upload failed: " + e.message);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast("Title is required"); return; }
    if (storageMethod === "url" && !form.url.trim()) { toast("URL is required for external links"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        type: form.type,
        url: form.url,
        status: form.status,
        isPreviewClip: form.isPreviewClip,
        ...(form.description.trim() && { description: form.description }),
        ...(form.fileSize && { fileSize: Number(form.fileSize) }),
        ...(form.pageCount && { pageCount: Number(form.pageCount) }),
      };
      if (editing) {
        await modulesApi.updateResource(editing.id, payload);
        toast("✅ Resource updated!");
      } else {
        await modulesApi.createResource(mod.id, payload);
        toast("✅ Resource added!");
      }
      reload();
      setAdding(false);
      setEditing(null);
      setUploadState(null);
    } catch (e) { toast("Error: " + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await modulesApi.deleteResource(id); toast("Resource removed"); reload(); }
    catch (e) { toast("Error: " + e.message); }
  };

  const isFileStorage = storageMethod !== "url";
  const hasUploadedFile = uploadState?.done;
  const canSave = form.title.trim() && (storageMethod !== "url" || form.url.trim());

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 640, width: "95vw" }}>
        <div className="modal-ttl">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="tc">{mod.code}</span>
            <span>Resources</span>
          </div>
          <CloseX onClick={onClose} />
        </div>

        {adding ? (
          /* ── Add / Edit form ── */
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 14 }}>
              {editing ? t("mods.editResource") : t("mods.addNewResource")}
            </div>

            {/* ── STORAGE METHOD SELECTOR ── */}
            <div className="f-grp" style={{ marginBottom: 14 }}>
              <label className="f-lbl">{t("mods.storageMethod")}</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                {STORAGE_METHODS.map(s => (
                  <button key={s.value}
                    onClick={() => {
                      setStorageMethod(s.value); setUploadState(null);
                      if (s.value === "url") set("type", "LINK");
                      else if (form.type === "LINK") set("type", "PDF");
                    }}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "10px 8px", border: `2px solid ${storageMethod === s.value ? "var(--teal)" : "var(--border)"}`, borderRadius: 10, background: storageMethod === s.value ? "var(--teal-dim)" : "var(--card)", cursor: "pointer", transition: "all .15s" }}>
                    <span style={{ fontSize: 22 }}>{s.icon}</span>
                    <span style={{ fontSize: 11, fontFamily: "var(--fb)", fontWeight: 700, color: storageMethod === s.value ? "var(--teal)" : "var(--muted)" }}>{s.label}</span>
                    <span style={{ fontSize: 9, color: "var(--ghost)", textAlign: "center", lineHeight: 1.3 }}>{s.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Type selector grid — hidden when URL storage (auto LINK) */}
            {isFileStorage && (
              <div className="f-grp" style={{ marginBottom: 14 }}>
                <label className="f-lbl">{t("mods.resourceType")}</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                  {RESOURCE_TYPES.filter(rt => rt.value !== "LINK").map(rt => (
                    <button key={rt.value} onClick={() => { set("type", rt.value); if (!editing) setUploadState(null); }}
                      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 8px", border: `2px solid ${form.type === rt.value ? rt.color : "var(--border)"}`, borderRadius: 10, background: form.type === rt.value ? `${rt.color}18` : "var(--card)", cursor: "pointer", transition: "all .15s" }}>
                      <span style={{ fontSize: 20 }}>{rt.icon}</span>
                      <span style={{ fontSize: 10, fontFamily: "var(--fb)", fontWeight: 700, color: form.type === rt.value ? rt.color : "var(--muted)" }}>{rt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── FILE UPLOAD ZONE (R2 or Server) ── */}
            {isFileStorage && (
              <div className="f-grp">
                <label className="f-lbl">
                  Upload File
                  <span style={{ fontWeight: 400, color: "var(--ghost)", marginLeft: 6 }}>— drag & drop or click to browse</span>
                </label>
                <ResourceDropZone type={form.type} onFile={handleFile} uploadState={uploadState} />
              </div>
            )}

            {/* ── URL section ── */}
            {isFileStorage && hasUploadedFile ? (
              /* Upload done → show generated link chip */
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--teal-dim)", border: "1.5px solid var(--teal)", borderRadius: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 16 }}>{storageMethod === "r2" ? "☁️" : "🖥️"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--teal)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2 }}>
                    {storageMethod === "r2" ? "Cloudflare R2 — link generated" : "Server storage — link generated"}
                  </div>
                  <div style={{ fontSize: 11, fontFamily: "var(--fm)", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.url}</div>
                </div>
                <button title="Copy link" className="btn btn-out btn-xs" onClick={() => { navigator.clipboard.writeText(form.url); toast("Link copied!"); }}>📋</button>
              </div>
            ) : !isFileStorage ? (
              /* External URL / YouTube link */
              <div className="f-grp">
                <label className="f-lbl">URL / Link <span style={{ color: "var(--rose)", fontWeight: 700 }}>*</span></label>
                <input className="f-in" value={form.url} onChange={e => set("url", e.target.value)}
                  placeholder="https://youtube.com/watch?v=… or https://drive.google.com/…"
                  style={{ fontFamily: "var(--fm)", fontSize: 12 }} />
                {form.url && /youtube\.com|youtu\.be/.test(form.url) && (
                  <div style={{ marginTop: 6, padding: "6px 10px", background: "#ff000012", border: "1px solid #ff000033", borderRadius: 8, fontSize: 11, color: "#c00" }}>
                    🎬 YouTube link — users will be redirected to YouTube to watch this video.
                  </div>
                )}
              </div>
            ) : null /* file storage before upload — URL will be set by upload */}

            {/* ── Title — auto-filled from filename ── */}
            <div className="f-grp">
              <label className="f-lbl">
                {t("mods.resourceTitle")} <span style={{ color: "var(--rose)", fontWeight: 700 }}>*</span>
                {hasUploadedFile && !editing && <span style={{ fontWeight: 400, color: "var(--teal)", marginLeft: 6, fontSize: 10 }}>✦ auto-filled from filename</span>}
              </label>
              <input className="f-in" value={form.title} onChange={e => set("title", e.target.value)}
                placeholder={`e.g. ${form.type === "PDF" ? "Module Handout Guide" : form.type === "BOOK" ? "The Speech Development Handbook" : form.type === "LINK" ? "Helpful Article on Speech Therapy" : form.type === "AUDIO" ? "Guided Breathing Exercise" : "Resource title"}`} />
            </div>

            {/* ── Description ── */}
            <div className="f-grp">
              <label className="f-lbl">{t("mods.resourceDesc")} <span style={{ color: "var(--ghost)", fontWeight: 400 }}>(optional)</span></label>
              <textarea className="f-in" value={form.description} onChange={e => set("description", e.target.value)} placeholder="Brief description shown to users…" rows={2} style={{ resize: "vertical", lineHeight: 1.5 }} />
            </div>

            {/* ── File metadata (auto-filled where possible) ── */}
            {(form.type === "PDF" || form.type === "BOOK" || form.type === "WORKSHEET" || form.type === "AUDIO" || form.type === "IMAGE") && (
              <div className="f-row">
                <div className="f-grp">
                  <label className="f-lbl">
                    {t("mods.fileSize")}
                    {form.fileSize && <span style={{ fontWeight: 400, color: "var(--teal)", marginLeft: 6, fontSize: 10 }}>✦ {fmtBytes(Number(form.fileSize))}</span>}
                  </label>
                  <input className="f-in" type="number" min="0" value={form.fileSize} onChange={e => set("fileSize", e.target.value)} placeholder="auto-filled on upload" readOnly={hasUploadedFile} style={{ background: hasUploadedFile ? "var(--surface)" : undefined, cursor: hasUploadedFile ? "default" : undefined }} />
                </div>
                {(form.type === "PDF" || form.type === "BOOK") && (
                  <div className="f-grp">
                    <label className="f-lbl">
                      {t("mods.pageCount")}
                      {form.pageCount && form.type === "PDF" && <span style={{ fontWeight: 400, color: "var(--teal)", marginLeft: 6, fontSize: 10 }}>✦ auto-detected</span>}
                    </label>
                    <input className="f-in" type="number" min="1" value={form.pageCount} onChange={e => set("pageCount", e.target.value)} placeholder="e.g. 24" />
                  </div>
                )}
              </div>
            )}

            {/* ── Visibility ── */}
            <div className="f-grp">
              <label className="f-lbl">{t("mods.visibility")}</label>
              <div style={{ display: "flex", gap: 6 }}>
                {[{ v: "PUBLISHED", l: "✅ Published", c: "var(--teal)" }, { v: "DRAFT", l: "📝 Draft", c: "var(--ghost)" }].map(s => (
                  <button key={s.v} onClick={() => set("status", s.v)}
                    style={{ flex: 1, padding: "7px 4px", border: `1.5px solid ${form.status === s.v ? s.c : "var(--border)"}`, borderRadius: 8, background: form.status === s.v ? (s.v === "PUBLISHED" ? "var(--teal-dim)" : "var(--surface)") : "var(--card)", color: form.status === s.v ? s.c : "var(--ghost)", cursor: "pointer", fontFamily: "var(--fb)", fontSize: 12, fontWeight: 700 }}>
                    {s.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="f-grp" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" id="resPrev" checked={form.isPreviewClip} onChange={e => set("isPreviewClip", e.target.checked)} />
              <label htmlFor="resPrev" style={{ fontSize: 12, color: "var(--muted)", cursor: "pointer" }}>{t("videos.previewClipLabel")}</label>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button className="btn btn-out" onClick={() => { setAdding(false); setEditing(null); setUploadState(null); }} style={{ flexShrink: 0 }}>{t("common.cancel")}</button>
              <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={save}
                disabled={saving || !canSave || uploadState?.uploading}>
                {saving ? t("common.saving") : uploadState?.uploading ? `${t("mods.uploading")} ${uploadState.progress}%` : editing ? t("common.save") : `✅ ${t("mods.addResource")}`}
              </button>
            </div>
          </div>
        ) : (
          /* ── Resource list ── */
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>{resources.length} resource{resources.length !== 1 ? "s" : ""} for <strong>{mod.code} — {mod.title}</strong></div>
              <button className="btn btn-teal btn-sm" onClick={openAdd}><Plus size={12} /> {t("mods.addResource")}</button>
            </div>

            {loading ? <Spinner /> : resources.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>📎</div>
                <div style={{ fontFamily: "var(--fb)", fontSize: 14, color: "var(--ink2)", marginBottom: 6 }}>{t("mods.noResourcesYet")}</div>
                <div style={{ fontSize: 12, color: "var(--ghost)", marginBottom: 20 }}>Upload PDFs, books, audio files or add links for subscribers</div>
                <button className="btn btn-teal" onClick={openAdd}><Plus size={13} /> {t("mods.addResource")}</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 420, overflowY: "auto" }}>
                {resources.map(r => {
                  const rType = RESOURCE_TYPES.find(rt => rt.value === r.type);
                  return (
                    <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", background: "var(--surface)", borderRadius: 12, border: "1px solid var(--border)", transition: "border-color .15s" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "var(--teal)"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
                      {/* Type icon */}
                      <div style={{ width: 42, height: 42, borderRadius: 10, background: `${rType?.color ?? "#999"}18`, border: `1.5px solid ${rType?.color ?? "#999"}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                        {rType?.icon ?? "📄"}
                      </div>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "var(--ink)", marginBottom: 2 }}>{r.title}</div>
                        {r.description && <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4, lineHeight: 1.4 }}>{r.description}</div>}
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                          <span className="badge" style={{ background: `${rType?.color ?? "#999"}18`, color: rType?.color ?? "#999", border: `1px solid ${rType?.color ?? "#999"}44`, fontSize: 9 }}>{r.type}</span>
                          <Badge status={r.status} />
                          {r.isPreviewClip && <span className="badge bg-amber" style={{ fontSize: 9 }}>🔓 Preview</span>}
                          {r.pageCount && <span style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>📖 {r.pageCount}p</span>}
                          {r.fileSize && <span style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>💾 {r.fileSize > 1048576 ? `${(r.fileSize / 1048576).toFixed(1)} MB` : `${Math.round(r.fileSize / 1024)} KB`}</span>}
                          <a href={r.url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: "var(--teal)", fontFamily: "var(--fm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }} onClick={e => e.stopPropagation()}>
                            🔗 {r.url.replace(/^https?:\/\//, "").slice(0, 40)}{r.url.length > 47 ? "…" : ""}
                          </a>
                        </div>
                      </div>
                      {/* Actions */}
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        <a href={r.url} target="_blank" rel="noreferrer" className="btn btn-out btn-xs" title="Open resource">⬆</a>
                        <button className="btn btn-out btn-xs" onClick={() => openEdit(r)} title="Edit">✏️</button>
                        <button className="btn btn-out btn-xs" style={{ color: "var(--rose)" }} onClick={() => remove(r.id)} title="Delete"><Trash2 size={10} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <button className="btn btn-out" onClick={onClose}>{t("mods.done")}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── TRANSLATION TABS ────────────────────────────────────────────────────────

const LANGS = [
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "fr", flag: "🇫🇷", label: "FR" },
  { code: "rw", flag: "🇷🇼", label: "RW" },
  { code: "sw", flag: "🇹🇿", label: "SW" },
];

function TranslationTabs({ activeLang, onChangeLang }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
      {LANGS.map(l => (
        <button key={l.code}
          onClick={() => onChangeLang(l.code)}
          style={{
            padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
            border: `1.5px solid ${activeLang === l.code ? "var(--teal)" : "var(--border)"}`,
            background: activeLang === l.code ? "var(--teal-dim)" : "var(--card)",
            color: activeLang === l.code ? "var(--teal)" : "var(--ghost)",
            cursor: "pointer",
          }}>
          {l.flag} {l.label}
        </button>
      ))}
    </div>
  );
}

function CreateModuleModal({ groups, onClose, onCreate, toast }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    code: "", title: "", groupId: "", description: "", whatToExpect: "",
    isPreview: false, status: "DRAFT", sortOrder: 0,
    titleTranslations: { fr: "", rw: "", sw: "" },
    descriptionTranslations: { fr: "", rw: "", sw: "" },
    whatToExpectTranslations: { fr: "", rw: "", sw: "" },
  });
  const [activeLang, setActiveLang] = useState("en");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.code || !form.title || !form.groupId) { toast("Code, title, and group are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sortOrder: Number(form.sortOrder),
        isPreview: Boolean(form.isPreview),
        titleTranslations: Object.values(form.titleTranslations).some(v => v.trim()) ? form.titleTranslations : undefined,
        descriptionTranslations: Object.values(form.descriptionTranslations).some(v => v.trim()) ? form.descriptionTranslations : undefined,
        whatToExpectTranslations: Object.values(form.whatToExpectTranslations).some(v => v.trim()) ? form.whatToExpectTranslations : undefined,
      };
      await modulesApi.create(payload);
      toast("✅ Module created successfully!"); onCreate(); onClose();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setTrans = (field, lang, v) => setForm(f => ({ ...f, [field]: { ...f[field], [lang]: v } }));

  const titleVal = activeLang === "en" ? form.title : form.titleTranslations[activeLang] ?? "";
  const descVal = activeLang === "en" ? form.description : form.descriptionTranslations[activeLang] ?? "";
  const wteVal = activeLang === "en" ? form.whatToExpect : form.whatToExpectTranslations[activeLang] ?? "";

  const setTitle = (v) => activeLang === "en" ? set("title", v) : setTrans("titleTranslations", activeLang, v);
  const setDesc = (v) => activeLang === "en" ? set("description", v) : setTrans("descriptionTranslations", activeLang, v);
  const setWte = (v) => activeLang === "en" ? set("whatToExpect", v) : setTrans("whatToExpectTranslations", activeLang, v);

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-ttl">{t("mods.createNewModule")} <CloseX onClick={onClose} /></div>
        <div className="f-row">
          <div className="f-grp"><label className="f-lbl">{t("mods.moduleCode")} *</label><input className="f-in" value={form.code} onChange={e => set("code", e.target.value.toUpperCase())} placeholder="e.g. S5, C4, M3" /></div>
          <div className="f-grp"><label className="f-lbl">{t("mods.groupLabel")} *</label>
            <select className="f-sel" value={form.groupId} onChange={e => set("groupId", e.target.value)}>
              <option value="">{t("mods.selectGroup")}</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.emoji} {g.name}</option>)}
            </select>
          </div>
        </div>
        <TranslationTabs activeLang={activeLang} onChangeLang={setActiveLang} />
        <div className="f-grp"><label className="f-lbl">{t("mods.titleLabel")} {activeLang === "en" && "*"}</label><input className="f-in" value={titleVal} onChange={e => setTitle(e.target.value)} placeholder="Module title…" /></div>
        <div className="f-grp"><label className="f-lbl">{t("mods.descLabel")}</label><textarea className="f-ta" rows={2} value={descVal} onChange={e => setDesc(e.target.value)} placeholder="Brief description…" /></div>
        <div className="f-grp"><label className="f-lbl">{t("mods.whatToExpect")}</label><textarea className="f-ta" rows={3} value={wteVal} onChange={e => setWte(e.target.value)} placeholder="3 sentences describing what parent and child will do…" /></div>
        <div className="f-row">
          <div className="f-grp"><label className="f-lbl">{t("mods.sortOrder")}</label><input className="f-in" type="number" value={form.sortOrder} onChange={e => set("sortOrder", e.target.value)} /></div>
          <div className="f-grp"><label className="f-lbl">{t("tbl.status")}</label>
            <select className="f-sel" value={form.status} onChange={e => set("status", e.target.value)}>
              <option value="DRAFT">{t("mods.draft")}</option>
              <option value="PUBLISHED">{t("mods.published")}</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <input type="checkbox" id="preview" checked={form.isPreview} onChange={e => set("isPreview", e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--teal)" }} />
          <label htmlFor="preview" style={{ fontSize: 13, color: "var(--muted)", cursor: "pointer" }}>{t("mods.freePreviewLabel")}</label>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.cancel")}</button>
          <button className="btn btn-teal" style={{ flex: 1 }} onClick={create} disabled={saving || !form.code || !form.title || !form.groupId}>
            {saving ? t("mods.creating") : `✅ ${t("mods.createModule")}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModuleModal({ mod, groups, onClose, onSave, toast }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    code: mod.code ?? "",
    title: mod.title ?? "",
    groupId: mod.groupId ?? "",
    description: mod.description ?? "",
    whatToExpect: mod.whatToExpect ?? "",
    isPreview: mod.isPreview ?? false,
    status: mod.status ?? "DRAFT",
    sortOrder: mod.sortOrder ?? 0,
    titleTranslations: mod.titleTranslations ?? { fr: "", rw: "", sw: "" },
    descriptionTranslations: mod.descriptionTranslations ?? { fr: "", rw: "", sw: "" },
    whatToExpectTranslations: mod.whatToExpectTranslations ?? { fr: "", rw: "", sw: "" },
  });
  const [activeLang, setActiveLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setTrans = (field, lang, v) => setForm(f => ({ ...f, [field]: { ...f[field], [lang]: v } }));

  const save = async () => {
    if (!form.code || !form.title) { toast("Code and title are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sortOrder: Number(form.sortOrder),
        isPreview: Boolean(form.isPreview),
        titleTranslations: Object.values(form.titleTranslations).some(v => v.trim()) ? form.titleTranslations : undefined,
        descriptionTranslations: Object.values(form.descriptionTranslations).some(v => v.trim()) ? form.descriptionTranslations : undefined,
        whatToExpectTranslations: Object.values(form.whatToExpectTranslations).some(v => v.trim()) ? form.whatToExpectTranslations : undefined,
      };
      await modulesApi.update(mod.id, payload);
      toast("✅ Module updated!"); onSave(); onClose();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSaving(false); }
  };

  const titleVal = activeLang === "en" ? form.title : form.titleTranslations[activeLang] ?? "";
  const descVal = activeLang === "en" ? form.description : form.descriptionTranslations[activeLang] ?? "";
  const wteVal = activeLang === "en" ? form.whatToExpect : form.whatToExpectTranslations[activeLang] ?? "";

  const setTitle = (v) => activeLang === "en" ? set("title", v) : setTrans("titleTranslations", activeLang, v);
  const setDesc = (v) => activeLang === "en" ? set("description", v) : setTrans("descriptionTranslations", activeLang, v);
  const setWte = (v) => activeLang === "en" ? set("whatToExpect", v) : setTrans("whatToExpectTranslations", activeLang, v);

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-ttl">{t("mods.editModule")} <CloseX onClick={onClose} /></div>
        <div className="f-row">
          <div className="f-grp"><label className="f-lbl">{t("mods.moduleCode")} *</label><input className="f-in" value={form.code} onChange={e => set("code", e.target.value.toUpperCase())} /></div>
          <div className="f-grp"><label className="f-lbl">{t("mods.groupLabel")} *</label>
            <select className="f-sel" value={form.groupId} onChange={e => set("groupId", e.target.value)}>
              <option value="">{t("mods.selectGroup")}</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.emoji} {g.name}</option>)}
            </select>
          </div>
        </div>
        <TranslationTabs activeLang={activeLang} onChangeLang={setActiveLang} />
        <div className="f-grp"><label className="f-lbl">{t("mods.titleLabel")} {activeLang === "en" && "*"}</label><input className="f-in" value={titleVal} onChange={e => setTitle(e.target.value)} /></div>
        <div className="f-grp"><label className="f-lbl">{t("mods.descLabel")}</label><textarea className="f-ta" rows={2} value={descVal} onChange={e => setDesc(e.target.value)} /></div>
        <div className="f-grp"><label className="f-lbl">{t("mods.whatToExpect")}</label><textarea className="f-ta" rows={3} value={wteVal} onChange={e => setWte(e.target.value)} placeholder="3 sentences describing what parent and child will do…" /></div>
        <div className="f-row">
          <div className="f-grp"><label className="f-lbl">{t("mods.sortOrder")}</label><input className="f-in" type="number" value={form.sortOrder} onChange={e => set("sortOrder", e.target.value)} /></div>
          <div className="f-grp"><label className="f-lbl">{t("tbl.status")}</label>
            <select className="f-sel" value={form.status} onChange={e => set("status", e.target.value)}>
              <option value="DRAFT">{t("mods.draft")}</option>
              <option value="PUBLISHED">{t("mods.published")}</option>
              <option value="ARCHIVED">{t("mods.archived")}</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <input type="checkbox" id="edit-prev" checked={form.isPreview} onChange={e => set("isPreview", e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--teal)" }} />
          <label htmlFor="edit-prev" style={{ fontSize: 13, color: "var(--muted)", cursor: "pointer" }}>{t("mods.freePreviewLabel")}</label>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.cancel")}</button>
          <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={save} disabled={saving || !form.code || !form.title}>
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── GROUP MANAGE MODAL ───────────────────────────────────────────────────────

function GroupManageModal({ onClose, onSaved, toast }) {
  const { t } = useLang();
  const { data: gd, reload: reloadGrps } = useApi(() => modulesApi.listGroups());
  const groups = gd?.groups ?? [];

  const handleReorderGroups = useCallback(async (items) => {
    try { await modulesApi.reorderGroups(items); reloadGrps(); onSaved(); } catch { /* silent */ }
  }, [reloadGrps, onSaved]);
  const { sensors: grpSensors, items: sortedGroups, handleDragEnd: grpDragEnd } = useSortableList(groups, handleReorderGroups);

  const [creating, setCreating] = useState(false);
  const [editGrp, setEditGrp]   = useState(null);
  const BLANK_GRP = { name: "", emoji: "📁", description: "", nameTranslations: { fr: "", rw: "", sw: "" }, descriptionTranslations: { fr: "", rw: "", sw: "" } };
  const [form, setForm]         = useState(BLANK_GRP);
  const [grpLang, setGrpLang]   = useState("en");
  const [saving, setSaving]     = useState(false);
  const [delConfirm, setDel]         = useState(null);
  const [permDelGrp, setPermDelGrp]  = useState(null);
  const [permDelLoading, setPermDelLoading] = useState(false);

  const openCreate = () => { setForm(BLANK_GRP); setEditGrp(null); setGrpLang("en"); setCreating(true); };
  const openEdit   = (g) => {
    setForm({ name: g.name, emoji: g.emoji, description: g.description ?? "", nameTranslations: g.nameTranslations ?? { fr: "", rw: "", sw: "" }, descriptionTranslations: g.descriptionTranslations ?? { fr: "", rw: "", sw: "" } });
    setEditGrp(g); setGrpLang("en"); setCreating(true);
  };

  const setGrpTrans = (field, lang, v) => setForm(f => ({ ...f, [field]: { ...f[field], [lang]: v } }));
  const grpNameVal = grpLang === "en" ? form.name : (form.nameTranslations[grpLang] ?? "");
  const grpDescVal = grpLang === "en" ? form.description : (form.descriptionTranslations[grpLang] ?? "");
  const setGrpName = (v) => grpLang === "en" ? setForm(f => ({ ...f, name: v })) : setGrpTrans("nameTranslations", grpLang, v);
  const setGrpDesc = (v) => grpLang === "en" ? setForm(f => ({ ...f, description: v })) : setGrpTrans("descriptionTranslations", grpLang, v);

  const save = async () => {
    if (!form.name.trim()) { toast("Group name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, emoji: form.emoji, description: form.description,
        ...(Object.values(form.nameTranslations).some(v => v.trim()) && { nameTranslations: form.nameTranslations }),
        ...(Object.values(form.descriptionTranslations).some(v => v.trim()) && { descriptionTranslations: form.descriptionTranslations }),
      };
      if (editGrp) { await modulesApi.updateGroup(editGrp.id, payload); toast("✅ Group updated!"); }
      else         { await modulesApi.createGroup(payload);              toast("✅ Group created!"); }
      reloadGrps(); onSaved(); setCreating(false); setEditGrp(null);
    } catch (e) { toast("Error: " + e.message); }
    finally { setSaving(false); }
  };

  const remove = async (g) => {
    setSaving(true);
    try { await modulesApi.deleteGroup(g.id); toast("Group deleted"); reloadGrps(); onSaved(); }
    catch (e) { toast("Error: " + e.message); }
    finally { setSaving(false); setDel(null); }
  };

  const toggleArchive = async (g) => {
    try {
      if (g.status === "ARCHIVED") { await modulesApi.unarchiveGroup(g.id); toast("✅ Group restored"); }
      else { await modulesApi.archiveGroup(g.id); toast("📦 Group archived"); }
      reloadGrps(); onSaved();
    } catch (e) { toast("Error: " + e.message); }
  };

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-ttl">{t("mods.moduleGroups")} <CloseX onClick={onClose} /></div>

        {creating ? (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 14 }}>{editGrp ? t("mods.editGroup") : t("mods.newGroup")}</div>
            <div className="f-grp" style={{ flex: "0 0 70px", marginBottom: 10 }}>
              <label className="f-lbl">{t("mods.emoji")}</label>
              <input className="f-in" value={form.emoji} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} style={{ textAlign: "center", fontSize: 20, maxWidth: 70 }} maxLength={4} />
            </div>
            <TranslationTabs activeLang={grpLang} onChangeLang={setGrpLang} />
            <div className="f-grp">
              <label className="f-lbl">{t("mods.groupName")} {grpLang === "en" && "*"}</label>
              <input className="f-in" value={grpNameVal} onChange={e => setGrpName(e.target.value)} placeholder={grpLang === "en" ? "e.g. Communication Skills" : `Group name in ${grpLang.toUpperCase()}…`} />
            </div>
            <div className="f-grp">
              <label className="f-lbl">{t("mods.descLabel")} <span style={{ fontSize: 10, color: "var(--ghost)", fontWeight: 400 }}>(optional)</span></label>
              <input className="f-in" value={grpDescVal} onChange={e => setGrpDesc(e.target.value)} placeholder={grpLang === "en" ? "Short description (optional)" : `Description in ${grpLang.toUpperCase()}…`} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-out" onClick={() => { setCreating(false); setEditGrp(null); }}>{t("common.cancel")}</button>
              <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={save} disabled={saving || !form.name.trim()}>
                {saving ? t("common.saving") : editGrp ? t("common.save") : t("mods.createGroup")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: "var(--muted)" }}>{t("mods.groupCount", { n: groups.length })}</span>
              <button className="btn btn-teal btn-sm" onClick={openCreate}><Plus size={12} /> {t("mods.newGroup")}</button>
            </div>
            {groups.length === 0 ? <div style={{ padding: "24px 0", textAlign: "center", color: "var(--ghost)", fontSize: 13 }}>{t("mods.noGroupsYet")}</div> : (
              <DndContext sensors={grpSensors} collisionDetection={closestCenter} onDragEnd={grpDragEnd}>
                <SortableContext items={sortedGroups.map(g => g.id)} strategy={verticalListSortingStrategy}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 380, overflowY: "auto" }}>
                {sortedGroups.map(g => (
                  <SortableItem key={g.id} id={g.id} type="div">
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)", opacity: g.status === "ARCHIVED" ? 0.6 : 1, flex: 1 }}>
                    <div style={{ fontSize: 22, flexShrink: 0 }}>{g.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "var(--ink)" }}>{g.name}</div>
                        {g.status === "ARCHIVED" && <span className="badge" style={{ fontSize: 9, color: "var(--ghost)", border: "1px solid var(--border)" }}>📦 Archived</span>}
                      </div>
                      {g.description && <div style={{ fontSize: 11, color: "var(--ghost)", marginTop: 2 }}>{g.description}</div>}
                      <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 3, fontFamily: "var(--fm)" }}>
                        {t("mods.moduleCount", { n: g.modules?.length ?? 0 })}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button className="btn btn-out btn-xs" onClick={() => openEdit(g)} title="Edit">✏️</button>
                      <button className="btn btn-out btn-xs" onClick={() => toggleArchive(g)} title={g.status === "ARCHIVED" ? "Restore" : "Archive"}>
                        {g.status === "ARCHIVED" ? "♻️" : "📦"}
                      </button>
                      <button className="btn btn-out btn-xs" style={{ color: "var(--rose)" }} onClick={() => setDel(g)} title="Archive group"><Trash2 size={10} /></button>
                      <button className="btn btn-out btn-xs" style={{ color: "var(--rose)", fontWeight: 700 }} onClick={() => setPermDelGrp(g)} title="Delete permanently">🗑</button>
                    </div>
                  </div>
                  </SortableItem>
                ))}
                </div>
                </SortableContext>
              </DndContext>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>{t("mods.done")}</button>
            </div>
          </>
        )}

        {delConfirm && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div className="modal" style={{ maxWidth: 360, position: "static" }}>
              <div className="modal-ttl">{t("mods.deleteGroup")}</div>
              <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.6, marginBottom: 20, padding: "12px 14px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
                {t("mods.deleteGroupConfirm", { name: delConfirm.name })}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn btn-out" style={{ flex: 1 }} onClick={() => setDel(null)}>{t("common.cancel")}</button>
                <button className="btn" style={{ flex: 1, justifyContent: "center", background: "var(--rose)", color: "white" }} onClick={() => remove(delConfirm)} disabled={saving}>
                  {saving ? t("mods.deleting") : t("common.delete")}
                </button>
              </div>
            </div>
          </div>
        )}
        {permDelGrp && (
          <PermanentDeleteModal
            description={`"${permDelGrp.name}" and all its modules, videos, and resources will be permanently deleted.`}
            loading={permDelLoading}
            onClose={() => setPermDelGrp(null)}
            onConfirm={async () => {
              setPermDelLoading(true);
              try { await modulesApi.permanentDeleteGroup(permDelGrp.id); toast("🗑 Group permanently deleted"); reloadGrps(); onSaved(); setPermDelGrp(null); }
              catch (e) { toast("Error: " + e.message); }
              finally { setPermDelLoading(false); }
            }}
          />
        )}
      </div>
    </div>
  );
}

function ModuleGroupSection({ g, onSelectMod, onEditMod, requestConfirm, canManage, onReordered }) {
  const { t } = useLang();
  const [page, setPage] = useState(1);
  const PER = 10;
  const [permDelMod, setPermDelMod]       = useState(null);
  const [permDelModLoading, setPermDelModLoading] = useState(false);

  const handleReorder = useCallback(async (items) => {
    try { await modulesApi.reorderModules(items); onReordered?.(); }
    catch { /* silent — optimistic update already applied */ }
  }, [onReordered]);

  const { sensors, items: sortedItems, handleDragEnd } = useSortableList(g.items, handleReorder);

  const total = sortedItems.length;
  const totalPages = Math.ceil(total / PER);
  const pageItems = sortedItems.slice((page - 1) * PER, page * PER);

  return (
    <div className="card">
      <div className="card-hdr">
        <div className="card-ttl">
          {g.emoji} {g.name}
          <span className="badge bg-green">{g.items.filter(m => m.status === "PUBLISHED").length} live</span>
          <span className="badge bg-gray">{g.items.filter(m => m.status === "DRAFT").length} draft</span>
        </div>
      </div>
      <div className="tbl-wrap"><table>
        <thead><tr><th style={{ width: 24 }}></th><th>{t("tbl.code")}</th><th>{t("tbl.title")}</th><th>{t("tbl.videos")}</th><th>{t("tbl.status")}</th><th>{t("tbl.preview")}</th><th>{t("tbl.completions")}</th><th>{t("tbl.actions")}</th></tr></thead>
        <tbody>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={pageItems.map(m => m.id)} strategy={verticalListSortingStrategy}>
              {pageItems.map(m => (
                <SortableItem key={m.id} id={m.id}>
                  <tr style={{ cursor: "pointer", display: "table", width: "100%", tableLayout: "fixed" }} onClick={() => onSelectMod(m)}>
                    <td><span className="tc">{m.code}</span></td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{m.title}</div>
                      {m.description && <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 1, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.description}</div>}
                    </td>
                    <td style={{ fontFamily: "var(--fm)" }}>{m.videos?.length ?? 0}</td>
                    <td><Badge status={m.status} /></td>
                    <td>{m.isPreview ? <span className="badge bg-teal">Free</span> : <span style={{ fontSize: 11, color: "var(--ghost)" }}>Paid</span>}</td>
                    <td style={{ fontFamily: "var(--fm)", fontWeight: 700, color: "var(--teal)" }}>{(m._count?.progress ?? 0).toLocaleString()}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn btn-out btn-xs" onClick={() => onSelectMod(m)}><Eye size={10} /></button>
                        {canManage && <button className="btn btn-out btn-xs" onClick={() => onEditMod(m)} title="Edit module">✏️</button>}
                        {canManage && (m.status === "PUBLISHED"
                          ? <button className="btn btn-out btn-xs" onClick={() => requestConfirm("toggle", m.id, "⏸ Unpublish Module", `Are you sure you want to unpublish "${m.title}"? It will no longer be visible to subscribers.`, "PUBLISHED")}>⏸</button>
                          : <button className="btn btn-teal btn-xs" onClick={() => requestConfirm("toggle", m.id, "▶ Publish Module", `Publish "${m.title}"? It will become visible to all subscribers immediately.`, "DRAFT")}>▶</button>)}
                        {canManage && <button className="btn btn-out btn-xs" style={{ color: "var(--rose)" }} onClick={() => requestConfirm("delete", m.id, "🗃 Archive Module", `Archive "${m.title}"? It will be hidden from subscribers. You can restore it later from the database.`, null)}><Trash2 size={10} /></button>}
                        {canManage && <button className="btn btn-out btn-xs" style={{ color: "var(--rose)", fontWeight: 900 }} onClick={() => setPermDelMod(m)} title="Delete permanently">🗑</button>}
                      </div>
                    </td>
                  </tr>
                </SortableItem>
              ))}
            </SortableContext>
          </DndContext>
        </tbody>
      </table></div>
      {total > 0 && (
        <Pagination page={page} totalPages={totalPages} total={total}
          onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
      )}
      {permDelMod && (
        <PermanentDeleteModal
          description={`"${permDelMod.title}" and all its videos will be permanently deleted.`}
          loading={permDelModLoading}
          onClose={() => setPermDelMod(null)}
          onConfirm={async () => {
            setPermDelModLoading(true);
            try { await modulesApi.permanentDeleteModule(permDelMod.id); toast("🗑 Module permanently deleted"); onReordered?.(); setPermDelMod(null); }
            catch (e) { toast("Error: " + e.message); }
            finally { setPermDelModLoading(false); }
          }}
        />
      )}
    </div>
  );
}

export function PageModules({ toast, search: externalSearch, onNavigate, hasPermission }) {
  const canManage = hasPermission ? hasPermission('MANAGE_CONTENT') : true;
  const { t } = useLang();
  const [search, setSearch] = useState(externalSearch ?? "");
  const [status, setStatus] = useState("");
  const [groupId, setGroupId] = useState("");
  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);
  const [selMod, setSelMod] = useState(null);
  const [editMod, setEditMod] = useState(null);
  const [resMod, setResMod] = useState(null);
  const [creating, setCreating] = useState(false);
  const [manageGroups, setManageGroups] = useState(false);
  const [confirm, setConfirm] = useState(null); // { action, id, label, description }

  const debSearch = useDebounce(search, 400);
  const params = { ...(debSearch && { search: debSearch }), ...(status && { status }), ...(groupId && { groupId }) };

  const { data: md, loading, reload } = useApi(() => modulesApi.list(params), [debSearch, status, groupId]);
  const { data: gd } = useApi(() => modulesApi.listGroups());

  const groups = gd?.groups ?? [];
  const mods = md?.modules ?? [];

  const grouped = status || debSearch
    ? [{ id: "all", name: "Results", emoji: "🔍", items: mods }]
    : groups
      .filter(g => !groupId || g.id === groupId)
      .map(g => ({ ...g, items: mods.filter(m => m.groupId === g.id) }))
      .filter(g => g.items.length > 0);

  const toggle = async (id, currentStatus) => {
    try {
      currentStatus === "PUBLISHED" ? await modulesApi.unpublish(id) : await modulesApi.publish(id);
      toast(currentStatus === "PUBLISHED" ? "Module unpublished" : "✅ Module published!");
      reload();
    } catch (e) { toast("Error: " + e.message); }
  };

  const remove = async (id) => {
    try { await modulesApi.delete(id); toast("Module archived"); reload(); }
    catch (e) { toast("Error: " + e.message); }
  };

  // Confirmation handler
  const requestConfirm = (action, id, label, description, currentStatus) => {
    setConfirm({ action, id, label, description, currentStatus });
  };

  const executeConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    if (c.action === "toggle") await toggle(c.id, c.currentStatus);
    else if (c.action === "delete") await remove(c.id);
  };

  const reset = () => { setSearch(""); setStatus(""); setGroupId(""); };

  return (
    <div>
      {/* Confirmation Modal */}
      {confirm && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-ttl">{confirm.label}</div>
            <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.6, marginBottom: 20, padding: "12px 14px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
              {confirm.description}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-out" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
              <button
                className="btn"
                style={{ flex: 1, justifyContent: "center", background: confirm.action === "delete" ? "var(--rose)" : "var(--teal)", color: "white" }}
                onClick={executeConfirm}
              >
                {confirm.label}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats — all clickable for filtering */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { cls: "sc-teal",  lbl: t("mods.totalModules"), val: mods.length,                                     sub: t("mods.allModules"),        filter: "",          action: () => { setStatus(""); setGroupId(""); } },
          { cls: "sc-teal",  lbl: t("mods.published"),   val: mods.filter(m => m.status === "PUBLISHED").length, sub: t("mods.liveForSubscribers"), filter: "PUBLISHED",  action: () => setStatus(status === "PUBLISHED" ? "" : "PUBLISHED") },
          { cls: "sc-amber", lbl: t("mods.draft"),        val: mods.filter(m => m.status === "DRAFT").length,   sub: t("mods.unpublishedModules"), filter: "DRAFT",      action: () => setStatus(status === "DRAFT" ? "" : "DRAFT") },
          { cls: "sc-blue",  lbl: t("mods.groups"),       val: groups.length,                                   sub: t("mods.contentCategories"),  filter: null,         action: () => setManageGroups(true) },
        ].map(s => (
          <div key={s.lbl} className={`stat-card ${s.cls}`}
            onClick={s.action}
            style={{ cursor: "pointer", outline: s.filter && status === s.filter ? "2px solid white" : "none", transition: "transform .15s", position: "relative" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}>
            <div className="sc-lbl">{s.lbl}</div>
            <div className="sc-val">{s.val}</div>
            <div className="sc-sub">{s.sub}</div>
            <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 9, color: "rgba(255,255,255,.3)", fontFamily: "var(--fm)" }}>
              {s.filter === null ? t("mods.clickToManage") : (s.filter === "" ? t("mods.clickToReset") : (status === s.filter ? t("ui.active") : t("ui.clickToFilter")))}
            </div>
          </div>
        ))}
      </div>

      <div className="sec-hd">
        <div>
          <div className="sec-ttl">{t("mods.title")}</div>
          <div className="sec-sub">{mods.length} {debSearch ? ` matching "${debSearch}"` : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-out" onClick={() => setManageGroups(true)}><Filter size={14} /> {t("mods.manageGroups")}</button>
          {canManage && <button className="btn btn-teal" onClick={() => setCreating(true)}><Plus size={14} /> {t("mods.createModule")}</button>}
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 280, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input placeholder={t("common.search")} value={search} onChange={e => setSearch(e.target.value)} style={{ border: "none", background: "none", outline: "none", fontFamily: "var(--fb)", fontSize: 13, flex: 1, color: "var(--ink)" }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)" }}><X size={12} /></button>}
        </div>
        <select className="f-sel" style={{ width: "auto" }} value={groupId} onChange={e => setGroupId(e.target.value)}>
          <option value="">{t("mods.groups")}</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.emoji} {g.name}</option>)}
        </select>
        <select className="f-sel" style={{ width: "auto" }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">{t("mods.allStatus")}</option>
          <option value="PUBLISHED">{t("mods.statusPublished")}</option>
          <option value="DRAFT">{t("mods.statusDraft")}</option>
          <option value="ARCHIVED">{t("mods.statusArchived")}</option>
        </select>
        {(search || status || groupId) && <button className="btn btn-out btn-sm" onClick={reset}><RefreshCw size={12} /> {t("users.clear")}</button>}
      </div>

      {loading ? <Spinner /> : grouped.length === 0 ? <Empty icon="📚" message={t("mods.noModulesMatch")} /> : grouped.map(g => (
        <ModuleGroupSection key={g.id} g={g} onSelectMod={m => onNavigate ? onNavigate("moduleDetail", m.id) : setSelMod(m)} onEditMod={setEditMod} requestConfirm={requestConfirm} canManage={canManage} onReordered={reload} />
      ))}

      {selMod && (
        <ModuleDetailModal
          mod={selMod} onClose={() => setSelMod(null)}
          onManageResources={m => { setSelMod(null); setResMod(m); }}
          onPublish={id => requestConfirm("toggle", id, "▶ Publish Module", "Publish this module? It will become visible to all subscribers immediately.", "DRAFT")}
          onUnpublish={id => requestConfirm("toggle", id, "⏸ Unpublish Module", "Unpublish this module? Subscribers will no longer see it.", "PUBLISHED")}
          onDelete={id => requestConfirm("delete", id, "🗃 Archive Module", "Archive this module? It will be hidden from subscribers.", null)}
        />
      )}
      {resMod && <ModuleResourcesModal mod={resMod} onClose={() => setResMod(null)} toast={toast} />}
      {editMod && <EditModuleModal mod={editMod} groups={groups} onClose={() => setEditMod(null)} onSave={reload} toast={toast} />}
      {creating && <CreateModuleModal groups={groups} onClose={() => setCreating(false)} onCreate={reload} toast={toast} />}
      {manageGroups && <GroupManageModal onClose={() => setManageGroups(false)} onSaved={reload} toast={toast} />}
    </div>
  );
}

// ─── MODULE DETAIL PAGE ──────────────────────────────────────────────────────

export function PageModuleDetail({ moduleId, toast, onNavigate, search = "" }) {
  const { t } = useLang();
  const { data, loading, error, reload } = useApi(() => modulesApi.getDetails(moduleId), [moduleId]);
  const { data: vd, reload: reloadVids } = useApi(() => videosApi.list({ moduleId, limit: 200 }), [moduleId]);
  const { data: gd } = useApi(() => modulesApi.listGroups());

  const [resMod, setResMod]     = useState(null);
  const [editMod, setEditMod]   = useState(null);
  const [creatingVid, setCreatingVid] = useState(false);
  const [editVid, setEditVid]   = useState(null);
  const [selVid, setSelVid]     = useState(null);
  const [confirm, setConfirm]   = useState(null);
  const [permDelVid, setPermDelVid]       = useState(null);
  const [permDelVidLoading, setPermDelVidLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all"); // "all" | "videos" | "resources"
  const [activePanel,  setActivePanel]  = useState(null);  // "completions" | "ratings" | null
  const contentSectionRef = useRef(null);

  const m     = data?.module;
  const groups = gd?.groups ?? [];

  const toggle = async () => {
    if (!m) return;
    try {
      m.status === "PUBLISHED" ? await modulesApi.unpublish(m.id) : await modulesApi.publish(m.id);
      toast(m.status === "PUBLISHED" ? "⏸ Module unpublished" : "✅ Module published!");
      reload();
    } catch (e) { toast(e.message); }
  };

  const archiveVideo = async (id) => {
    try { await videosApi.delete(id); toast("Video archived"); reloadVids(); } catch (e) { toast(e.message); }
  };
  const publishVideo = async (id, status) => {
    try { await videosApi.update(id, { status }); toast(status === "PUBLISHED" ? "📣 Published!" : "📝 Unpublished"); reloadVids(); }
    catch (e) { toast("Error: " + e.message); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const fmtDur  = (s) => s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—";

  // Derive before early returns so hooks can be called unconditionally
  const sq = search.trim().toLowerCase();
  const _vids = vd?.videos ?? [];
  const _resources = data?.module?.resources ?? [];
  const vids      = sq ? _vids.filter(v => v.title?.toLowerCase().includes(sq) || v.type?.toLowerCase().includes(sq)) : _vids;
  const resources = sq ? _resources.filter(r => r.title?.toLowerCase().includes(sq) || r.type?.toLowerCase().includes(sq) || r.description?.toLowerCase().includes(sq)) : _resources;

  const handleReorderVids = useCallback(async (items) => {
    try { await modulesApi.reorderVideos(items); reloadVids(); } catch { /* silent */ }
  }, [reloadVids]);
  const { sensors: vidSensors, items: sortedVids, handleDragEnd: vidDragEnd } = useSortableList(vids, handleReorderVids);

  const handleReorderRes = useCallback(async (items) => {
    try { await modulesApi.reorderResources(moduleId, items); reload(); } catch { /* silent */ }
  }, [moduleId, reload]);
  const { sensors: resSensors, items: sortedRes, handleDragEnd: resDragEnd } = useSortableList(resources, handleReorderRes);

  if (loading) return <Spinner />;
  if (error || !m) return (
    <div>
      <button onClick={() => onNavigate("modules")} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "5px 12px", cursor: "pointer", fontFamily: "var(--fb)", fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>{t("mods.backToModules")}</button>
      <div style={{ background: "var(--rose-lo)", border: "1px solid var(--rose)", borderRadius: 12, padding: "20px 24px", color: "var(--rose)" }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ {t("mods.failedToLoad")}</div>
        <div style={{ fontSize: 12, fontFamily: "var(--fm)" }}>{error ?? "Module not found"}</div>
        <button className="btn btn-out btn-sm" style={{ marginTop: 12 }} onClick={reload}>↺ Retry</button>
      </div>
    </div>
  );

  const completions = m._count?.progress ?? 0;
  const rawResources = m.resources ?? [];

  return (
    <div>
      {/* ── Breadcrumb ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
        <button
          onClick={() => onNavigate("modules")}
          style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "5px 12px", cursor: "pointer", fontFamily: "var(--fb)", fontSize: 12, color: "var(--muted)", display: "flex", alignItems: "center", gap: 6 }}
        >{t("mods.backToModules")}</button>
        <span style={{ color: "var(--ghost)" }}>/</span>
        <span className="tc" style={{ fontSize: 13 }}>{m.code}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>{m.title}</span>
      </div>

      {/* ── Module header card ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ padding: "18px 20px" }}>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <span className="tc" style={{ fontSize: 15 }}>{m.code}</span>
                <Badge status={m.status} />
                {m.isPreview && <span className="badge bg-teal" style={{ fontSize: 9 }}>{t("mods.freePreview")}</span>}
                {m.group && (
                  <span style={{ background: "var(--surface)", border: "1px solid var(--border)", fontSize: 11, padding: "2px 8px", borderRadius: 20, color: "var(--muted)" }}>
                    {m.group.emoji} {m.group.name}
                  </span>
                )}
              </div>
              <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, marginBottom: 8, lineHeight: 1.2 }}>{m.title}</div>
              {m.description && (
                <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.55, borderLeft: "3px solid var(--teal)", paddingLeft: 10 }}>{m.description}</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
              <button className="btn btn-out btn-sm" onClick={() => setEditMod(m)}>{t("mods.editModule")}</button>
              <button
                className={`btn btn-sm ${m.status === "PUBLISHED" ? "btn-out" : "btn-teal"}`}
                onClick={toggle}
              >{m.status === "PUBLISHED" ? t("mods.unpublish") : t("mods.publish")}</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats row (clickable) ── */}
      <div className="stats-grid" style={{ marginBottom: activePanel ? 0 : 16 }}>
        {[
          { cls: "sc-teal",  lbl: t("mods.completions"), val: completions.toLocaleString(), sub: t("mods.completionsCTA"), panel: "completions" },
          { cls: "sc-amber", lbl: t("mods.avgRating"),  val: m.avgRating ?? "—",          sub: t("mods.outOf50"),          panel: "ratings"     },
          { cls: "sc-blue",  lbl: t("mods.videos"),     val: vids.length,                 sub: t("mods.totalVideos"),      filter: "videos"     },
          { cls: "sc-rose",  lbl: t("mods.manageResources").replace("📎 ", ""), val: resources.length, sub: t("mods.attachments"), filter: "resources"  },
        ].map(s => (
          <div
            key={s.lbl}
            className={`stat-card ${s.cls} clickable${
              (s.panel && activePanel === s.panel) || (s.filter && activeFilter === s.filter) ? " active" : ""
            }`}
            onClick={() => {
              if (s.panel) {
                setActivePanel(p => p === s.panel ? null : s.panel);
              } else if (s.filter) {
                setActivePanel(null);
                setActiveFilter(f => f === s.filter ? "all" : s.filter);
                setTimeout(() => contentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
              }
            }}
          >
            <div className="sc-lbl">{s.lbl}</div>
            <div className="sc-val">{s.val}</div>
            <div className="sc-sub">{s.sub}</div>
            <div style={{ fontSize: 9, color: "var(--ghost)", marginTop: 6, fontStyle: "italic" }}>
              {s.panel ? (activePanel === s.panel ? "▲ hide" : "▼ show details") : (activeFilter === s.filter ? "● filtered" : "click to filter")}
            </div>
          </div>
        ))}
      </div>

      {/* ── Inline panels for Completions / Ratings ── */}
      {activePanel === "completions" && (
        <div className="card" style={{ marginBottom: 16, borderTop: "3px solid var(--teal)" }}>
          <div className="card-hdr">
            <div className="card-ttl">🏆 Completions</div>
            <button className="btn btn-out btn-xs" onClick={() => setActivePanel(null)}>✕ Close</button>
          </div>
          <div className="card-body">
            <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "var(--fd)", fontSize: 42, fontWeight: 900, color: "var(--teal)", lineHeight: 1 }}>{completions.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: "var(--ghost)", marginTop: 4 }}>learners completed this module</div>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.6 }}>
                  Completion is counted when a learner marks the module as done or views all content.
                  To see individual user details, visit the <strong>Users</strong> section and filter by module progress.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activePanel === "ratings" && (
        <div className="card" style={{ marginBottom: 16, borderTop: "3px solid var(--amber)" }}>
          <div className="card-hdr">
            <div className="card-ttl">⭐ Module Ratings</div>
            <button className="btn btn-out btn-xs" onClick={() => setActivePanel(null)}>✕ Close</button>
          </div>
          <div className="card-body">
            <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "var(--fd)", fontSize: 42, fontWeight: 900, color: "var(--amber)", lineHeight: 1 }}>
                  {m.avgRating ? Number(m.avgRating).toFixed(1) : "—"}
                </div>
                <div style={{ fontSize: 12, color: "var(--ghost)", marginTop: 4 }}>average out of 5.0</div>
                {m.avgRating && (
                  <div style={{ marginTop: 8, display: "flex", justifyContent: "center", gap: 3 }}>
                    {[1,2,3,4,5].map(i => (
                      <span key={i} style={{ fontSize: 18, color: i <= Math.round(m.avgRating) ? "var(--amber)" : "var(--border)" }}>★</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.6 }}>
                  Ratings are submitted by learners after completing the module. Each learner can rate the module once.
                </div>
                {m._count?.feedbacks != null && (
                  <div style={{ marginTop: 8, fontSize: 13, color: "var(--muted)" }}>
                    <strong>{m._count.feedbacks}</strong> rating{m._count.feedbacks !== 1 ? "s" : ""} submitted
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Videos & Resources combined section ── */}
      <div ref={contentSectionRef} className="card" style={{ marginBottom: 16 }}>
        <div className="card-hdr">
          <div className="card-ttl"><div className="card-ico">🎬</div>{t("mods.videosResources")}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {/* Filter tabs */}
            <div className="sec-tabs">
              {["all", "videos", "resources"].map(f => (
                <button
                  key={f}
                  className={`sec-tab${activeFilter === f ? " active" : ""}`}
                  onClick={() => setActiveFilter(f)}
                >
                  {f === "all" ? `All (${vids.length + resources.length})` : f === "videos" ? `🎥 ${t("mods.videos")} (${vids.length})` : `📎 ${t("mods.manageResources").replace("📎 ","")} (${resources.length})`}
                </button>
              ))}
            </div>
            <button className="btn btn-out btn-sm" onClick={() => setResMod(m)}>{t("mods.editResources")}</button>
            <button className="btn btn-teal btn-sm" onClick={() => setCreatingVid(true)}><Plus size={12} /> {t("videos.addVideo")}</button>
          </div>
        </div>

        {/* Videos table */}
        {(activeFilter === "all" || activeFilter === "videos") && (
          <>
            {activeFilter === "all" && vids.length > 0 && resources.length > 0 && (
              <div style={{ padding: "10px 18px 4px", fontSize: 11, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".7px", borderBottom: "1px solid var(--border)", background: "var(--surface)" }}>
                🎥 Videos ({vids.length})
              </div>
            )}
            {vids.length === 0 && activeFilter === "videos" ? (
              <div style={{ padding: 32, textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🎥</div>
                <div style={{ fontSize: 14, color: "var(--ink2)", marginBottom: 6 }}>{t("mods.noVideosYet")}</div>
                <div style={{ fontSize: 12, color: "var(--ghost)", marginBottom: 16 }}>Add the first video for this module</div>
                <button className="btn btn-teal btn-sm" onClick={() => setCreatingVid(true)}><Plus size={12} /> {t("videos.addVideo")}</button>
              </div>
            ) : vids.length > 0 ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th style={{ width: 24 }}></th><th>{t("tbl.title")}</th><th>{t("tbl.type")}</th><th>{t("tbl.source")}</th><th>{t("tbl.duration")}</th><th>{t("tbl.status")}</th><th>{t("tbl.added")}</th><th>{t("tbl.actions")}</th></tr></thead>
                  <tbody>
                    <DndContext sensors={vidSensors} collisionDetection={closestCenter} onDragEnd={vidDragEnd}>
                      <SortableContext items={sortedVids.map(v => v.id)} strategy={verticalListSortingStrategy}>
                        {sortedVids.map(v => {
                          const isYT    = !!v.hlsUrl && (v.hlsUrl.includes("youtu.be") || v.hlsUrl.includes("youtube.com"));
                          const isCFRow = !!v.cloudflareStreamId;
                          const isMinioRow = !isYT && !isCFRow && !!v.hlsUrl;
                          return (
                            <SortableItem key={v.id} id={v.id}>
                              <tr style={{ cursor: "pointer", display: "table", width: "100%", tableLayout: "fixed" }} onClick={() => setSelVid(v)}>
                                <td style={{ fontWeight: 600 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <VideoThumb url={v.thumbnailUrl} />
                                    <span style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.title}</span>
                                  </div>
                                </td>
                                <td><span className={`badge bg-${v.type === "PRACTICE" ? "teal" : v.type === "EXPLANATION" ? "blue" : "gray"}`} style={{ fontSize: 9 }}>{v.type}</span></td>
                                <td>
                                  {isYT      ? <span style={{ color: "#c00", fontSize: 11, fontWeight: 700 }}>▶ YouTube</span>
                                  : isCFRow  ? <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--fm)" }}>☁️ Cloudflare</span>
                                  : isMinioRow ? <span style={{ fontSize: 10, color: "var(--teal)", fontFamily: "var(--fm)", fontWeight: 700 }}>🗄️ MinIO</span>
                                  : <span style={{ color: "var(--ghost)" }}>—</span>}
                                </td>
                                <td style={{ fontFamily: "var(--fm)", color: "var(--muted)" }}>{fmtDur(v.durationSecs)}</td>
                                <td><Badge status={v.status} /></td>
                                <td style={{ fontSize: 11, color: "var(--ghost)", fontFamily: "var(--fm)", whiteSpace: "nowrap" }}>{fmtDate(v.createdAt)}</td>
                                <td onClick={e => e.stopPropagation()}>
                                  <div style={{ display: "flex", gap: 4 }}>
                                    <button className="btn btn-out btn-xs" onClick={() => setSelVid(v)}><Play size={10} /></button>
                                    {v.status === "PUBLISHED"
                                      ? <button className="btn btn-xs btn-out btn-sm" style={{ fontSize: 9, color: "var(--ghost)" }} onClick={() => publishVideo(v.id, "DRAFT")}>📝</button>
                                      : <button className="btn btn-xs btn-teal" style={{ fontSize: 9 }} onClick={() => publishVideo(v.id, "PUBLISHED")}>📣</button>}
                                    <button className="btn btn-out btn-xs" onClick={() => { setSelVid(null); setEditVid(v); }}>✏️</button>
                                    <button className="btn btn-out btn-xs" style={{ color: "var(--rose)" }} onClick={() => archiveVideo(v.id)}><Trash2 size={10} /></button>
                                    <button className="btn btn-out btn-xs" style={{ color: "var(--rose)", fontWeight: 900 }} onClick={() => setPermDelVid(v)} title="Delete permanently">🗑</button>
                                  </div>
                                </td>
                              </tr>
                            </SortableItem>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        )}

        {/* Resources grid */}
        {(activeFilter === "all" || activeFilter === "resources") && (
          <>
            {activeFilter === "all" && vids.length > 0 && resources.length > 0 && (
              <div style={{ padding: "10px 18px 4px", fontSize: 11, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".7px", borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
                📎 Resources ({resources.length})
              </div>
            )}
            {resources.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", borderTop: activeFilter === "all" && vids.length > 0 ? "1px solid var(--border)" : "none" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
                <div style={{ fontSize: 13, color: "var(--ink2)", marginBottom: 4 }}>{t("mods.noResourcesYet")}</div>
                <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 12 }}>Add PDFs, books, audio files, links and more</div>
                <button className="btn btn-out btn-sm" onClick={() => setResMod(m)}>+ {t("mods.addResource")}</button>
              </div>
            ) : (
              <div className="tbl-wrap" style={{ borderTop: activeFilter === "all" && vids.length > 0 ? "1px solid var(--border)" : "none" }}>
                <table>
                  <thead>
                    <tr><th style={{ width: 24 }}></th><th>{t("tbl.title")}</th><th>{t("tbl.type")}</th><th>{t("tbl.description")}</th><th>{t("tbl.storage")}</th><th>{t("tbl.added")}</th><th>{t("tbl.actions")}</th></tr>
                  </thead>
                  <tbody>
                    <DndContext sensors={resSensors} collisionDetection={closestCenter} onDragEnd={resDragEnd}>
                      <SortableContext items={sortedRes.map(r => r.id)} strategy={verticalListSortingStrategy}>
                        {sortedRes.map(r => {
                          const typeIcon  = { PDF: "📄", BOOK: "📚", AUDIO: "🎧", LINK: "🔗", OTHER: "📁" };
                          const typeCls   = { PDF: "rose", BOOK: "blue", AUDIO: "teal", LINK: "orange", OTHER: "gray" };
                          const isMinio   = r.url && (r.url.includes("resources.devemm.rw") || /\/kunga\//.test(r.url));
                          const isLegacy  = r.url && (r.url.includes("/uploads/resources/") || r.url.includes("/uploads/avatars/"));
                          const isR2      = r.url && (r.url.includes("r2.dev") || r.url.includes("cloudflare") || r.url.includes("cdnjs"));
                          const storageLabel = isMinio ? "🗄️ MinIO" : isLegacy ? "🖥️ Server" : isR2 ? "☁️ R2" : r.url ? "🔗 Link" : "—";
                          return (
                            <SortableItem key={r.id} id={r.id}>
                              <tr style={{ cursor: r.url ? "pointer" : "default", display: "table", width: "100%", tableLayout: "fixed" }}
                                onClick={() => { if (r.url) window.open(r.url, "_blank", "noopener,noreferrer"); }}
                              >
                                <td style={{ fontWeight: 600 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                                      {typeIcon[r.type] ?? "📁"}
                                    </div>
                                    <span style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</span>
                                  </div>
                                </td>
                                <td><span className={`badge bg-${typeCls[r.type] ?? "gray"}`} style={{ fontSize: 9 }}>{r.type}</span></td>
                                <td style={{ fontSize: 11, color: "var(--ghost)", maxWidth: 240 }}>
                                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.description || "—"}</div>
                                </td>
                                <td style={{ fontSize: 11, color: "var(--muted)", fontFamily: "var(--fm)", whiteSpace: "nowrap" }}>{storageLabel}</td>
                                <td style={{ fontSize: 11, color: "var(--ghost)", fontFamily: "var(--fm)", whiteSpace: "nowrap" }}>{fmtDate(r.createdAt)}</td>
                                <td onClick={e => e.stopPropagation()}>
                                  <div style={{ display: "flex", gap: 4 }}>
                                    {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="btn btn-out btn-xs" style={{ textDecoration: "none" }}>Open ↗</a>}
                                    <button className="btn btn-out btn-xs" onClick={() => setResMod(m)}>✏️</button>
                                    <button
                                      className="btn btn-xs"
                                      style={{ background: "var(--rose-lo)", border: "1.5px solid var(--rose)", color: "var(--rose)" }}
                                      onClick={() => setConfirm({
                                        label: "Delete Resource",
                                        desc: `Remove "${r.title}" permanently from this module?`,
                                        fn: () => modulesApi.deleteResource(r.id)
                                          .then(() => { toast("Resource deleted"); reload(); })
                                          .catch(e => toast("Error: " + e.message)),
                                      })}
                                    >🗑️</button>
                                  </div>
                                </td>
                              </tr>
                            </SortableItem>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Empty state when both are empty */}
        {activeFilter === "all" && vids.length === 0 && resources.length === 0 && (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 42, marginBottom: 10 }}>🎬</div>
            <div style={{ fontSize: 15, color: "var(--ink2)", fontWeight: 600, marginBottom: 6 }}>No content yet</div>
            <div style={{ fontSize: 12, color: "var(--ghost)", marginBottom: 18 }}>Add videos or resources to this module</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button className="btn btn-teal btn-sm" onClick={() => setCreatingVid(true)}><Plus size={12} /> Add Video</button>
              <button className="btn btn-out btn-sm" onClick={() => setResMod(m)}>📎 Add Resource</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Confirm modal ── */}
      {confirm && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-ttl">{confirm.label}</div>
            <div style={{ fontSize: 13, color: "var(--ink2)", padding: "10px 12px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 16 }}>{confirm.desc}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-out" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
              <button className="btn" style={{ flex: 1, justifyContent: "center", background: "var(--rose)", color: "white" }} onClick={() => { confirm.fn(); setConfirm(null); }}>{confirm.label}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Overlays ── */}
      {resMod   && <ModuleResourcesModal mod={resMod}   onClose={() => setResMod(null)}   toast={toast} />}
      {editMod  && <EditModuleModal mod={editMod} groups={groups} onClose={() => setEditMod(null)} onSave={reload} toast={toast} />}
      {editVid  && <EditVideoModal vid={editVid}  onClose={() => setEditVid(null)}  onSave={reloadVids} toast={toast} />}
      {creatingVid && <CreateVideoModal onClose={() => setCreatingVid(false)} onCreate={reloadVids} toast={toast} defaultModuleId={moduleId} />}
      {selVid   && (
        <VideoDetailModal
          vid={selVid}
          onClose={() => setSelVid(null)}
          onEdit={v => { setSelVid(null); setEditVid(v); }}
          onArchive={id => { setSelVid(null); archiveVideo(id); }}
          onPublish={(id, status) => { publishVideo(id, status); setSelVid(null); }}
        />
      )}
      {permDelVid && (
        <PermanentDeleteModal
          description={`"${permDelVid.title}" will be permanently deleted, including its MinIO file.`}
          loading={permDelVidLoading}
          onClose={() => setPermDelVid(null)}
          onConfirm={async () => {
            setPermDelVidLoading(true);
            try { await videosApi.permanentDelete(permDelVid.id); toast("🗑 Video permanently deleted"); reloadVids(); setPermDelVid(null); }
            catch (e) { toast("Error: " + e.message); }
            finally { setPermDelVidLoading(false); }
          }}
        />
      )}
    </div>
  );
}

// ─── VIDEOS ───────────────────────────────────────────────────────────────────

function ytIdFromUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("?")[0];
    return u.searchParams.get("v") ?? u.pathname.split("/").pop();
  } catch { return null; }
}

function VideoDetailModal({ vid, onClose, onEdit, onArchive, onPublish, hasPermission }) {
  const { t } = useLang();
  const isYT    = !!vid.hlsUrl && (vid.hlsUrl.includes("youtu.be") || vid.hlsUrl.includes("youtube.com"));
  const isCF    = !!vid.cloudflareStreamId;
  const isMinio = !isYT && !isCF && !!vid.hlsUrl; // MinIO / server-side MP4 stored in hlsUrl
  const ytId    = isYT ? ytIdFromUrl(vid.hlsUrl) : null;
  const dur = vid.durationSecs
    ? `${Math.floor(vid.durationSecs / 60)}:${String(vid.durationSecs % 60).padStart(2, "0")}`
    : null;
  const typeColor = { PRACTICE: "teal", EXPLANATION: "blue", INTRO: "gray" };
  const iframeRef = useRef(null);
  const videoRef  = useRef(null);
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // Listen for YouTube player state via postMessage (no API key needed)
  useEffect(() => {
    if (!ytId) return;
    const handler = (e) => {
      try {
        const msg = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (msg?.event === "onStateChange" && msg?.info === 0) setVideoEnded(true);
        if (msg?.event === "onStateChange" && (msg?.info === 1 || msg?.info === 2)) setVideoEnded(false);
      } catch {}
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [ytId]);

  const replay = () => {
    setVideoEnded(false);
    if (ytId && iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "seekTo", args: [0, true] }), "*"
      );
      iframeRef.current.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*"
      );
    }
    if (isMinio && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  return (
    <div className="modal-ov open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640, width: "95vw" }} onClick={e => e.stopPropagation()}>
        <div className="modal-ttl">{t("videos.details")} <CloseX onClick={onClose} /></div>

        {/* ── YouTube player ── */}
        {isYT && ytId && (
          <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 10, overflow: "hidden", background: "#000", marginBottom: 16 }}>
            <iframe
              ref={iframeRef}
              src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1&iv_load_policy=3&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={vid.title}
            />
            {videoEnded && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.88)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, zIndex: 10 }}>
                <div style={{ fontSize: 40 }}>🎬</div>
                <div style={{ color: "white", fontFamily: "var(--fd)", fontSize: 15, fontWeight: 700 }}>{t("videos.videoEnded")}</div>
                <button className="btn btn-teal" onClick={replay} style={{ padding: "8px 28px", fontSize: 13 }}>{t("videos.replay")}</button>
              </div>
            )}
          </div>
        )}

        {/* ── MinIO / server-uploaded video — native HTML5 player ── */}
        {isMinio && (
          <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "#000", marginBottom: 16 }}>
            {videoError ? (
              <div style={{ padding: "32px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
                <div style={{ color: "white", fontFamily: "var(--fd)", fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Could not load video</div>
                <div style={{ color: "rgba(255,255,255,.5)", fontSize: 11, marginBottom: 14 }}>Check that the file exists in MinIO storage</div>
                <a href={vid.hlsUrl} target="_blank" rel="noreferrer" className="btn btn-teal btn-sm" style={{ display: "inline-flex" }}>⬆ Open file directly</a>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  src={vid.hlsUrl}
                  controls
                  autoPlay
                  playsInline
                  style={{ display: "block", width: "100%", maxHeight: 380, background: "#000", outline: "none" }}
                  onEnded={() => setVideoEnded(true)}
                  onPlay={() => setVideoEnded(false)}
                  onError={() => setVideoError(true)}
                />
                {videoEnded && (
                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.88)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, zIndex: 10 }}>
                    <div style={{ fontSize: 40 }}>🗄️</div>
                    <div style={{ color: "white", fontFamily: "var(--fd)", fontSize: 15, fontWeight: 700 }}>{t("videos.videoEnded")}</div>
                    <button className="btn btn-teal" onClick={replay} style={{ padding: "8px 28px", fontSize: 13 }}>{t("videos.replay")}</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Cloudflare / thumbnail fallback ── */}
        {!isYT && !isMinio && vid.thumbnailUrl && (
          <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 10, overflow: "hidden", marginBottom: 16, background: "var(--surface)" }}>
            <img src={vid.thumbnailUrl} alt={vid.title} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} onError={e => e.currentTarget.style.display = "none"} />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.45)" }}>
              <div style={{ background: "rgba(255,255,255,.15)", backdropFilter: "blur(4px)", borderRadius: "50%", width: 56, height: 56, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>▶</div>
            </div>
            {isCF && <div style={{ position: "absolute", bottom: 10, left: 10, background: "rgba(0,0,0,.7)", color: "white", fontSize: 10, fontFamily: "var(--fb)", padding: "3px 8px", borderRadius: 4 }}>☁️ Cloudflare Stream — signed URL required to play</div>}
          </div>
        )}
        {!isYT && !isMinio && !vid.thumbnailUrl && isCF && (
          <div style={{ borderRadius: 10, background: "var(--surface)", border: "1px solid var(--border)", marginBottom: 16, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>☁️</div>
            <div style={{ fontFamily: "var(--fb)", fontSize: 13, color: "var(--ghost)" }}>Cloudflare Stream — signed URL required to play</div>
            <div style={{ fontFamily: "var(--fm)", fontSize: 10, color: "var(--ghost)", marginTop: 4 }}>ID: {vid.cloudflareStreamId}</div>
          </div>
        )}

        {/* Title + badges */}
        <div style={{ fontFamily: "var(--fd)", fontSize: 18, fontWeight: 900, marginBottom: 10 }}>{vid.title}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
          <span className={`badge bg-${typeColor[vid.type] ?? "gray"}`} style={{ fontSize: 10 }}>{vid.type}</span>
          <Badge status={vid.status} />
          {vid.isPreviewClip && <span className="badge bg-amber" style={{ fontSize: 10 }}>🔓 Preview Clip</span>}
          {isYT    && <span className="badge" style={{ background: "#fee", color: "#c00", fontSize: 10, border: "1px solid #fcc" }}>▶ YouTube</span>}
          {isCF    && <span className="badge bg-blue" style={{ fontSize: 10 }}>☁️ Cloudflare</span>}
          {isMinio && <span className="badge bg-teal" style={{ fontSize: 10 }}>🗄️ MinIO</span>}
        </div>

        {/* Details grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[
            [t("videos.module"), vid.module ? `${vid.module.code} — ${vid.module.title ?? ""}` : "—"],
            [t("videos.duration"), dur ?? t("videos.unknown")],
            [t("videos.added"), vid.createdAt ? new Date(vid.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"],
            [t("videos.idLabel"), vid.id],
          ].map(([label, value]) => (
            <div key={label} style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 12px", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fb)", marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 12, fontFamily: "var(--fm)", color: "var(--ink2)", wordBreak: "break-all" }}>{value}</div>
            </div>
          ))}
        </div>
        {isYT && vid.hlsUrl && (
          <div style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 12px", border: "1px solid var(--border)", marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fb)", marginBottom: 3 }}>🔗 YouTube URL</div>
            <div style={{ fontSize: 11, fontFamily: "var(--fm)", color: "var(--teal)", wordBreak: "break-all" }}>{vid.hlsUrl}</div>
          </div>
        )}
        {isMinio && vid.hlsUrl && (
          <div style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 12px", border: "1px solid var(--border)", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fb)", marginBottom: 3 }}>🗄️ MinIO Storage URL</div>
              <div style={{ fontSize: 11, fontFamily: "var(--fm)", color: "var(--teal)", wordBreak: "break-all" }}>{vid.hlsUrl}</div>
            </div>
            <a href={vid.hlsUrl} target="_blank" rel="noreferrer" className="btn btn-out btn-xs" style={{ flexShrink: 0 }}>⬆ Open</a>
          </div>
        )}

        {/* Actions */}
        {hasPermission?.('MANAGE_VIDEOS') && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {vid.status !== "PUBLISHED" ? (
              <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={() => onPublish(vid.id, "PUBLISHED")}>
                {t("videos.publish")}
              </button>
            ) : (
              <button className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={() => onPublish(vid.id, "DRAFT")}>
                {t("videos.unpublish")}
              </button>
            )}
            <button className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={() => { onClose(); onEdit(vid); }}>{t("videos.editBtn")}</button>
            <button className="btn btn-out" style={{ flex: 1, justifyContent: "center", color: "var(--rose)" }} onClick={() => { onClose(); onArchive(vid.id); }}>{t("videos.archive")}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ytGetDuration(videoId) {
  return new Promise((resolve, reject) => {
    const launch = () => {
      const el = document.createElement("div");
      el.id = `yt-dur-${Date.now()}`;
      Object.assign(el.style, { position: "fixed", left: "-9999px", opacity: "0", width: "1px", height: "1px", overflow: "hidden" });
      document.body.appendChild(el);
      const cleanup = () => { try { document.body.removeChild(el); } catch {} };
      const t = setTimeout(() => { cleanup(); reject(new Error("timeout")); }, 15000);
      new window.YT.Player(el.id, {
        videoId,
        playerVars: { autoplay: 0 },
        events: {
          onReady: e => { clearTimeout(t); const d = e.target.getDuration(); e.target.destroy(); cleanup(); resolve(Math.round(d)); },
          onError: () => { clearTimeout(t); cleanup(); reject(new Error("player error")); },
        },
      });
    };
    if (window.YT?.Player) {
      launch();
    } else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); window.onYouTubeIframeAPIReady = prev; launch(); };
      if (!document.getElementById("yt-iframe-api-script")) {
        const s = document.createElement("script");
        s.id = "yt-iframe-api-script";
        s.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(s);
      }
    }
  });
}

/** Small list-row thumbnail with graceful fallback to a 🎬 placeholder
 *  when `url` is missing or fails to load (e.g. stale/broken thumbnailUrl). */
function VideoThumb({ url }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <div style={{ width: 54, height: 30, borderRadius: 4, background: "var(--surface)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🎬</div>
    );
  }
  return (
    <img src={url} alt="" style={{ width: 54, height: 30, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} onError={() => setFailed(true)} />
  );
}

/** Convert a data: URL (e.g. canvas.toDataURL output) into a File object. */
function dataURLtoFile(dataUrl, filename) {
  const [header, b64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return new File([u8], filename, { type: mime });
}

/** Extract duration (seconds) + a thumbnail data-URL from a local video File. */
function getVideoMetadata(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    video.src = url;
    const cleanup = () => { try { URL.revokeObjectURL(url); } catch {} };
    const done = (r) => { clearTimeout(tid); cleanup(); resolve(r); };
    const tid = setTimeout(() => done({ duration: null, thumb: null }), 10000);

    video.onerror = () => done({ duration: null, thumb: null });
    video.onloadedmetadata = () => {
      const dur = isFinite(video.duration) && video.duration > 0 ? Math.round(video.duration) : null;
      video.currentTime = Math.min(2, (video.duration / 4) || 0);
      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          const scale = Math.min(1, 640 / (video.videoWidth || 640));
          canvas.width  = Math.round((video.videoWidth  || 640) * scale);
          canvas.height = Math.round((video.videoHeight || 360) * scale);
          canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
          done({ duration: dur, thumb: canvas.toDataURL('image/jpeg', 0.7) });
        } catch { done({ duration: dur, thumb: null }); }
      };
    };
  });
}

function CreateVideoModal({ onClose, onCreate, toast, defaultModuleId = "" }) {
  const { t } = useLang();
  const { data: gd } = useApi(() => modulesApi.listGroups());
  const groups = gd?.groups ?? [];

  const [source, setSource] = useState("youtube"); // "youtube" | "cloudflare" | "minio"
  const [form, setForm] = useState({ title: "", moduleId: defaultModuleId, type: "EXPLANATION", youtubeUrl: "", cloudflareVideoId: "", minioUrl: "", durationSecs: "", thumbnailUrl: "", isPreviewClip: false, titleTranslations: { fr: "", rw: "", sw: "" } });
  const [vidLang, setVidLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [ytFetching, setYtFetching] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [thumbUploading, setThumbUploading] = useState(false);
  const videoFileRef = useRef(null);
  const thumbFileRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Manual thumbnail image upload — used by mobile app & admin lists.
  // Optional: overrides any auto-extracted/YouTube thumbnail when provided.
  const handleThumbFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbUploading(true);
    try {
      const tr = await paymentsApi.videoUploadServer(file);
      setForm(f => ({ ...f, thumbnailUrl: tr.fileUrl }));
      setThumbPreview(URL.createObjectURL(file));
      toast("✅ Thumbnail uploaded!");
    } catch (err) { toast("Thumbnail upload error: " + err.message); }
    finally { setThumbUploading(false); }
  };

  // Auto-fill from YouTube when URL is pasted
  useEffect(() => {
    if (source !== "youtube" || !form.youtubeUrl) return;
    const videoId = ytIdFromUrl(form.youtubeUrl);
    if (!videoId || videoId.length < 5) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setYtFetching(true);
      try {
        const r = await fetch(`https://www.youtube.com/oembed?url=https://youtu.be/${videoId}&format=json`);
        if (r.ok && !cancelled) {
          const info = await r.json();
          setForm(f => ({
            ...f,
            title: f.title || (info.title ?? ""),
            thumbnailUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
          }));
        }
      } catch {}
      ytGetDuration(videoId)
        .then(dur => { if (!cancelled && dur > 0) setForm(f => ({ ...f, durationSecs: String(dur) })); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setYtFetching(false); });
    }, 700);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.youtubeUrl, source]);

  // When a MinIO URL is pasted (not uploaded — upload sets durationSecs in the same tick),
  // extract the filename as title and probe the URL for duration via a hidden <video> element.
  useEffect(() => {
    if (source !== "minio" || !form.minioUrl || form.durationSecs) return;
    // Extract a readable title from the URL path
    try {
      const pathname = new URL(form.minioUrl).pathname;
      const filename = pathname.split("/").pop() ?? "";
      const readable = decodeURIComponent(filename).replace(/\.[^.]+$/, "").replace(/[-_.]+/g, " ").trim();
      if (readable) setForm(f => ({ ...f, title: f.title || readable }));
    } catch { /* invalid URL — ignore */ }
    // Probe duration — no crossOrigin so CORS doesn't block metadata loading
    const vid = document.createElement("video");
    vid.preload = "metadata";
    vid.src = form.minioUrl;
    vid.load();
    const onMeta = () => {
      if (vid.duration && isFinite(vid.duration)) {
        setForm(f => ({ ...f, durationSecs: f.durationSecs || String(Math.round(vid.duration)) }));
      }
    };
    const onErr = () => { /* URL unreachable or format unsupported — silently skip */ };
    vid.addEventListener("loadedmetadata", onMeta, { once: true });
    vid.addEventListener("error", onErr, { once: true });
    return () => {
      vid.removeEventListener("loadedmetadata", onMeta);
      vid.removeEventListener("error", onErr);
      vid.src = "";
    };
  }, [form.minioUrl, source]);

  const ytThumb = source === "youtube" && form.youtubeUrl ? `https://img.youtube.com/vi/${ytIdFromUrl(form.youtubeUrl)}/hqdefault.jpg` : null;

  const isValid = form.title && form.moduleId && (
    source === "youtube" ? form.youtubeUrl :
    source === "cloudflare" ? form.cloudflareVideoId :
    form.minioUrl
  );

  const handleVideoFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadPct(0); setThumbPreview(null);
    // Extract local metadata and upload to MinIO in parallel
    const cleanName = file.name.replace(/\.[^.]+$/, "").replace(/[-_.]+/g, " ").trim();
    try {
      const [r, meta] = await Promise.all([
        paymentsApi.videoUploadServer(file, setUploadPct),
        getVideoMetadata(file),
      ]);
      // Single atomic form update — use functional `f` so we never read stale closure
      setForm(f => ({
        ...f,
        minioUrl: r.fileUrl,
        title: f.title || cleanName,
        durationSecs: f.durationSecs || (meta.duration ? String(meta.duration) : ""),
      }));
      if (meta.thumb) {
        setThumbPreview(meta.thumb);
        // Upload the auto-extracted frame to MinIO so the library shows a
        // real thumbnail instead of falling back to a placeholder/YouTube image.
        try {
          const thumbFile = dataURLtoFile(meta.thumb, "thumb.jpg");
          const tr = await paymentsApi.videoUploadServer(thumbFile);
          setForm(f => ({ ...f, thumbnailUrl: tr.fileUrl }));
        } catch { /* non-fatal — video upload already succeeded */ }
      }
      toast("✅ Video uploaded to MinIO!");
    } catch (err) { toast("Upload error: " + err.message); }
    finally { setUploading(false); }
  };

  const create = async () => {
    if (!isValid) { toast("Please fill in all required fields"); return; }
    setSaving(true);
    try {
      await videosApi.create({
        title: form.title, moduleId: form.moduleId, type: form.type,
        durationSecs: form.durationSecs ? Number(form.durationSecs) : undefined,
        isPreviewClip: form.isPreviewClip,
        ...(source === "youtube" ? { youtubeUrl: form.youtubeUrl } :
            source === "cloudflare" ? { cloudflareVideoId: form.cloudflareVideoId } :
            { youtubeUrl: form.minioUrl }),
        ...(form.thumbnailUrl ? { thumbnailUrl: form.thumbnailUrl } : ytThumb ? { thumbnailUrl: ytThumb } : {}),
        ...(Object.values(form.titleTranslations).some(v => v.trim()) && { titleTranslations: form.titleTranslations }),
      });
      toast("✅ Video created!"); onCreate(); onClose();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-ttl">🎬 {t("videos.addVideo")} <CloseX onClick={onClose} /></div>

        {/* Source toggle */}
        <div style={{ display: "flex", gap: 0, marginBottom: 16, border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
          {[["youtube", "🎬 YouTube"], ["cloudflare", "☁️ Cloudflare"], ["minio", "🗄️ MinIO Upload"]].map(([key, lbl]) => (
            <button key={key} onClick={() => setSource(key)}
              style={{ flex: 1, padding: "9px 0", border: "none", background: source === key ? "var(--teal)" : "var(--surface)", color: source === key ? "white" : "var(--muted)", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "var(--fb)", transition: "all .15s" }}>
              {lbl}
            </button>
          ))}
        </div>

        {source === "youtube" ? (<>
          {/* YouTube URL first — auto-fills title, duration, thumbnail */}
          <div className="f-grp">
            <label className="f-lbl" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              YouTube URL *
              {ytFetching && <span style={{ fontSize: 10, color: "var(--teal)", fontWeight: 600 }}>⏳ Fetching info…</span>}
              {!ytFetching && form.durationSecs && <span style={{ fontSize: 10, color: "var(--teal)", fontWeight: 600 }}>✅ Info loaded</span>}
            </label>
            <input className="f-in" value={form.youtubeUrl} onChange={e => set("youtubeUrl", e.target.value)}
              placeholder="https://youtu.be/… or https://www.youtube.com/watch?v=…" style={{ fontFamily: "var(--fm)", fontSize: 12 }} />
            {ytThumb && ytIdFromUrl(form.youtubeUrl) && (
              <div style={{ marginTop: 8, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)", position: "relative" }}>
                <img src={ytThumb} alt="thumbnail" style={{ width: "100%", display: "block", maxHeight: 130, objectFit: "cover" }}
                  onError={e => e.currentTarget.style.display = "none"} />
                <div style={{ position: "absolute", bottom: 6, left: 8, background: "rgba(0,0,0,.65)", color: "white", fontSize: 10, padding: "2px 8px", borderRadius: 10 }}>
                  ▶ YouTube · {ytIdFromUrl(form.youtubeUrl)}
                </div>
              </div>
            )}
          </div>
        </>) : source === "cloudflare" ? (
          <div style={{ background: "var(--teal-dim)", border: "1px solid var(--teal-lo)", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "var(--muted)" }}>
            Upload to Cloudflare Stream first, then paste the video ID here.
          </div>
        ) : (
          /* MinIO — upload file or paste URL from MinIO console */
          <div className="f-grp">
            <label className="f-lbl">Video File *</label>
            <input ref={videoFileRef} type="file" accept="video/*" style={{ display: "none" }} onChange={handleVideoFileSelect} />
            {!form.minioUrl ? (
              <>
                <div
                  className="upl-zone"
                  onClick={() => videoFileRef.current?.click()}
                  style={{ cursor: uploading ? "not-allowed" : "pointer" }}
                >
                  {uploading ? (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "var(--fd)", fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Uploading… {uploadPct}%</div>
                      <div style={{ height: 6, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${uploadPct}%`, background: "var(--teal)", borderRadius: 3, transition: "width .3s" }} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🗄️</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>Click to select video</div>
                      <div style={{ fontSize: 11, color: "var(--ghost)" }}>MP4, MOV, WebM — uploaded to MinIO storage</div>
                    </>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0" }}>
                  <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                  <span style={{ fontSize: 11, color: "var(--ghost)", whiteSpace: "nowrap" }}>or paste URL from MinIO console</span>
                  <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                </div>
                <input
                  className="f-in"
                  value={form.minioUrl}
                  onChange={e => set("minioUrl", e.target.value)}
                  placeholder="https://api.resources.kungabasics.com/videos/…"
                  style={{ fontFamily: "var(--fm)", fontSize: 12 }}
                />
              </>
            ) : (
              <div style={{ background: "var(--teal-dim)", border: "1px solid var(--teal-lo)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                {thumbPreview
                  ? <img src={thumbPreview} alt="preview" style={{ width: 72, height: 42, objectFit: "cover", borderRadius: 6, flexShrink: 0, border: "1px solid var(--border)" }} />
                  : <span style={{ fontSize: 24, flexShrink: 0 }}>🗄️</span>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)" }}>✅ MinIO video ready</div>
                  <div style={{ fontSize: 10, fontFamily: "var(--fm)", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.minioUrl}</div>
                  {form.durationSecs && <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 2 }}>⏱ {Math.floor(Number(form.durationSecs) / 60)}m {Number(form.durationSecs) % 60}s</div>}
                </div>
                <button className="btn btn-out btn-xs" onClick={() => { setForm(f => ({ ...f, minioUrl: "", durationSecs: "", title: "" })); setThumbPreview(null); if (videoFileRef.current) videoFileRef.current.value = ""; }}>✕</button>
              </div>
            )}
          </div>
        )}

        <TranslationTabs activeLang={vidLang} onChangeLang={setVidLang} />
        <div className="f-grp">
          <label className="f-lbl">{t("mods.titleLabel")} {vidLang === "en" && "*"}</label>
          <input className="f-in"
            value={vidLang === "en" ? form.title : (form.titleTranslations[vidLang] ?? "")}
            onChange={e => vidLang === "en"
              ? set("title", e.target.value)
              : setForm(f => ({ ...f, titleTranslations: { ...f.titleTranslations, [vidLang]: e.target.value } }))}
            placeholder={vidLang === "en" ? (ytFetching ? t("videos.ytFetching") : t("videos.titlePlaceholder")) : `Video title in ${vidLang.toUpperCase()}…`}
          />
        </div>

        <div className="f-row">
          <div className="f-grp">
            <label className="f-lbl">{t("videos.module")} *</label>
            <select className="f-sel" value={form.moduleId} onChange={e => set("moduleId", e.target.value)}>
              <option value="">{t("videos.selectModule")}</option>
              {groups.map(g => {
                const gMods = (g.modules ?? []);
                if (gMods.length === 0) return null;
                return (
                  <optgroup key={g.id} label={`${g.emoji ?? ""} ${g.name}`}>
                    {gMods.map(m => <option key={m.id} value={m.id}>{m.code} — {m.title}</option>)}
                  </optgroup>
                );
              })}
            </select>
          </div>
          <div className="f-grp">
            <label className="f-lbl">{t("tbl.type")} *</label>
            <select className="f-sel" value={form.type} onChange={e => set("type", e.target.value)}>
              <option value="EXPLANATION">{t("videos.typeExplanation")}</option>
              <option value="PRACTICE">{t("videos.typePractice")}</option>
              <option value="TASTER">{t("videos.typeTaster")}</option>
              <option value="DEMONSTRATION">{t("videos.typeDemonstration")}</option>
              <option value="ASSESSMENT">{t("videos.typeAssessment")}</option>
            </select>
          </div>
        </div>

        {source === "cloudflare" && (
          <div className="f-grp">
            <label className="f-lbl">Cloudflare Stream Video ID *</label>
            <input className="f-in" value={form.cloudflareVideoId} onChange={e => set("cloudflareVideoId", e.target.value)} placeholder="e.g. 5d5bc37ffcf54c9b82e996823bde9ef9" style={{ fontFamily: "var(--fm)", fontSize: 12 }} />
          </div>
        )}

        {/* Thumbnail image — optional, shown in admin lists & mobile app */}
        <div className="f-grp">
          <label className="f-lbl">Thumbnail Image (optional)</label>
          <input ref={thumbFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleThumbFileSelect} />
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
            {(form.thumbnailUrl || thumbPreview)
              ? <img src={thumbPreview || form.thumbnailUrl} alt="thumbnail" style={{ width: 72, height: 42, objectFit: "cover", borderRadius: 6, flexShrink: 0, border: "1px solid var(--border)" }} />
              : <span style={{ fontSize: 24, flexShrink: 0 }}>🖼️</span>}
            <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: "var(--ghost)" }}>
              {form.thumbnailUrl ? "Custom thumbnail set — used across admin & mobile." : "Auto-generated where possible. Upload your own to override."}
            </div>
            <button className="btn btn-out btn-xs" onClick={() => thumbFileRef.current?.click()} disabled={thumbUploading}>
              {thumbUploading ? "Uploading…" : (form.thumbnailUrl || thumbPreview) ? "Change" : "Upload"}
            </button>
            {form.thumbnailUrl && (
              <button className="btn btn-out btn-xs" onClick={() => { set("thumbnailUrl", ""); setThumbPreview(null); }}>✕</button>
            )}
          </div>
        </div>

        <div className="f-row">
          <div className="f-grp">
            <label className="f-lbl" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {t("videos.duration")} (seconds)
              {source === "minio" && form.durationSecs && <span style={{ fontSize: 10, color: "var(--teal)", fontWeight: 600 }}>✅ Auto-detected</span>}
            </label>
            <input className="f-in" type="number" value={form.durationSecs} onChange={e => set("durationSecs", e.target.value)} placeholder="e.g. 420" />
          </div>
          <div className="f-grp" style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 22 }}>
            <input type="checkbox" id="prev" checked={form.isPreviewClip} onChange={e => set("isPreviewClip", e.target.checked)} />
            <label htmlFor="prev" style={{ fontSize: 12, color: "var(--muted)", cursor: "pointer" }}>{t("videos.previewClipLabel")}</label>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" onClick={onClose} disabled={saving || uploading}>{t("common.cancel")}</button>
          <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={create} disabled={saving || uploading || !isValid}>
            {saving ? t("mods.creating") : uploading ? `${t("mods.uploading")} ${uploadPct}%` : `✅ ${t("videos.createVideo")}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditVideoModal({ vid, onClose, onSave, toast }) {
  const { t } = useLang();
  const { data: gd } = useApi(() => modulesApi.listGroups());
  const groups = gd?.groups ?? [];
  const isYT = !!vid.hlsUrl && (vid.hlsUrl.includes("youtu.be") || vid.hlsUrl.includes("youtube.com"));
  const [form, setForm] = useState({
    title: vid.title ?? "",
    moduleId: vid.moduleId ?? "",
    type: vid.type ?? "EXPLANATION",
    status: vid.status ?? "DRAFT",
    youtubeUrl: isYT ? vid.hlsUrl : "",
    durationSecs: String(vid.durationSecs ?? ""),
    isPreviewClip: vid.isPreviewClip ?? false,
    titleTranslations: vid.titleTranslations ?? { fr: "", rw: "", sw: "" },
    thumbnailUrl: vid.thumbnailUrl ?? "",
  });
  const [editVidLang, setEditVidLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const thumbFileRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Manual thumbnail image upload — used by mobile app & admin lists.
  const handleThumbFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbUploading(true);
    try {
      const tr = await paymentsApi.videoUploadServer(file);
      set("thumbnailUrl", tr.fileUrl);
      toast("✅ Thumbnail uploaded!");
    } catch (err) { toast("Thumbnail upload error: " + err.message); }
    finally { setThumbUploading(false); }
  };

  const save = async (statusOverride) => {
    const statusToSave = statusOverride ?? form.status;
    setSaving(true);
    try {
      await videosApi.update(vid.id, {
        title: form.title,
        moduleId: form.moduleId,
        type: form.type,
        status: statusToSave,
        durationSecs: form.durationSecs ? Number(form.durationSecs) : undefined,
        isPreviewClip: form.isPreviewClip,
        ...(isYT && form.youtubeUrl ? { youtubeUrl: form.youtubeUrl } : {}),
        ...(Object.values(form.titleTranslations).some(v => v.trim()) && { titleTranslations: form.titleTranslations }),
        ...(form.thumbnailUrl ? { thumbnailUrl: form.thumbnailUrl } : {}),
      });
      toast(`✅ Video ${statusToSave === "PUBLISHED" ? "published" : "updated"}!`);
      onSave();
      onClose();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSaving(false); }
  };

  const STATUS_OPTS = [
    { v: "DRAFT",     label: "Draft",     color: "var(--ghost)" },
    { v: "PUBLISHED", label: "Published", color: "var(--teal)" },
    { v: "ARCHIVED",  label: "Archived",  color: "var(--rose)" },
  ];

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-ttl">{t("videos.editVideo")} <CloseX onClick={onClose} /></div>

        {/* Status selector */}
        <div className="f-grp" style={{ marginBottom: 14 }}>
          <label className="f-lbl">Status</label>
          <div style={{ display: "flex", gap: 6 }}>
            {STATUS_OPTS.map(s => (
              <button key={s.v} onClick={() => set("status", s.v)}
                style={{ flex: 1, padding: "7px 4px", border: `1.5px solid ${form.status === s.v ? s.color : "var(--border)"}`, borderRadius: 8, background: form.status === s.v ? (s.v === "PUBLISHED" ? "var(--teal-dim)" : s.v === "ARCHIVED" ? "var(--rose-lo,#fee)" : "var(--surface)") : "var(--card)", color: form.status === s.v ? s.color : "var(--ghost)", cursor: "pointer", fontFamily: "var(--fb)", fontSize: 12, fontWeight: 700, transition: "all .15s" }}>
                {s.v === "PUBLISHED" ? "✅ " : s.v === "ARCHIVED" ? "🗑 " : "📝 "}{s.label}
              </button>
            ))}
          </div>
        </div>

        <TranslationTabs activeLang={editVidLang} onChangeLang={setEditVidLang} />
        <div className="f-grp">
          <label className="f-lbl">{t("mods.titleLabel")}</label>
          <input className="f-in"
            value={editVidLang === "en" ? form.title : (form.titleTranslations[editVidLang] ?? "")}
            onChange={e => editVidLang === "en"
              ? set("title", e.target.value)
              : setForm(f => ({ ...f, titleTranslations: { ...f.titleTranslations, [editVidLang]: e.target.value } }))}
            placeholder={editVidLang === "en" ? "" : `Video title in ${editVidLang.toUpperCase()}…`}
          />
        </div>

        <div className="f-row">
          <div className="f-grp">
            <label className="f-lbl">{t("videos.module")}</label>
            <select className="f-sel" value={form.moduleId} onChange={e => set("moduleId", e.target.value)}>
              <option value="">{t("videos.selectModuleDash")}</option>
              {groups.map(g => {
                const mods = (g.modules ?? []);
                if (!mods.length) return null;
                return (
                  <optgroup key={g.id} label={`${g.emoji ?? ""} ${g.name}`}>
                    {mods.map(m => <option key={m.id} value={m.id}>{m.code} — {m.title}</option>)}
                  </optgroup>
                );
              })}
            </select>
          </div>
          <div className="f-grp">
            <label className="f-lbl">{t("tbl.type")}</label>
            <select className="f-sel" value={form.type} onChange={e => set("type", e.target.value)}>
              <option value="EXPLANATION">{t("videos.typeExplanation")}</option>
              <option value="PRACTICE">{t("videos.typePractice")}</option>
              <option value="TASTER">{t("videos.typeTaster")}</option>
              <option value="DEMONSTRATION">{t("videos.typeDemonstration")}</option>
              <option value="ASSESSMENT">{t("videos.typeAssessment")}</option>
            </select>
          </div>
        </div>

        {isYT && (
          <div className="f-grp">
            <label className="f-lbl">YouTube URL</label>
            <input className="f-in" value={form.youtubeUrl} onChange={e => set("youtubeUrl", e.target.value)} style={{ fontFamily: "var(--fm)", fontSize: 12 }} />
          </div>
        )}

        {/* Thumbnail image — optional, shown in admin lists & mobile app */}
        <div className="f-grp">
          <label className="f-lbl">Thumbnail Image (optional)</label>
          <input ref={thumbFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleThumbFileSelect} />
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
            {form.thumbnailUrl
              ? <img src={form.thumbnailUrl} alt="thumbnail" style={{ width: 72, height: 42, objectFit: "cover", borderRadius: 6, flexShrink: 0, border: "1px solid var(--border)" }} onError={e => e.currentTarget.style.display = "none"} />
              : <span style={{ fontSize: 24, flexShrink: 0 }}>🖼️</span>}
            <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: "var(--ghost)" }}>
              {form.thumbnailUrl ? "Used across admin & mobile." : "No thumbnail set — placeholder shown in lists."}
            </div>
            <button className="btn btn-out btn-xs" onClick={() => thumbFileRef.current?.click()} disabled={thumbUploading}>
              {thumbUploading ? "Uploading…" : form.thumbnailUrl ? "Change" : "Upload"}
            </button>
            {form.thumbnailUrl && (
              <button className="btn btn-out btn-xs" onClick={() => set("thumbnailUrl", "")}>✕</button>
            )}
          </div>
        </div>

        <div className="f-row">
          <div className="f-grp">
            <label className="f-lbl">{t("videos.duration")} (seconds)</label>
            <input className="f-in" type="number" min="0" value={form.durationSecs} onChange={e => set("durationSecs", e.target.value)} />
          </div>
          <div className="f-grp" style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 22 }}>
            <input type="checkbox" id="prevE" checked={form.isPreviewClip} onChange={e => set("isPreviewClip", e.target.checked)} />
            <label htmlFor="prevE" style={{ fontSize: 12, color: "var(--muted)", cursor: "pointer" }}>{t("videos.previewClipLabel")}</label>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button className="btn btn-out" onClick={onClose} style={{ flexShrink: 0 }}>{t("common.cancel")}</button>
          {form.status !== "PUBLISHED" && (
            <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={() => save("PUBLISHED")} disabled={saving || !form.title || !form.moduleId}>
              {saving ? t("common.saving") : t("videos.publish")}
            </button>
          )}
          {form.status === "PUBLISHED" && (
            <button className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={() => save("DRAFT")} disabled={saving}>
              {saving ? t("common.saving") : t("videos.unpublish")}
            </button>
          )}
          <button className="btn" style={{ flex: 1, justifyContent: "center", background: "var(--teal)", color: "white" }} onClick={() => save()} disabled={saving || !form.title || !form.moduleId}>
            {saving ? t("common.saving") : `✅ ${t("common.save")}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PageVideos({ toast, search: externalSearch, onNavigate, hasPermission }) {
  const { t } = useLang();
  const [creating, setCreating]         = useState(false);
  const [editVid, setEditVid]           = useState(null);
  const [selVid, setSelVid]             = useState(null);
  const [typeFilter, setTypeFilter]     = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [groupByMod, setGroupByMod]     = useState(true);
  const [search, setSearch]             = useState(externalSearch ?? "");
  const [page, setPage]                 = useState(1);
  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);
  useEffect(() => { setPage(1); }, [typeFilter, moduleFilter, search]);
  const { data, loading, reload } = useApi(() => videosApi.list({ limit: 500 }));

  const allVids = data?.videos ?? [];

  // Build dynamic module list for filter dropdown
  const moduleOptions = [...new Map(
    allVids.filter(v => v.module).map(v => [v.module.id, v.module])
  ).values()].sort((a, b) => (a.code ?? "").localeCompare(b.code ?? ""));

  let vids = allVids;
  if (typeFilter) vids = vids.filter(v => v.type === typeFilter);
  if (moduleFilter) vids = vids.filter(v => v.module?.id === moduleFilter);
  if (search) {
    const q = search.toLowerCase();
    vids = vids.filter(v => v.title?.toLowerCase().includes(q) || v.module?.code?.toLowerCase().includes(q));
  }

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const fmtDur = (s) => s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—";

  const statCards = [
    { cls: "sc-teal",  val: allVids.length,                                       sub: t("mods.totalVideos"), lbl: t("videos.allVideos"), filter: "" },
    { cls: "sc-blue",  val: allVids.filter(v => v.type === "INTRO").length,       sub: t("videos.typeIntro"),    lbl: t("videos.typeIntro"),    filter: "INTRO" },
    { cls: "sc-amber", val: allVids.filter(v => v.type === "PRACTICE").length,    sub: t("videos.typePractice"), lbl: t("videos.typePractice"), filter: "PRACTICE" },
    { cls: "sc-rose",  val: allVids.filter(v => v.type === "EXPLANATION").length, sub: t("videos.typeContent"),  lbl: t("videos.typeContent"),  filter: "EXPLANATION" },
  ];

  const archiveVideo = async (id) => {
    try { await videosApi.delete(id); toast("Archived"); reload(); } catch (e) { toast(e.message); }
  };
  const publishVideo = async (id, status) => {
    try { await videosApi.update(id, { status }); toast(status === "PUBLISHED" ? "📣 Published!" : "📝 Unpublished"); reload(); }
    catch (e) { toast("Error: " + e.message); }
  };

  // Build grouped view: group videos by module, each group = card
  const grouped = groupByMod
    ? (() => {
        const map = new Map();
        for (const v of vids) {
          const key = v.module?.id ?? "__none__";
          if (!map.has(key)) map.set(key, { mod: v.module, items: [] });
          map.get(key).items.push(v);
        }
        return [...map.values()];
      })()
    : [{ mod: null, items: vids.slice((page - 1) * 20, page * 20) }];

  const VideoRow = ({ v }) => {
    const isYT = !!v.hlsUrl && (v.hlsUrl.includes("youtu.be") || v.hlsUrl.includes("youtube.com"));
    return (
      <tr key={v.id} style={{ cursor: "pointer" }} onClick={() => setSelVid(v)}>
        <td style={{ fontWeight: 600 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <VideoThumb url={v.thumbnailUrl} />
            <span>{v.title}</span>
          </div>
        </td>
        {!groupByMod && (
          <td>
            {v.module
              ? <button className="tc" style={{ background: "none", border: "none", cursor: onNavigate ? "pointer" : "default", padding: 0, fontFamily: "inherit", fontWeight: 700, fontSize: 11, color: "var(--teal)" }}
                  onClick={e => { e.stopPropagation(); onNavigate && onNavigate("moduleDetail", v.module.id); }}
                  title="Open module page">
                  {v.module.code}
                </button>
              : <span style={{ color: "var(--ghost)" }}>—</span>}
          </td>
        )}
        <td><span className={`badge bg-${v.type === "PRACTICE" ? "teal" : v.type === "EXPLANATION" ? "blue" : "gray"}`} style={{ fontSize: 9 }}>{v.type}</span></td>
        <td>
          {isYT
            ? <span style={{ color: "#c00", fontSize: 11, fontWeight: 700 }}>▶ YouTube</span>
            : v.cloudflareStreamId
            ? <span style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--fm)" }}>☁️ Cloudflare</span>
            : <span style={{ color: "var(--ghost)", fontSize: 11 }}>—</span>}
        </td>
        <td style={{ fontFamily: "var(--fm)", color: "var(--muted)" }}>{fmtDur(v.durationSecs)}</td>
        <td><Badge status={v.status} /></td>
        <td style={{ fontSize: 11, color: "var(--ghost)", fontFamily: "var(--fm)", whiteSpace: "nowrap" }}>{fmtDate(v.createdAt)}</td>
        <td onClick={e => e.stopPropagation()}>
          <div style={{ display: "flex", gap: 4 }}>
            <button className="btn btn-out btn-xs" title="Preview" onClick={() => setSelVid(v)}><Play size={10} /></button>
            {hasPermission?.('MANAGE_VIDEOS') && (
              <>
                {v.status === "PUBLISHED"
                  ? <button className="btn btn-xs btn-out btn-sm" title="Unpublish" style={{ fontSize: 9, color: "var(--ghost)" }} onClick={() => publishVideo(v.id, "DRAFT")}>📝</button>
                  : <button className="btn btn-xs btn-teal" title="Publish" style={{ fontSize: 9 }} onClick={() => publishVideo(v.id, "PUBLISHED")}>📣</button>}
                <button className="btn btn-out btn-xs" onClick={() => setEditVid(v)} title="Edit">✏️</button>
                <button className="btn btn-out btn-xs" style={{ color: "var(--rose)" }} title="Archive" onClick={() => archiveVideo(v.id)}><Trash2 size={10} /></button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const theadCols = groupByMod
    ? <tr><th>Title</th><th>Type</th><th>Source</th><th>Duration</th><th>Status</th><th>Added</th><th>Actions</th></tr>
    : <tr><th>Title</th><th>Module</th><th>Type</th><th>Source</th><th>Duration</th><th>Status</th><th>Added</th><th>Actions</th></tr>;

  return (
    <div>
      {/* Stat cards */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {statCards.map(s => (
          <div key={s.lbl} className={`stat-card ${s.cls}`}
            onClick={() => setTypeFilter(typeFilter === s.filter ? "" : s.filter)}
            style={{ cursor: "pointer", outline: typeFilter === s.filter ? "2px solid white" : "none", transition: "transform .15s", position: "relative" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}>
            <div className="sc-lbl">{s.lbl}</div>
            <div className="sc-val">{s.val}</div>
            <div className="sc-sub">{s.sub}</div>
            <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 9, color: "rgba(255,255,255,.3)", fontFamily: "var(--fm)" }}>
              {typeFilter === s.filter ? "● active" : "click to filter"}
            </div>
          </div>
        ))}
      </div>

      <div className="sec-hd">
        <div>
          <div className="sec-ttl">{t("videos.title")}</div>
          <div className="sec-sub">Cloudflare Stream · HLS + DRM</div>
        </div>
        {hasPermission?.('MANAGE_VIDEOS') && (
          <button className="btn btn-teal" onClick={() => setCreating(true)}><Plus size={14} /> {t("videos.addVideo")}</button>
        )}
      </div>

      {/* Filters */}
      <div className="filter-bar" style={{ marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div className="tb-search" style={{ maxWidth: 240, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input placeholder={t("common.search")} value={search} onChange={e => setSearch(e.target.value)} style={{ border: "none", background: "none", outline: "none", fontFamily: "var(--fb)", fontSize: 13, flex: 1, color: "var(--ink)" }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)" }}><X size={12} /></button>}
        </div>

        {/* Dynamic module filter */}
        <select
          className="f-sel"
          value={moduleFilter}
          onChange={e => setModuleFilter(e.target.value)}
          style={{ fontSize: 12, padding: "5px 10px", border: "1px solid var(--border)", borderRadius: 8, background: moduleFilter ? "var(--teal-dim)" : "var(--card)", color: moduleFilter ? "var(--teal)" : "var(--muted)", cursor: "pointer", fontFamily: "var(--fb)", fontWeight: moduleFilter ? 700 : 500 }}
        >
          <option value="">{t("mods.groups")}</option>
          {moduleOptions.map(m => (
            <option key={m.id} value={m.id}>{m.code} — {m.title}</option>
          ))}
        </select>

        {/* Type buttons */}
        {[["", t("videos.allTypes")], ["INTRO", t("videos.typeIntro")], ["PRACTICE", t("videos.typePractice")], ["EXPLANATION", t("videos.typeContent")]].map(([v, lbl]) => (
          <button key={v} className={`btn btn-sm ${typeFilter === v ? "btn-teal" : "btn-out"}`} onClick={() => setTypeFilter(v)}>{lbl}</button>
        ))}

        {/* Group / flat toggle */}
        <button
          className={`btn btn-sm ${groupByMod ? "btn-teal" : "btn-out"}`}
          onClick={() => setGroupByMod(g => !g)}
        >
          {groupByMod ? "📦 Grouped" : "☰ Flat list"}
        </button>

        {(typeFilter || moduleFilter || search) && (
          <button className="btn btn-out btn-sm" onClick={() => { setTypeFilter(""); setModuleFilter(""); setSearch(""); }}><X size={11} /> {t("users.clear")}</button>
        )}
      </div>

      {loading ? <Spinner /> : vids.length === 0 ? (
        <div className="card" style={{ padding: 24 }}><Empty icon="🎥" message={t("videos.noVideosMatch")} /></div>
      ) : groupByMod ? (
        /* ── Grouped view ── */
        grouped.map(({ mod, items }) => (
          <div key={mod?.id ?? "__none__"} className="card" style={{ marginBottom: 14 }}>
            <div className="card-hdr" style={{ alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="card-ico">🎬</div>
                <div>
                  {mod
                    ? <>
                        <button
                          style={{ background: "none", border: "none", cursor: onNavigate ? "pointer" : "default", padding: 0, fontFamily: "var(--fd)", fontWeight: 900, fontSize: 14, color: onNavigate ? "var(--teal)" : "var(--ink)", display: "flex", alignItems: "center", gap: 6 }}
                          onClick={() => onNavigate && onNavigate("moduleDetail", mod.id)}
                          title={onNavigate ? "Open module page" : undefined}
                        >
                          <span className="tc" style={{ fontSize: 11 }}>{mod.code}</span>
                          {mod.title}
                          {onNavigate && <span style={{ fontSize: 10, color: "var(--ghost)", fontWeight: 400 }}>→</span>}
                        </button>
                      </>
                    : <span style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 14, color: "var(--ink)" }}>No Module</span>}
                  <div style={{ fontSize: 11, color: "var(--ghost)", fontWeight: 400, marginTop: 2 }}>{items.length} video{items.length !== 1 ? "s" : ""}</div>
                </div>
              </div>
            </div>
            <div className="tbl-wrap">
              <table>
                <thead>{theadCols}</thead>
                <tbody>{items.map(v => <VideoRow key={v.id} v={v} />)}</tbody>
              </table>
            </div>
          </div>
        ))
      ) : (
        /* ── Flat list with pagination ── */
        <>
          <div className="card">
            <div className="card-hdr"><div className="card-ttl"><div className="card-ico">🎬</div>Videos ({vids.length})</div></div>
            <div className="tbl-wrap">
              <table>
                <thead>{theadCols}</thead>
                <tbody>{grouped[0]?.items.map(v => <VideoRow key={v.id} v={v} />)}</tbody>
              </table>
            </div>
          </div>
          <Pagination page={page} totalPages={Math.max(1, Math.ceil(vids.length / 20))} total={vids.length}
            onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
        </>
      )}

      {creating && <CreateVideoModal onClose={() => setCreating(false)} onCreate={reload} toast={toast} />}
      {editVid && <EditVideoModal vid={editVid} onClose={() => setEditVid(null)} onSave={reload} toast={toast} />}
      {selVid && (
        <VideoDetailModal
          vid={selVid}
          onClose={() => setSelVid(null)}
          onEdit={v => { setSelVid(null); setEditVid(v); }}
          onArchive={id => { setSelVid(null); archiveVideo(id); }}
          onPublish={(id, status) => { publishVideo(id, status); setSelVid(null); }}
          hasPermission={hasPermission}
        />
      )}
    </div>
  );
}

// ─── ANNOUNCEMENTS ────────────────────────────────────────────────────────────

function AnnouncementDetailModal({ ann, onClose, onViewers }) {
  const { t } = useLang();
  const seen = ann._count?.dismissals ?? ann.seenCount ?? 0;
  const rate = ann.pushSentCount > 0 ? Math.round((seen / ann.pushSentCount) * 100) : 0;
  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-ttl">{t("anns.details")} <CloseX onClick={onClose} /></div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <Badge status={ann.status} />
          <span className="badge bg-blue" style={{ fontSize: 9 }}>{(ann.targetAudience ?? "").replace(/_/g, " ")}</span>
        </div>
        <div style={{ fontFamily: "var(--fd)", fontSize: 18, fontWeight: 900, marginBottom: 8 }}>{ann.title}</div>
        <div style={{ background: "var(--surface)", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13, lineHeight: 1.6, border: "1px solid var(--border)", color: "var(--ink2)" }}>
          {ann.body ?? <span style={{ color: "var(--ghost)", fontStyle: "italic" }}>No body text</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
          {[["📣", t("anns.pushSent"), ann.pushSentCount ?? 0], ["👁", t("anns.bannerViews"), ann.bannerViewCount ?? 0], ["✅", t("anns.dismissed"), seen], ["📊", t("anns.openRate"), `${rate}%`]].map(([icon, label, val]) => (
            <div key={label} style={{ textAlign: "center", padding: "10px 6px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 16 }}>{icon}</div>
              <div style={{ fontFamily: "var(--fd)", fontSize: 16, fontWeight: 900, color: "var(--teal)" }}>{val}</div>
              <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
            </div>
          ))}
        </div>
        <InfoGrid items={[
          [t("anns.created"), new Date(ann.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })],
          [t("anns.publishedDate"), ann.publishedAt ? new Date(ann.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : t("anns.draftOnly")],
        ]} cols={2} />
        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.close")}</button>
          <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={() => { onClose(); onViewers(ann); }}>
            <Eye size={12} /> {t("anns.whoSawThis")}
          </button>
        </div>
      </div>
    </div>
  );
}

function AnnouncementViewersModal({ ann, onClose }) {
  const { t } = useLang();
  const { data, loading } = useApi(() => annApi.viewers(ann.id), [ann.id]);
  const viewers = data?.viewers ?? [];
  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-ttl">{t("anns.whoViewed")} · "{ann.title}" <CloseX onClick={onClose} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[["📣", t("anns.pushSent"), ann.pushSentCount ?? 0], ["👁", t("anns.bannerViews"), ann.bannerViewCount ?? 0], ["✅", t("anns.dismissed"), viewers.length]].map(([icon, label, val]) => (
            <div key={label} style={{ textAlign: "center", padding: 12, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 18 }}>{icon}</div>
              <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, color: "var(--teal)" }}>{val}</div>
              <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px" }}>{label}</div>
            </div>
          ))}
        </div>
        {loading ? <Spinner /> : viewers.length === 0 ? <Empty icon="👁" message={t("anns.noViewData")} /> : (
          <div className="tbl-wrap"><table>
            <thead><tr><th>{t("tbl.user")}</th><th>{t("tbl.status")}</th><th>{t("anns.viewedAt")}</th></tr></thead>
            <tbody>{viewers.map((v, i) => (
              <tr key={i}>
                <td><div style={{ fontWeight: 600, fontSize: 12 }}>{v.name}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{v.email}</div></td>
                <td><Badge status={v.subscriptionStatus} /></td>
                <td style={{ fontFamily: "var(--fm)", fontSize: 11 }}>{new Date(v.viewedAt).toLocaleDateString()}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

export function PageAnnouncements({ toast, hasPermission }) {
  const { t } = useLang();
  const { data, reload } = useApi(() => annApi.list());
  const { data: statsData } = useApi(() => annApi.stats());
  const [annPage, setAnnPage] = useState(1);
  const [annLang, setAnnLang] = useState("en");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [titleTranslations, setTitleTranslations] = useState({ fr: "", rw: "", sw: "" });
  const [bodyTranslations, setBodyTranslations] = useState({ fr: "", rw: "", sw: "" });
  const [audience, setAudience] = useState("ALL_SUBSCRIBERS");
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [pubConfirm, setPubConfirm] = useState(false);
  const [viewersAnn, setViewersAnn] = useState(null);
  const [selAnnDetail, setSelAnnDetail] = useState(null);
  const [statModal, setStatModal] = useState(null); // "sent" | "views" | "banner" | "dismissals"
  const recent = data?.announcements ?? [];
  const stats = statsData ?? {};
  const allAnns = stats.announcements ?? recent;

  const annTitleVal = annLang === "en" ? title : titleTranslations[annLang] ?? "";
  const annBodyVal  = annLang === "en" ? body  : bodyTranslations[annLang]  ?? "";
  const setAnnTitle = (v) => annLang === "en" ? setTitle(v) : setTitleTranslations(p => ({ ...p, [annLang]: v }));
  const setAnnBody  = (v) => annLang === "en" ? setBody(v)  : setBodyTranslations(p => ({ ...p, [annLang]: v }));

  const buildAnnPayload = () => ({
    title, body, targetAudience: audience,
    ...(Object.values(titleTranslations).some(v => v.trim()) && { titleTranslations }),
    ...(Object.values(bodyTranslations).some(v => v.trim()) && { bodyTranslations }),
  });

  const resetAnnForm = () => {
    setTitle(""); setBody("");
    setTitleTranslations({ fr: "", rw: "", sw: "" });
    setBodyTranslations({ fr: "", rw: "", sw: "" });
    setAnnLang("en");
  };

  const doPublish = async () => {
    setPubConfirm(false);
    setPublishing(true);
    try {
      const r = await annApi.create(buildAnnPayload());
      await annApi.publish(r.announcement.id);
      toast(t("anns.publishedToast")); resetAnnForm(); reload();
    } catch (e) { toast("Error: " + e.message); }
    finally { setPublishing(false); }
  };

  const publish = () => {
    if (!title || !body) { toast(t("anns.titleRequired")); return; }
    setPubConfirm(true);
  };

  const saveDraft = async () => {
    if (!title || !body) { toast(t("anns.titleRequired")); return; }
    setSavingDraft(true);
    try {
      await annApi.create(buildAnnPayload());
      toast(t("anns.draftSaved")); resetAnnForm(); reload();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSavingDraft(false); }
  };

  // Stat card detail modal content
  const StatDetailModal = () => {
    if (!statModal) return null;
    const configs = {
      sent: {
        title: t("anns.sentTitle"),
        desc: t("anns.sentDesc"),
        rows: allAnns.filter(a => a.status === "PUBLISHED"),
        cols: [[t("tbl.title"), "title"], [t("anns.audience"), "targetAudience"], [t("anns.pushSent"), "pushSentCount"], [t("anns.publishedDate"), "publishedAt"]],
      },
      views: {
        title: t("anns.bannerViewsTitle"),
        desc: t("anns.bannerDesc"),
        rows: allAnns.filter(a => (a.bannerViewCount ?? 0) > 0),
        cols: [[t("tbl.title"), "title"], [t("anns.bannerViews"), "bannerViewCount"], [t("anns.pushSent"), "pushSentCount"], [t("anns.publishedDate"), "publishedAt"]],
      },
      banner: {
        title: t("anns.pushDeliveredTitle"),
        desc: t("anns.pushDesc"),
        rows: allAnns.filter(a => (a.pushSentCount ?? 0) > 0),
        cols: [[t("tbl.title"), "title"], [t("anns.pushSent"), "pushSentCount"], [t("anns.openRate"), "openRate"], [t("anns.audience"), "targetAudience"]],
      },
      dismissals: {
        title: t("anns.dismissalsTitle"),
        desc: t("anns.dismissalsDesc"),
        rows: allAnns.filter(a => (a._count?.dismissals ?? a.seenCount ?? 0) > 0),
        cols: [[t("tbl.title"), "title"], [t("anns.dismissedBy"), "seenCount"], [t("anns.pushSent"), "pushSentCount"], [t("anns.openRate"), "openRate"]],
      },
    };
    const cfg = configs[statModal];
    if (!cfg) return null;

    const getVal = (row, key) => {
      if (key === "openRate") {
        const seen = row.seenCount ?? row._count?.dismissals ?? 0;
        const sent = row.pushSentCount ?? 0;
        return sent > 0 ? `${Math.round((seen / sent) * 100)}%` : "0%";
      }
      if (key === "seenCount") return (row.seenCount ?? row._count?.dismissals ?? 0).toLocaleString();
      if (key === "publishedAt") return row.publishedAt ? new Date(row.publishedAt).toLocaleDateString() : "—";
      if (key === "pushSentCount" || key === "bannerViewCount") return (row[key] ?? 0).toLocaleString();
      return row[key] ?? "—";
    };

    return (
      <div className="modal-ov open">
        <div className="modal" style={{ maxWidth: 580 }}>
          <div className="modal-ttl">{cfg.title} <CloseX onClick={() => setStatModal(null)} /></div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14, padding: "8px 12px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
            {cfg.desc}
          </div>
          {cfg.rows.length === 0
            ? <Empty icon="📭" message={t("ui.noData")} />
            : (
              <div className="tbl-wrap" style={{ maxHeight: 380, overflowY: "auto" }}>
                <table>
                  <thead><tr>{cfg.cols.map(([lbl]) => <th key={lbl}>{lbl}</th>)}</tr></thead>
                  <tbody>
                    {cfg.rows.map((row, i) => (
                      <tr key={i} style={{ cursor: "pointer" }} onClick={() => { setStatModal(null); setViewersAnn(row); }}>
                        {cfg.cols.map(([lbl, key]) => (
                          <td key={lbl} style={key === "title" ? { fontWeight: 600, maxWidth: 200 } : { fontFamily: "var(--fm)", fontSize: 12 }}>
                            {key === "title"
                              ? <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{row.title}</div>
                              : key === "targetAudience"
                                ? <span className="badge bg-teal" style={{ fontSize: 9 }}>{row.targetAudience}</span>
                                : getVal(row, key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          <div style={{ paddingTop: 10, fontSize: 11, color: "var(--ghost)", textAlign: "center" }}>
            {t("anns.clickToViewWhoSaw")}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Clickable Stats */}
      <div className="stats-grid" style={{ marginBottom: 18 }}>
        {[
          { cls: "sc-teal", key: "sent", lbl: t("anns.totalSent"), val: recent.length, sub: t("anns.subAnnouncements"), icon: "📣" },
          { cls: "sc-amber", key: "banner", lbl: t("anns.pushDelivered"), val: (stats.totalPushSent ?? 0).toLocaleString(), sub: t("anns.subPushNotifs"), icon: "🔔" },
          { cls: "sc-blue", key: "views", lbl: t("anns.bannerViews"), val: (stats.totalBannerViews ?? 0).toLocaleString(), sub: t("anns.subBannerViews"), icon: "👁" },
          { cls: "sc-rose", key: "dismissals", lbl: t("anns.dismissals"), val: (stats.totalDismissals ?? 0).toLocaleString(), sub: t("anns.subConfirmedViews"), icon: "✅" },
        ].map(s => (
          <div
            key={s.key}
            className={`stat-card ${s.cls}`}
            onClick={() => setStatModal(s.key)}
            style={{ cursor: "pointer", transition: "transform .15s, box-shadow .15s", position: "relative" }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(4,18,14,.12)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
          >
            <div className="sc-lbl">{s.lbl}</div>
            <div className="sc-val">{s.val}</div>
            <div className="sc-sub">{s.sub}</div>
            <div style={{ position: "absolute", bottom: 10, right: 12, fontSize: 10, color: "rgba(255,255,255,.3)", fontFamily: "var(--fm)" }}>{t("ui.clickToView")}</div>
          </div>
        ))}
      </div>

      <div className="side-panel">
        <div className="side-main">
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-hdr"><div className="card-ttl"><div className="card-ico">📣</div>{t("anns.title")}</div></div>
            <div className="card-body">
              <TranslationTabs activeLang={annLang} onChangeLang={setAnnLang} />
              <div className="f-grp"><label className="f-lbl">{t("anns.announcementTitle")}</label><input data-testid="announcement-title-input" className="f-in" value={annTitleVal} onChange={e => setAnnTitle(e.target.value)} maxLength={80} placeholder={t("anns.titleInputPh")} /></div>
              <div className="f-grp"><label className="f-lbl">{t("anns.message")}</label><textarea data-testid="announcement-body-input" className="f-ta" value={annBodyVal} onChange={e => setAnnBody(e.target.value)} rows={4} maxLength={300} placeholder={t("anns.messageInputPh")} /></div>
              <div className="f-grp"><label className="f-lbl">{t("anns.targetAudience")}</label>
                <select className="f-sel" value={audience} onChange={e => setAudience(e.target.value)}>
                  <option value="ALL_SUBSCRIBERS">{t("anns.targetAllSubscribers")}</option>
                  <option value="ACTIVE_ONLY">{t("anns.targetActiveOnly")}</option>
                  <option value="SPEECH_MODULE_USERS">{t("anns.targetSpeechModule")}</option>
                  <option value="ALL_REGISTERED">{t("anns.targetAllRegistered")}</option>
                </select>
              </div>
              {hasPermission?.('MANAGE_ANNOUNCEMENTS') && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button data-testid="save-draft-button" className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={saveDraft} disabled={savingDraft || publishing || !title || !body}>
                    {savingDraft ? t("common.saving") : t("anns.saveDraft")}
                  </button>
                  <button data-testid="publish-now-button" className="btn btn-teal" style={{ flex: 2, justifyContent: "center" }} onClick={publish} disabled={publishing || savingDraft || !title || !body}>
                    {publishing ? t("common.updating") : t("anns.publishNow")}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="card" style={{ marginTop: 18 }}>
            <div className="card-hdr"><div className="card-ttl"><div className="card-ico">📊</div>{t("anns.statistics")}</div></div>
            <div className="tbl-wrap"><table>
              <thead><tr><th>{t("tbl.name")}</th><th>{t("tbl.status")}</th><th>{t("anns.totalSent")}</th><th>{t("anns.bannerViews")}</th><th>{t("anns.openRate")}</th><th>{t("anns.viewersHeader")}</th></tr></thead>
              <tbody>
                {recent.length === 0 ? (<tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "var(--ghost)" }}>{t("anns.noAnnouncements")}</td></tr>)
                  : recent.slice((annPage - 1) * 10, annPage * 10).map(a => {
                    const seen = a._count?.dismissals ?? a.seenCount ?? 0;
                    const rate = a.pushSentCount > 0 ? Math.round((seen / a.pushSentCount) * 100) : 0;
                    return (<tr data-testid="announcement-row" key={a.id} style={{ cursor: "pointer" }} onClick={() => setSelAnnDetail(a)}>
                      <td style={{ fontWeight: 600, maxWidth: 180 }}><div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</div><div style={{ fontSize: 9, color: "var(--ghost)" }}>{new Date(a.createdAt).toLocaleDateString()}</div></td>
                      <td><Badge status={a.status} /></td>
                      <td style={{ fontFamily: "var(--fm)", fontWeight: 700 }}>{(a.pushSentCount ?? 0).toLocaleString()}</td>
                      <td style={{ fontFamily: "var(--fm)" }}>{(a.bannerViewCount ?? 0).toLocaleString()}</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1, height: 4, background: "var(--border)", borderRadius: 2, minWidth: 40 }}>
                            <div style={{ width: `${rate}%`, height: "100%", background: rate > 50 ? "var(--teal)" : "var(--amber)", borderRadius: 2 }} />
                          </div>
                          <span style={{ fontFamily: "var(--fm)", fontSize: 11, fontWeight: 700 }}>{rate}%</span>
                        </div>
                      </td>
                      <td onClick={e => { e.stopPropagation(); setViewersAnn(a); }}><button className="btn btn-out btn-xs"><Eye size={10} /> {seen}</button></td>
                    </tr>);
                  })}
              </tbody>
            </table></div>
            {recent.length > 0 && (
              <Pagination page={annPage} totalPages={Math.max(1, Math.ceil(recent.length / 10))} total={recent.length}
                onPrev={() => setAnnPage(p => p - 1)} onNext={() => setAnnPage(p => p + 1)} />
            )}
          </div>
        </div>
        <div className="side-aside">
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-hdr"><div className="card-ttl">{t("anns.livePreview")}</div></div>
            <div className="card-body">
              <div className="ann-push">
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <div style={{ width: 26, height: 26, background: "var(--teal)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>🌿</div>
                  <div><div style={{ fontSize: 11, fontWeight: 700, color: "white" }}>Kunga Basics</div><div style={{ fontSize: 10, color: "rgba(255,255,255,.35)" }}>{t("anns.previewNow")}</div></div>
                </div>
                <div className="push-ttl">{title || t("anns.previewTitlePh")}</div>
                <div className="push-body">{body || t("anns.previewBodyPh")}</div>
              </div>
              <div className="banner-prev">
                <div style={{ fontSize: 9, fontWeight: 700, color: "var(--teal)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 2 }}>{t("anns.inAppBanner")}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)" }}>{title || t("anns.bannerTitlePh")}</div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>{body || t("anns.bannerBodyPh")}</div>
              </div>
              <div style={{ padding: "8px 10px", background: "var(--surface)", borderRadius: 8, marginTop: 8 }}>
                <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 2 }}>{t("anns.target")}</div>
                <div style={{ fontFamily: "var(--fd)", fontSize: 13, fontWeight: 900, color: "var(--teal)" }}>{audience.replace(/_/g, " ")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StatDetailModal />
      {pubConfirm && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-ttl">{t("anns.confirmPublish")}</div>
            <div style={{ background: "var(--amber-lo)", border: "1px solid var(--amber)", borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13, color: "var(--ink2)", lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>{body}</div>
              <div style={{ marginTop: 8, fontSize: 11, color: "var(--ghost)" }}>{t("anns.confirmTarget", { target: audience.replace(/_/g, " ") })}</div>
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>
              {t("anns.publishWarning")}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-out" style={{ flex: 1 }} onClick={() => setPubConfirm(false)}>{t("common.cancel")}</button>
              <button data-testid="confirm-publish-button" className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={doPublish}>{t("anns.yesPublish")}</button>
            </div>
          </div>
        </div>
      )}
      {selAnnDetail && <AnnouncementDetailModal ann={selAnnDetail} onClose={() => setSelAnnDetail(null)} onViewers={a => setViewersAnn(a)} />}
      {viewersAnn && <AnnouncementViewersModal ann={viewersAnn} onClose={() => setViewersAnn(null)} />}
    </div>
  );
}

// ─── DONATIONS ────────────────────────────────────────────────────────────────

function DonationModal({ d, onClose }) {
  const { t } = useLang();
  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-ttl">{t("donations.details")} <CloseX onClick={onClose} /></div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, background: "var(--panel-dark)", borderRadius: 12, marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--teal)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>💝</div>
          <div><div style={{ fontFamily: "var(--fd)", fontSize: 22, fontWeight: 900, color: "var(--teal)" }}>${d.amountUsd}</div><div style={{ fontSize: 12, color: "rgba(255,255,255,.5)" }}>{d.donorName ?? "Anonymous"} · {d.paymentMethod}</div></div>
          <Badge status={d.status} />
        </div>
        <InfoGrid items={[[t("tbl.donor"), d.donorName ?? "Anonymous"], [t("donations.email"), d.email], [t("tbl.amount"), `$${d.amountUsd} ${d.currency}`], [t("tbl.method"), d.paymentMethod], [t("tbl.campaign"), d.campaign ?? "—"], [t("tbl.status"), d.status], [t("tbl.date"), new Date(d.createdAt).toLocaleDateString()], [t("donations.donorWall"), d.showOnDonorWall ? t("donations.visible") : t("donations.private")]]} />
      </div>
    </div>
  );
}

function ScholarshipModal({ s, onClose }) {
  const { t } = useLang();
  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-ttl">{t("donations.scholarshipDetails")} <CloseX onClick={onClose} /></div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, background: "linear-gradient(135deg,var(--amber-lo),#fff)", borderRadius: 12, marginBottom: 16, border: "1px solid var(--amber)" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--amber-lo)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>🎓</div>
          <div><div style={{ fontFamily: "var(--fd)", fontSize: 15, fontWeight: 900, color: "var(--ink)" }}>{s.email}</div><div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{t("donations.scholarshipGranted")}</div></div>
          <span className="badge bg-green">{t("donations.activeLabel")}</span>
        </div>
        <InfoGrid items={[[t("donations.grantedOn"), new Date(s.grantedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })], [t("donations.notes"), s.notes ?? "—"], [t("donations.userId"), s.userId ?? "Guest"], [t("donations.activeLabel"), s.active ? "Yes" : "No"]]} />
      </div>
    </div>
  );
}

export function PageDonations({ toast }) {
  const { t } = useLang();
  const { data: statsData } = useApi(() => donationsApi.stats());
  const [donFilter, setDonFilter] = useState("");
  const [donPage, setDonPage] = useState(1);
  const [schPage, setSchPage] = useState(1);
  useEffect(() => { setDonPage(1); }, [donFilter]);
  const { data: listData, loading } = useApi(() => donationsApi.list({ limit: 20, page: donPage, ...(donFilter && { status: donFilter }) }), [donPage, donFilter]);
  const { data: schData, reload: reSch } = useApi(() => donationsApi.scholarships());
  const [grantEmail, setGrantEmail] = useState("");
  const [granting, setGranting] = useState(false);
  const [selDon, setSelDon] = useState(null);
  const [selGrant, setSelGrant] = useState(null);
  const [schModal, setSchModal] = useState(false);
  const [methodModal, setMethodModal] = useState(false);
  const [grantConfirm, setGrantConfirm] = useState(false);

  const stats = statsData ?? {};
  const allDonations = listData?.donations ?? [];
  const donations = allDonations;
  const scholarships = Array.isArray(schData) ? schData : (schData?.grants ?? []);

  const grant = async () => { setGranting(true); try { await donationsApi.grantScholarship({ email: grantEmail }); toast("✅ Scholarship granted!"); setGrantEmail(""); reSch(); } catch (e) { toast("Error: " + e.message); } finally { setGranting(false); } };
  const exportCSV = () => { const rows = ["Donor,Amount,Method,Campaign,Date,Status", ...allDonations.map(d => `"${d.donorName ?? "Anonymous"}","$${d.amountUsd}","${d.paymentMethod}","${d.campaign ?? ""}","${new Date(d.createdAt).toLocaleDateString()}","${d.status}"`)].join("\n"); const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([rows], { type: "text/csv" })); a.download = "donations.csv"; a.click(); toast("✅ CSV downloaded!"); };

  return (
    <div>
      {/* Clickable stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,150px),1fr))", gap: 12, marginBottom: 16 }}>
        {[
          { val: `$${((stats.totalUsd ?? 0) / 1000).toFixed(1)}k`, lbl: t("donations.totalReceived"), color: "var(--teal)", action: () => setDonFilter("") },
          { val: stats.activeScholarships ?? 0, lbl: t("donations.scholarships"), color: "var(--amber)", action: () => setSchModal(true) },
          { val: stats.pendingCount ?? 0, lbl: t("donations.pending"), color: "var(--rose)", action: () => setDonFilter("PENDING") },
          { val: (stats.byMethod ?? []).length, lbl: t("donations.methods"), color: "var(--blue)", action: () => setMethodModal(true) },
        ].map(s => (
          <div key={s.lbl} onClick={s.action}
            style={{ textAlign: "center", padding: 14, background: "var(--surface)", borderRadius: "var(--r)", border: `1px solid ${s.color}33`, cursor: "pointer", transition: "transform .15s,box-shadow .15s", position: "relative" }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,.08)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
            <div style={{ fontFamily: "var(--fd)", fontSize: 21, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", marginTop: 2 }}>{s.lbl}</div>
            <div style={{ fontSize: 8, color: "rgba(0,0,0,.25)", position: "absolute", bottom: 6, right: 8, fontFamily: "var(--fm)" }}>click →</div>
          </div>
        ))}
      </div>
      <div className="cg2">
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">💝</div>{t("donations.donationsCardTitle", { n: donations.length })}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <select className="f-sel" style={{ width: "auto", fontSize: 12 }} value={donFilter} onChange={e => setDonFilter(e.target.value)}>
                <option value="">{t("donations.filterAll")}</option><option value="COMPLETED">{t("donations.filterCompleted")}</option><option value="PENDING">{t("donations.filterPending")}</option><option value="FAILED">{t("donations.filterFailed")}</option>
              </select>
              <button className="btn btn-out btn-sm" onClick={() => { exportDonationsExcel(allDonations); toast("✅ Excel downloaded!") }}><FileSpreadsheet size={11} />XLS</button>
              <button className="btn btn-out btn-sm" onClick={() => { exportDonationsCSV(allDonations); toast("✅ CSV downloaded!") }}><Download size={11} />CSV</button>
              <button className="btn btn-out btn-sm" onClick={() => { exportDonationsPDF(allDonations, { filter: donFilter }); toast("✅ PDF downloaded!") }}><FileText size={11} />PDF</button>
            </div>
          </div>
          {loading ? <Spinner /> : donations.length === 0 ? <div style={{ padding: 24 }}><Empty icon="💝" message={t("donations.noYet")} /></div> : (
            <div className="tbl-wrap"><table>
              <thead><tr><th>{t("tbl.donor")}</th><th>{t("tbl.amount")}</th><th>{t("tbl.method")}</th><th>{t("tbl.campaign")}</th><th>{t("tbl.date")}</th><th>{t("tbl.status")}</th></tr></thead>
              <tbody>{donations.map((d, i) => (
                <tr key={i} style={{ cursor: "pointer" }} onClick={() => setSelDon(d)}>
                  <td style={{ fontWeight: 600 }}>{d.donorName ?? "Anonymous"}<div style={{ fontSize: 10, color: "var(--ghost)" }}>{d.email}</div></td>
                  <td style={{ fontFamily: "var(--fm)", fontWeight: 800, color: "var(--teal)" }}>${d.amountUsd}</td>
                  <td><span className="badge bg-blue">{d.paymentMethod}</span></td>
                  <td style={{ fontSize: 12, color: "var(--muted)" }}>{d.campaign ?? "—"}</td>
                  <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{new Date(d.createdAt).toLocaleDateString()}</td>
                  <td><Badge status={d.status} /></td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
          {!loading && (listData?.total ?? 0) > 0 && (
            <Pagination page={donPage} totalPages={Math.max(1, Math.ceil((listData?.total ?? 0) / 20))} total={listData?.total ?? 0}
              onPrev={() => setDonPage(p => p - 1)} onNext={() => setDonPage(p => p + 1)} />
          )}
        </div>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr"><div className="card-ttl"><div className="card-ico">🎓</div>{t("donations.scholarshipsTitle", { n: scholarships.length })}</div></div>
          <div className="card-body">
            {scholarships.length === 0 ? <Empty icon="🎓" message={t("donations.noScholarshipsYet")} /> : scholarships.slice((schPage - 1) * 10, schPage * 10).map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--border)", cursor: "pointer" }} onClick={() => setSelGrant(s)}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--amber-lo)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🎓</div>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 12, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.email}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{new Date(s.grantedAt).toLocaleDateString()}</div></div>
                <span className="badge bg-green">{t("donations.activeBadge")}</span>
              </div>
            ))}
            {scholarships.length > 0 && (
              <Pagination page={schPage} totalPages={Math.max(1, Math.ceil(scholarships.length / 10))} total={scholarships.length}
                onPrev={() => setSchPage(p => p - 1)} onNext={() => setSchPage(p => p + 1)} />
            )}
            <div style={{ paddingTop: 14 }}>
              <label className="f-lbl">{t("donations.grantByEmail")}</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="f-in" value={grantEmail} onChange={e => setGrantEmail(e.target.value)} placeholder="email@example.com" style={{ flex: 1 }} />
                <button className="btn btn-teal" onClick={() => { if (!grantEmail) { toast("Email required"); return; } setGrantConfirm(true); }} disabled={granting}>{granting ? "…" : t("donations.grant")}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selDon && <DonationModal d={selDon} onClose={() => setSelDon(null)} />}
      {selGrant && <ScholarshipModal s={selGrant} onClose={() => setSelGrant(null)} />}

      {/* Scholarship methods modal */}
      {schModal && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-ttl">🎓 {t("donations.activeScholarships")} <CloseX onClick={() => setSchModal(false)} /></div>
            <div style={{ marginBottom: 12, padding: "10px 12px", background: "var(--amber-lo)", borderRadius: 8, border: "1px solid var(--amber)", fontSize: 12, color: "var(--muted)" }}>
              {t("donations.scholarshipsCount", { n: scholarships.length })}
            </div>
            {scholarships.length === 0 ? <Empty icon="🎓" message={t("donations.noScholarshipsYet")} /> : (
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                {scholarships.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--border)", cursor: "pointer" }} onClick={() => { setSchModal(false); setSelGrant(s); }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--amber-lo)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🎓</div>
                    <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 12, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.email}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{t("donations.grantedLabel", { date: new Date(s.grantedAt).toLocaleDateString() })}</div></div>
                    <div style={{ fontSize: 11, color: "var(--muted)", flexShrink: 0 }}>{s.notes?.slice(0, 30) ?? ""}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment methods modal */}
      {methodModal && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-ttl">{t("donations.donationsByMethod")} <CloseX onClick={() => setMethodModal(false)} /></div>
            {(stats.byMethod ?? []).length === 0 ? <Empty icon="💳" message={t("donations.noData")} /> : (
              <div>
                {(stats.byMethod ?? []).map((m, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <span className="badge bg-blue">{m.paymentMethod}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: "var(--muted)" }}>{t("donations.donationsCount", { n: m._count ?? 0 })}</span>
                        <span style={{ fontFamily: "var(--fm)", fontSize: 12, fontWeight: 700, color: "var(--teal)" }}>${(m._sum?.amountUsd ?? 0).toFixed(0)}</span>
                      </div>
                      <div className="prog-track"><div className="prog-fill" style={{ width: `${Math.round(((m._sum?.amountUsd ?? 0) / (stats.totalUsd || 1)) * 100)}%` }} /></div>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 14, padding: "10px 12px", background: "var(--teal-dim)", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)" }}>Total Received</span>
                  <span style={{ fontFamily: "var(--fd)", fontSize: 18, fontWeight: 900, color: "var(--teal)" }}>${(stats.totalUsd ?? 0).toFixed(0)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Grant confirmation */}
      {grantConfirm && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-ttl">🎓 Confirm Scholarship Grant</div>
            <div style={{ padding: "12px 14px", background: "var(--amber-lo)", borderRadius: 8, border: "1px solid var(--amber)", marginBottom: 16, fontSize: 13 }}>
              Grant <strong>free full access</strong> to:<br />
              <span style={{ fontFamily: "var(--fm)", color: "var(--teal)", fontWeight: 700 }}>{grantEmail}</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>This will give the user unlimited access to all modules and videos without requiring a paid subscription.</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-out" style={{ flex: 1 }} onClick={() => setGrantConfirm(false)}>Cancel</button>
              <button className="btn btn-teal" style={{ flex: 1 }} onClick={async () => { setGrantConfirm(false); await grant(); }}>✅ Confirm Grant</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MOBILE MONEY ─────────────────────────────────────────────────────────────

const PROVIDER_LABELS = { mpesa: "M-Pesa (Safaricom)", mtn_momo: "MTN MoMo", airtel_money: "Airtel Money", vodacom_mpesa: "Vodacom M-Pesa" };

// Brand-coloured provider icons rendered as inline SVG shapes
function ProviderIcon({ provider, size = 32 }) {
  const s = size;
  if (provider === "mpesa") return (
    <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#C8102E"/>
      <text x="16" y="21" textAnchor="middle" fontFamily="Arial Black,sans-serif" fontWeight="900" fontSize="8" fill="white">M-PESA</text>
    </svg>
  );
  if (provider === "mtn_momo") return (
    <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#FFCC00"/>
      <text x="16" y="16" textAnchor="middle" dominantBaseline="central" fontFamily="Arial Black,sans-serif" fontWeight="900" fontSize="10" fill="#004A99">MTN</text>
    </svg>
  );
  if (provider === "airtel_money") return (
    <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#E8001D"/>
      <text x="16" y="13" textAnchor="middle" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="7" fill="white">AIRTEL</text>
      <text x="16" y="23" textAnchor="middle" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="7" fill="white">MONEY</text>
    </svg>
  );
  if (provider === "vodacom_mpesa") return (
    <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#E60000"/>
      <text x="16" y="13" textAnchor="middle" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="7" fill="white">VODA</text>
      <text x="16" y="23" textAnchor="middle" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="7" fill="white">M-PESA</text>
    </svg>
  );
  return <span style={{ fontSize: size * 0.7, lineHeight: 1 }}>🌍</span>;
}

function MMDetailModal({ tx, onClose, onActivate, hasPermission }) {
  const { t } = useLang();
  const [activating, setActivating] = useState(false);
  const [comment, setComment]       = useState("");
  const [busy, setBusy]             = useState(false);

  const fmtDate     = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const InfoRow = ({ label, value, mono }) => (
    <div style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 14px", border: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 700, flexShrink: 0, paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, fontFamily: mono ? "var(--fm)" : "var(--fb)", color: "var(--ink)", textAlign: "right", wordBreak: "break-all" }}>{value ?? "—"}</div>
    </div>
  );

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 10, fontWeight: 800, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: "1px", marginTop: 6, marginBottom: 4, paddingLeft: 2 }}>{children}</div>
  );

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="modal-ttl">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>🌊</span>
            {t("mm.transactionDetails")}
          </div>
          <CloseX onClick={onClose} />
        </div>

        {/* User card */}
        <div style={{ display: "flex", gap: 14, alignItems: "center", padding: 16, background: "var(--panel-dark)", borderRadius: 12, marginBottom: 16 }}>
          {tx.user?.avatarUrl
            ? <img src={tx.user.avatarUrl} alt="" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
            : <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><ProviderIcon provider={tx.mobileMoneyProvider} size={32} /></div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--fd)", fontSize: 15, fontWeight: 900, color: "white", marginBottom: 2 }}>{tx.user?.name ?? "Unknown"}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>{tx.user?.email ?? "—"}</div>
            {tx.user?.childProfile && (
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", marginTop: 3 }}>
                👶 {tx.user.childProfile.childName}{tx.user.childProfile.ageMonths ? ` · ${tx.user.childProfile.ageMonths} months` : ""}
              </div>
            )}
          </div>
          <Badge status={tx.status} />
        </div>

        {/* Subscription */}
        <SectionLabel>Subscription</SectionLabel>
        <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Plan"     value={PLAN_LABEL(tx.plan)} />
          <InfoRow label="Status"   value={tx.status} />
          <InfoRow label="Platform" value={PLT_LABELS_CP[tx.platform] ?? tx.platform} />
          {tx.amount != null && <InfoRow label="Amount Charged" value={`${tx.currency ?? "USD"} ${Number(tx.amount).toFixed(2)}`} />}
          {tx.promoCode && <InfoRow label="Promo Code" value={tx.promoCode} mono />}
          {(tx.questionAddonUsd > 0) && <InfoRow label="Question Add-on" value={`+$${tx.questionAddonUsd}/renewal`} />}
        </div>

        {/* Mobile Money */}
        <SectionLabel>Mobile Money</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Provider" value={PROVIDER_LABELS[tx.mobileMoneyProvider] ?? tx.mobileMoneyProvider} />
          <InfoRow label="Phone"    value={tx.mobileMoneyPhone} mono />
        </div>

        {/* Billing period */}
        <SectionLabel>Billing Period</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Period Start" value={fmtDate(tx.periodStart)} />
          <InfoRow label="Period End"   value={fmtDate(tx.periodEnd)} />
          <InfoRow label="Created"      value={fmtDate(tx.createdAt)} />
          {tx.cancelledAt && <InfoRow label="Cancelled At" value={fmtDateTime(tx.cancelledAt)} />}
        </div>

        {/* Card info (tokenized) */}
        {(tx.cardLast4 || tx.cardBrand) && (
          <>
            <SectionLabel>Card Used</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              {tx.cardBrand && <InfoRow label="Card Brand"    value={tx.cardBrand} />}
              {tx.cardLast4 && <InfoRow label="Last 4 Digits" value={`•••• ${tx.cardLast4}`} mono />}
            </div>
          </>
        )}

        {/* Transaction reference */}
        <SectionLabel>Transaction Reference</SectionLabel>
        <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
          {(() => { const { label, value } = getTxRef(tx); return <InfoRow label={label} value={value} mono />; })()}
          <InfoRow label="Record ID"          value={tx.id} mono />
        </div>

        {/* Auto-renewal */}
        {(tx.autoRenewAttemptedAt || tx.autoRenewFailedAt) && (
          <>
            <SectionLabel>Auto-Renewal</SectionLabel>
            <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
              {tx.autoRenewAttemptedAt && <InfoRow label="Last Attempt" value={fmtDateTime(tx.autoRenewAttemptedAt)} />}
              {tx.autoRenewFailedAt    && <InfoRow label="Last Failure"  value={fmtDateTime(tx.autoRenewFailedAt)} />}
            </div>
          </>
        )}

        {(tx.status === "NONE" || tx.status === "CANCELLED") && hasPermission?.('MANAGE_SUBSCRIPTIONS') && (
          activating ? (
            <div style={{ marginTop: 12, padding: "14px 14px 12px", background: "var(--amber-lo)", borderRadius: 10, border: "1.5px solid var(--amber)" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#8a5c00", marginBottom: 8 }}>⚡ Confirm Manual Activation</div>
              <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 5, color: "#8a5c00" }}>Comment *</label>
              <textarea
                rows={3}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="e.g. User confirmed payment via phone — activating manually"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1.5px solid var(--amber)", fontSize: 13, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", background: "var(--surface)" }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button className="btn btn-out btn-sm" style={{ flex: 1 }} onClick={() => { setActivating(false); setComment(""); }} disabled={busy}>Cancel</button>
                <button className="btn btn-sm"
                  style={{ flex: 1, background: comment.trim() ? "var(--amber)" : "var(--border)", border: "1.5px solid var(--amber)", color: "#8a5c00", opacity: busy || !comment.trim() ? .6 : 1, cursor: !comment.trim() ? "not-allowed" : "pointer" }}
                  onClick={async () => { setBusy(true); try { await onActivate(tx.id, comment); onClose(); } catch {} finally { setBusy(false); } }}
                  disabled={busy || !comment.trim()}
                >
                  {busy ? "Activating…" : "⚡ Confirm Activation"}
                </button>
              </div>
            </div>
          ) : (
            <button className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 8, background: "var(--amber-lo)", border: "1.5px solid var(--amber)", color: "#8a5c00" }} onClick={() => setActivating(true)}>{t("mm.manualActivate")}</button>
          )
        )}
      </div>
    </div>
  );
}

export function PageMobileMoney({ toast, hasPermission }) {
  const { t } = useLang();
  const [provider, setProvider] = useState(""); const [status, setStatus] = useState(""); const [plan, setPlan] = useState(""); const [search, setSearch] = useState(""); const [selTx, setSelTx] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [provider, status, plan]);
  const params = { ...(provider && { provider }), ...(status && { status }), ...(plan && { plan }), page, limit: 20 };
  const { data, loading, reload } = useApi(() => paymentsApi.mobileMoneyList(params), [provider, status, plan, page]);
  let txs = data?.transactions ?? [];
  if (search) { const q = search.toLowerCase(); txs = txs.filter(tx => tx.user?.name?.toLowerCase().includes(q) || tx.user?.email?.toLowerCase().includes(q) || tx.mobileMoneyPhone?.includes(q) || tx.flutterwaveTxId?.toLowerCase().includes(q)); }

  const activate = async (id, comment) => { try { await paymentsApi.manualActivate(id, { comment }); toast("⚡ Activated!"); reload(); } catch (e) { toast("Error: " + e.message); } };
  const reset = () => { setProvider(""); setStatus(""); setPlan(""); setSearch(""); };
  const byProvider = ["mpesa", "mtn_momo", "airtel_money", "vodacom_mpesa"].map(p => ({ p, count: txs.filter(tx => tx.mobileMoneyProvider === p).length }));

  return (
    <div>
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { cls: "sc-blue", lbl: t("mm.total"), val: txs.length, filter: "", sub: "all providers" },
          { cls: "sc-teal", lbl: t("mm.active"), val: txs.filter(tx => tx.status === "ACTIVE").length, filter: "ACTIVE", sub: "live subscriptions" },
          { cls: "sc-amber", lbl: t("mm.pending"), val: txs.filter(tx => tx.status === "NONE").length, filter: "NONE", sub: "need activation" },
          { cls: "sc-rose", lbl: t("mm.failed"), val: txs.filter(tx => tx.status === "CANCELLED").length, filter: "CANCELLED", sub: "cancelled" },
        ].map(s => (
          <div key={s.lbl} className={`stat-card ${s.cls}`}
            onClick={() => setStatus(status === s.filter ? "" : s.filter)}
            style={{ cursor: "pointer", outline: status === s.filter ? "2px solid white" : "none", transition: "transform .15s", position: "relative" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}>
            <div className="sc-lbl">{s.lbl}</div>
            <div className="sc-val">{s.val}</div>
            <div className="sc-sub">{s.sub}</div>
            <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 9, color: "rgba(255,255,255,.3)", fontFamily: "var(--fm)" }}>{status === s.filter ? t("ui.active") : t("ui.clickToFilter")}</div>
          </div>
        ))}
      </div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-hdr"><div className="card-ttl"><div className="card-ico">📊</div>{t("mm.providerBreakdown")}</div></div>
        <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10 }}>
          {byProvider.map(({ p, count }) => (
            <div key={p} style={{ textAlign: "center", padding: 12, background: provider === p ? "var(--teal-dim)" : "var(--surface)", borderRadius: 8, border: `1px solid ${provider === p ? "var(--teal)" : "var(--border)"}`, cursor: "pointer" }} onClick={() => setProvider(provider === p ? "" : p)}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 2 }}><ProviderIcon provider={p} size={28} /></div>
              <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, color: provider === p ? "var(--teal)" : "var(--ink)" }}>{count}</div>
              <div style={{ fontSize: 9, color: "var(--ghost)", marginTop: 2 }}>{PROVIDER_LABELS[p]}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 240, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input placeholder="Name, phone, tx ref…" value={search} onChange={e => setSearch(e.target.value)} style={{ border: "none", background: "none", outline: "none", fontFamily: "var(--fb)", fontSize: 13, flex: 1, color: "var(--ink)" }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)" }}><X size={12} /></button>}
        </div>
        <select className="f-sel" style={{ width: "auto" }} value={provider} onChange={e => setProvider(e.target.value)}>
          <option value="">{t("mm.allProviders")}</option><option value="mpesa">M-Pesa (Safaricom)</option><option value="mtn_momo">MTN MoMo</option><option value="airtel_money">Airtel Money</option><option value="vodacom_mpesa">Vodacom M-Pesa</option>
        </select>
        <select className="f-sel" style={{ width: "auto" }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">{t("mm.allStatus")}</option><option value="ACTIVE">✅ Active</option><option value="NONE">⏳ Pending</option><option value="CANCELLED">❌ Failed</option><option value="EXPIRED">🕐 Expired</option>
        </select>
        <select className="f-sel" style={{ width: "auto" }} value={plan} onChange={e => setPlan(e.target.value)}>
          <option value="">{t("mm.allPlans")}</option><option value="monthly">Monthly</option><option value="annual">Annual</option>
        </select>
        {(provider || status || plan || search) && <button className="btn btn-out btn-sm" onClick={reset}><RefreshCw size={12} /> {t("users.clear")}</button>}
      </div>
      <div className="card">
        <div className="card-hdr">
          <div className="card-ttl"><div className="card-ico">📱</div>{t("mm.title", { n: txs.length })}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button className="btn btn-out btn-sm" onClick={() => { exportMobileMoneyExcel(txs); toast("✅ Excel downloaded!") }}><FileSpreadsheet size={11} />XLS</button>
            <button className="btn btn-out btn-sm" onClick={() => { exportMobileMoneyCSV(txs); toast("✅ CSV downloaded!") }}><Download size={11} />CSV</button>
            <button className="btn btn-out btn-sm" onClick={() => { exportMobileMoneyPDF(txs, { provider, status, plan }); toast("✅ PDF downloaded!") }}><FileText size={11} />PDF</button>
          </div>
        </div>
        {loading ? <Spinner /> : txs.length === 0 ? <div style={{ padding: 24 }}><Empty icon="📱" message={t("mm.noMatch")} /></div> : (
          <div className="tbl-wrap"><table>
            <thead><tr><th>{t("tbl.user")}</th><th>{t("mm.provider")}</th><th>{t("mm.phone")}</th><th>{t("tbl.plan")}</th><th>{t("tbl.status")}</th><th>{t("tbl.date")}</th><th>{t("tbl.action")}</th></tr></thead>
            <tbody>{txs.map(tx => (
              <tr key={tx.id} style={{ cursor: "pointer" }} onClick={() => setSelTx(tx)}>
                <td><div style={{ fontWeight: 700, fontSize: 13 }}>{tx.user?.name ?? "—"}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{tx.user?.email}</div></td>
                <td><div style={{ display: "flex", alignItems: "center", gap: 6 }}><ProviderIcon provider={tx.mobileMoneyProvider} size={22} /><span className="badge bg-blue">{PROVIDER_LABELS[tx.mobileMoneyProvider] ?? tx.mobileMoneyProvider}</span></div></td>
                <td style={{ fontFamily: "var(--fm)", fontSize: 11 }}>{tx.mobileMoneyPhone ?? "—"}</td>
                <td><span className="badge bg-teal">{tx.plan}</span></td>
                <td><Badge status={tx.status} /></td>
                <td style={{ fontFamily: "var(--fm)", fontSize: 11 }}>{new Date(tx.createdAt).toLocaleDateString()}</td>
                <td onClick={e => e.stopPropagation()}>
                  {(tx.status === "NONE" || tx.status === "CANCELLED") && hasPermission?.('MANAGE_SUBSCRIPTIONS')
                    ? <button className="btn btn-xs" style={{ background: "var(--amber-lo)", border: "1.5px solid var(--amber)", color: "#8a5c00" }} onClick={() => setSelTx(tx)}>⚡ {t("mm.activate").replace("⚡ ","")}</button>
                    : <button className="btn btn-out btn-xs" onClick={() => setSelTx(tx)}><Eye size={10} /> {t("users.view")}</button>}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {!loading && (data?.total ?? 0) > 0 && (
        <Pagination page={page} totalPages={Math.max(1, Math.ceil((data?.total ?? 0) / 20))} total={data?.total ?? 0}
          onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
      )}
      {selTx && <MMDetailModal tx={selTx} onClose={() => setSelTx(null)} onActivate={activate} hasPermission={hasPermission} />}
    </div>
  );
}

// ─── PRICING MANAGEMENT PAGE ──────────────────────────────────────────────────

const PRICING_LABELS = {
  price_gold_monthly:      { labelKey: "pricing.labelGoldMonthly",      descKey: "pricing.descGoldMonthly",      icon: "🥇", groupKey: "pricing.groupGold" },
  price_gold_quarterly:    { labelKey: "pricing.labelGoldQuarterly",    descKey: "pricing.descGoldQuarterly",    icon: "🥇", groupKey: "pricing.groupGold" },
  price_gold_annual:       { labelKey: "pricing.labelGoldAnnual",       descKey: "pricing.descGoldAnnual",       icon: "🥇", groupKey: "pricing.groupGold" },
  price_premium_monthly:   { labelKey: "pricing.labelPremiumMonthly",   descKey: "pricing.descPremiumMonthly",   icon: "💎", groupKey: "pricing.groupPremium" },
  price_premium_quarterly: { labelKey: "pricing.labelPremiumQuarterly", descKey: "pricing.descPremiumQuarterly", icon: "💎", groupKey: "pricing.groupPremium" },
  price_premium_annual:    { labelKey: "pricing.labelPremiumAnnual",    descKey: "pricing.descPremiumAnnual",    icon: "💎", groupKey: "pricing.groupPremium" },
  price_monthly_usd:       { labelKey: "pricing.labelMonthlyUsd",   descKey: "pricing.descMonthlyUsd",    icon: "💵", groupKey: "pricing.groupSubscription" },
  price_annual_usd:        { labelKey: "pricing.labelAnnualUsd",    descKey: "pricing.descAnnualUsd",     icon: "💵", groupKey: "pricing.groupSubscription" },
  price_monthly_local:     { labelKey: "pricing.labelMonthlyLocal", descKey: "pricing.descMonthlyLocal",  icon: "🏦", groupKey: "pricing.groupSubscription" },
  price_annual_local:      { labelKey: "pricing.labelAnnualLocal",  descKey: "pricing.descAnnualLocal",   icon: "🏦", groupKey: "pricing.groupSubscription" },
  currency_local:          { labelKey: "pricing.labelCurrencyLocal",descKey: "pricing.descCurrencyLocal", icon: "🌍", groupKey: "pricing.groupSubscription" },
  local_country:           { labelKey: "pricing.labelLocalCountry", descKey: "pricing.descLocalCountry",  icon: "🏳️", groupKey: "pricing.groupSubscription" },
  trial_days:              { labelKey: "pricing.labelTrialDays",    descKey: "pricing.descTrialDays",     icon: "⏳", groupKey: "pricing.groupTrial" },
  stripe_monthly_price_id: { labelKey: "pricing.labelStripeMonthly",descKey: "pricing.descStripeMonthly", icon: "🔑", groupKey: "pricing.groupStripe" },
  stripe_annual_price_id:  { labelKey: "pricing.labelStripeAnnual", descKey: "pricing.descStripeAnnual",  icon: "🔑", groupKey: "pricing.groupStripe" },
};

// Country list with flag emoji for pricing page country selector
const COUNTRIES = [
  { code: "KE", name: "Kenya",       flag: "🇰🇪", currency: "KES" },
  { code: "TZ", name: "Tanzania",    flag: "🇹🇿", currency: "TZS" },
  { code: "UG", name: "Uganda",      flag: "🇺🇬", currency: "UGX" },
  { code: "RW", name: "Rwanda",      flag: "🇷🇼", currency: "RWF" },
  { code: "NG", name: "Nigeria",     flag: "🇳🇬", currency: "NGN" },
  { code: "GH", name: "Ghana",       flag: "🇬🇭", currency: "GHS" },
  { code: "ZA", name: "South Africa",flag: "🇿🇦", currency: "ZAR" },
  { code: "ET", name: "Ethiopia",    flag: "🇪🇹", currency: "ETB" },
  { code: "US", name: "United States",flag: "🇺🇸", currency: "USD" },
  { code: "GB", name: "United Kingdom",flag: "🇬🇧", currency: "GBP" },
];

export function PagePricing({ toast, hasPermission }) {
  const { t } = useLang();
  const canManage = hasPermission ? hasPermission('MANAGE_PRICING') : true;
  const { data, loading, error, reload } = useApi(() => adminApi.getPricing(), []);
  const [editing, setEditing] = useState(null); // { key, value }
  const [saving, setSaving] = useState(false);

  const configList = data?.config ?? [];

  // WhatsApp config — managed via dedicated card, not the generic table
  const WHATSAPP_KEYS = new Set(["whatsapp_enabled", "whatsapp_number"]);
  const waEnabled = configList.find(c => c.key === "whatsapp_enabled")?.value === "true";
  const waNumber  = configList.find(c => c.key === "whatsapp_number")?.value ?? "+250788596281";
  const [waNum, setWaNum]         = useState("");
  const [waOn, setWaOn]           = useState(true);
  const [waSaving, setWaSaving]   = useState(false);
  const [waInitDone, setWaInit]   = useState(false);
  useEffect(() => {
    if (!waInitDone && configList.length > 0) {
      setWaNum(waNumber);
      setWaOn(waEnabled);
      setWaInit(true);
    }
  }, [configList]); // eslint-disable-line

  const saveWhatsApp = async () => {
    setWaSaving(true);
    try {
      await Promise.all([
        adminApi.setPricing("whatsapp_enabled", waOn ? "true" : "false"),
        adminApi.setPricing("whatsapp_number",  waNum.trim() || "+250788596281"),
      ]);
      toast("✅ WhatsApp settings saved!");
      reload();
    } catch (e) { toast("Error: " + e.message); }
    finally { setWaSaving(false); }
  };

  // Payment toggle — managed via dedicated card
  const PAYMENT_KEYS = new Set(["allow_payment"]);
  const paymentAllowedRaw = configList.find(c => c.key === "allow_payment")?.value;
  const [payOn,      setPayOn]      = useState(true);
  const [paySaving,  setPaySaving]  = useState(false);
  const [payInitDone, setPayInit]   = useState(false);
  useEffect(() => {
    if (!payInitDone && configList.length > 0) {
      setPayOn((paymentAllowedRaw ?? "true") === "true");
      setPayInit(true);
    }
  }, [configList]); // eslint-disable-line

  const savePaymentToggle = async (newVal) => {
    setPaySaving(true);
    try {
      await adminApi.setPricing("allow_payment", newVal ? "true" : "false");
      setPayOn(newVal);
      toast(newVal ? "✅ Payments enabled." : "⚠️ Payments disabled.");
      reload();
    } catch (e) { toast("Error: " + e.message); }
    finally { setPaySaving(false); }
  };

  // Group items — exclude dedicated-card keys
  const groups = {};
  for (const item of configList) {
    if (WHATSAPP_KEYS.has(item.key)) continue;
    if (PAYMENT_KEYS.has(item.key))  continue; // shown in dedicated payment card
    const meta = PRICING_LABELS[item.key] ?? { labelKey: null, descKey: null, icon: "⚙️", groupKey: null };
    const groupLabel = meta.groupKey ? t(meta.groupKey) : "Other";
    if (!groups[groupLabel]) groups[groupLabel] = [];
    groups[groupLabel].push({
      ...item,
      icon: meta.icon,
      label: meta.labelKey ? t(meta.labelKey) : item.key,
      description: meta.descKey ? t(meta.descKey) : "",
    });
  }

  const startEdit = (item) => setEditing({ key: item.key, value: item.value ?? "" });

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await adminApi.setPricing(editing.key, editing.value);
      toast("✅ Setting saved!");
      setEditing(null);
      reload();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSaving(false); }
  };

  // Quick MRR estimate from current prices
  const monthlyPrice = parseFloat(configList.find(c => c.key === "price_monthly_usd")?.value ?? "15") || 15;
  const annualPrice  = parseFloat(configList.find(c => c.key === "price_annual_usd")?.value ?? "141") || 141;
  const cfg = (key, def) => parseFloat(configList.find(c => c.key === key)?.value ?? def) || parseFloat(def);
  const goldMonthly   = cfg("price_gold_monthly",      "9");
  const goldQuarterly = cfg("price_gold_quarterly",    "24");
  const goldAnnual    = cfg("price_gold_annual",       "84");
  const premMonthly   = cfg("price_premium_monthly",   "15");
  const premQuarterly = cfg("price_premium_quarterly", "39");
  const premAnnual    = cfg("price_premium_annual",    "141");
  const trialDays    = parseInt(configList.find(c => c.key === "trial_days")?.value ?? "7") || 7;
  const localCurrency = configList.find(c => c.key === "currency_local")?.value ?? "—";
  const localCountryCode = configList.find(c => c.key === "local_country")?.value ?? "KE";
  const localCountry = COUNTRIES.find(c => c.code === localCountryCode) ?? COUNTRIES[0];
  const [savingCountry, setSavingCountry] = useState(false);

  const setLocalCountry = async (code) => {
    setSavingCountry(true);
    try {
      await adminApi.setPricing("local_country", code);
      toast(`✅ Local country set to ${COUNTRIES.find(c => c.code === code)?.name ?? code}`);
      reload();
    } catch (e) { toast("Error: " + e.message); }
    finally { setSavingCountry(false); }
  };

  return (
    <div>
      <div className="sec-hd">
        <div>
          <div className="sec-ttl">{t("pricing.title")}</div>
          <div className="sec-sub">{t("pricing.subtitle")}</div>
        </div>
        <button className="btn btn-out btn-sm" onClick={reload}><RefreshCw size={12} /> {t("pricing.refresh")}</button>
      </div>

      {/* Country selector */}
      <div className="card" style={{ marginBottom: 16, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", background: "var(--surface)", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "var(--ink2)", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>{localCountry.flag}</span>
            {t("pricing.localCountry")} — <span style={{ color: "var(--teal)" }}>{localCountry.name}</span>
          </div>
          <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("pricing.currency", { code: localCountry.currency })}</span>
        </div>
        <div style={{ padding: "12px 16px", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {COUNTRIES.map(c => (
            <button
              key={c.code}
              onClick={() => setLocalCountry(c.code)}
              disabled={savingCountry || !canManage}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                borderRadius: 8, border: `1.5px solid ${c.code === localCountryCode ? "var(--teal)" : "var(--border)"}`,
                background: c.code === localCountryCode ? "var(--teal-dim)" : "var(--surface)",
                color: c.code === localCountryCode ? "var(--teal)" : "var(--muted)",
                cursor: "pointer", fontFamily: "var(--fb)", fontSize: 12, fontWeight: c.code === localCountryCode ? 700 : 500,
                transition: "all .15s",
              }}
            >
              <span style={{ fontSize: 16 }}>{c.flag}</span> {c.name}
              {c.code === localCountryCode && <span style={{ fontSize: 10, marginLeft: 2 }}>✓</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Price preview — Gold vs Premium */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[
          {
            tier: "Gold 🥇", color: "#b45309", bg: "#fffbeb", border: "#fcd34d",
            rows: [
              { period: "Monthly",  price: `$${goldMonthly}`,   note: "/mo" },
              { period: "3 Months", price: `$${goldQuarterly}`, note: `≈ $${(goldQuarterly/3).toFixed(2)}/mo` },
              { period: "Annual",   price: `$${goldAnnual}`,    note: `≈ $${(goldAnnual/12).toFixed(2)}/mo` },
            ],
          },
          {
            tier: "Premium 💎", color: "var(--teal)", bg: "var(--surface)", border: "var(--teal)",
            rows: [
              { period: "Monthly",  price: `$${premMonthly}`,   note: "/mo" },
              { period: "3 Months", price: `$${premQuarterly}`, note: `≈ $${(premQuarterly/3).toFixed(2)}/mo` },
              { period: "Annual",   price: `$${premAnnual}`,    note: `≈ $${(premAnnual/12).toFixed(2)}/mo` },
            ],
          },
        ].map(t => (
          <div key={t.tier} className="card" style={{ padding: 16, borderTop: `3px solid ${t.border}`, background: t.bg }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: t.color, marginBottom: 12 }}>{t.tier}</div>
            {t.rows.map(r => (
              <div key={r.period} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: 12, color: "var(--muted)" }}>{r.period}</span>
                <span style={{ fontWeight: 800, fontSize: 16, color: t.color }}>{r.price}
                  <span style={{ fontWeight: 400, fontSize: 10, color: "var(--ghost)", marginLeft: 3 }}>{r.note}</span>
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      {/* Trial & local currency */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { icon: "🎁", label: t("pricing.freeTrial"), value: `${trialDays} days`, sub: t("pricing.thenCharged") },
          { icon: localCountry.flag, label: t("pricing.localCurrency"), value: localCurrency, sub: `${localCountry.name} · ${localCountry.currency}` },
        ].map(c => (
          <div key={c.label} className="card" style={{ padding: "16px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 26, marginBottom: 6 }}>{c.icon}</div>
            <div style={{ fontFamily: "var(--fd)", fontSize: 22, fontWeight: 900, color: "var(--teal)" }}>{c.value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink)", marginTop: 2 }}>{c.label}</div>
            <div style={{ fontSize: 10, color: "var(--ghost)", marginTop: 2 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* ── WhatsApp Contact Card ─────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "14px 18px", background: "linear-gradient(135deg, #25D36611, #25D36604)", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg, #25D366, #128C7E)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0, boxShadow: "0 4px 12px #25D36640" }}>
            💬
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: "var(--ink)" }}>WhatsApp Contact</div>
            <div style={{ fontSize: 11, color: "var(--ghost)", marginTop: 2 }}>
              Control whether users can contact you via WhatsApp from the mobile app
            </div>
          </div>
          {/* Live status badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20, background: waOn ? "rgba(37,211,102,.12)" : "var(--surface)", border: `1px solid ${waOn ? "#25D36640" : "var(--border)"}` }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: waOn ? "#25D366" : "var(--ghost)", flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: waOn ? "#25D366" : "var(--ghost)" }}>
              {waOn ? "Visible to users" : "Hidden from users"}
            </span>
          </div>
        </div>

        <div style={{ padding: "18px 20px" }}>
          {/* Toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--surface)", borderRadius: 12, border: "1px solid var(--border)", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>Enable WhatsApp Button</div>
              <div style={{ fontSize: 11, color: "var(--ghost)", marginTop: 2 }}>
                When enabled, a WhatsApp floating button appears on the mobile home screen
              </div>
            </div>
            <button
              onClick={() => setWaOn(v => !v)}
              style={{
                width: 52, height: 28, borderRadius: 14, border: "none", cursor: "pointer",
                background: waOn ? "#25D366" : "var(--border)",
                position: "relative", transition: "background .2s", flexShrink: 0,
              }}
            >
              <div style={{
                position: "absolute", top: 3, left: waOn ? 26 : 3,
                width: 22, height: 22, borderRadius: "50%", background: "white",
                boxShadow: "0 1px 4px rgba(0,0,0,.25)", transition: "left .2s",
              }} />
            </button>
          </div>

          {/* Number input */}
          <div className="f-grp" style={{ marginBottom: 16 }}>
            <label className="f-lbl" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              WhatsApp Phone Number
              <span style={{ fontSize: 10, fontWeight: 400, color: "var(--ghost)" }}>E.164 format — e.g. +250788596281</span>
            </label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>📞</span>
              <input
                className="f-in"
                value={waNum}
                onChange={e => setWaNum(e.target.value)}
                placeholder="+250788596281"
                style={{ paddingLeft: 38, fontFamily: "var(--fm)", letterSpacing: ".5px" }}
              />
            </div>
            {waNum && (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#25D36612", borderRadius: 8, border: "1px solid #25D36630" }}>
                <span style={{ fontSize: 14 }}>💬</span>
                <span style={{ fontSize: 11, color: "#128C7E", fontFamily: "var(--fm)", fontWeight: 600 }}>
                  wa.me/{waNum.replace(/\D/g, "")}
                </span>
                <a href={`https://wa.me/${waNum.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                  style={{ marginLeft: "auto", fontSize: 10, color: "#25D366", textDecoration: "none", fontWeight: 700 }}>
                  Test ↗
                </a>
              </div>
            )}
          </div>

          {/* Save button */}
          {canManage && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              className="btn"
              onClick={saveWhatsApp}
              disabled={waSaving}
              style={{
                background: waSaving ? "var(--border)" : "linear-gradient(135deg, #25D366, #128C7E)",
                color: waSaving ? "var(--ghost)" : "white",
                border: "none", padding: "10px 24px", borderRadius: 10,
                fontFamily: "var(--fb)", fontSize: 13, fontWeight: 700,
                cursor: waSaving ? "not-allowed" : "pointer",
                boxShadow: waSaving ? "none" : "0 4px 14px #25D36640",
                transition: "all .2s",
              }}
            >
              {waSaving ? "Saving…" : "💾 Save WhatsApp Settings"}
            </button>
          </div>
          )}
        </div>
      </div>

      {/* ── Payment Toggle Card ───────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", background: payOn ? "linear-gradient(135deg, #10b98111, #10b98104)" : "linear-gradient(135deg, #ef444411, #ef444404)", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: payOn ? "linear-gradient(135deg, #10b981, #0d9488)" : "linear-gradient(135deg, #ef4444, #b91c1c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0, boxShadow: payOn ? "0 4px 12px #10b98140" : "0 4px 12px #ef444440" }}>
            💳
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: "var(--ink)" }}>Payment Control</div>
            <div style={{ fontSize: 12, color: "var(--ghost)", marginTop: 2 }}>
              Enable or disable all subscription and donation payments globally
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20, background: payOn ? "rgba(16,185,129,.12)" : "rgba(239,68,68,.10)", border: `1px solid ${payOn ? "#10b98140" : "#ef444440"}` }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: payOn ? "#10b981" : "#ef4444", flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: payOn ? "#10b981" : "#ef4444" }}>
              {payOn ? "Payments enabled" : "Payments disabled"}
            </span>
          </div>
        </div>

        {canManage && (
        <div style={{ padding: "18px 18px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>Allow Payments</div>
              <div style={{ fontSize: 12, color: "var(--ghost)", marginTop: 2 }}>
                When disabled, no subscription or donation payment can be initiated
              </div>
            </div>
            <button
              onClick={() => !paySaving && savePaymentToggle(!payOn)}
              disabled={paySaving}
              style={{ position: "relative", width: 50, height: 28, borderRadius: 14, border: "none", cursor: paySaving ? "not-allowed" : "pointer", padding: 0, background: payOn ? "#10b981" : "var(--border)", transition: "background .2s", flexShrink: 0 }}
              aria-label="Toggle payments"
            >
              <div style={{ position: "absolute", top: 3, left: payOn ? 25 : 3, width: 22, height: 22, borderRadius: "50%", background: "white", boxShadow: "0 1px 4px rgba(0,0,0,.25)", transition: "left .2s" }} />
            </button>
          </div>

          {!payOn && (
            <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.2)", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>
                Payments are currently disabled. Users will see an "unavailable" message when they try to subscribe or donate.
              </span>
            </div>
          )}
        </div>
        )}
      </div>

      {loading ? <div className="card" style={{ padding: 48, textAlign: "center" }}><Spinner /></div>
       : error ? (
          <div style={{ background: "var(--rose-lo)", border: "1px solid var(--rose)", borderRadius: 10, padding: 20, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "var(--rose)" }}>⚠️ {error}</div>
            <button className="btn btn-out btn-sm" style={{ marginTop: 10 }} onClick={reload}>{t("pricing.retry")}</button>
          </div>
        ) : (
          Object.entries(groups).map(([groupName, items]) => (
            <div key={groupName} className="card" style={{ marginBottom: 16, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", background: "var(--surface)", borderBottom: "1px solid var(--border)", fontWeight: 700, fontSize: 13, color: "var(--ink2)" }}>
                {groupName}
              </div>
              <div>
                {items.map(item => (
                  <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid var(--border)" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</div>
                      <div style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)", marginTop: 1 }}>{item.description}</div>
                      {item.updatedAt && <div style={{ fontSize: 9, color: "var(--ghost)", marginTop: 2 }}>{t("pricing.updated", { date: new Date(item.updatedAt).toLocaleDateString() })}</div>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontFamily: "var(--fm)", fontSize: 13, fontWeight: 700, color: item.value ? "var(--ink)" : "var(--ghost)" }}>
                        {item.value || <span style={{ color: "var(--ghost)", fontStyle: "italic", fontFamily: "inherit" }}>{t("pricing.notSet")}</span>}
                      </span>
                      {canManage && <button className="btn btn-out btn-xs" onClick={() => startEdit(item)}>{t("pricing.editBtn")}</button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

      {/* Edit modal */}
      {editing && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-ttl">
              {t("pricing.editTitle", { label: PRICING_LABELS[editing.key]?.labelKey ? t(PRICING_LABELS[editing.key].labelKey) : editing.key })}
              <CloseX onClick={() => setEditing(null)} />
            </div>
            <div style={{ marginBottom: 6, fontSize: 11, color: "var(--ghost)", fontFamily: "var(--fm)" }}>
              Key: <code>{editing.key}</code>
            </div>
            {editing.key.includes("price") && !editing.key.includes("id") && (
              <div style={{ marginBottom: 12, padding: "8px 12px", background: "var(--teal-dim)", borderRadius: 8, fontSize: 11, color: "var(--teal)" }}>
                {t("pricing.priceHint")}
              </div>
            )}
            <div className="f-grp">
              <label className="f-lbl">{PRICING_LABELS[editing.key]?.labelKey ? t(PRICING_LABELS[editing.key].labelKey) : editing.key}</label>
              <input className="f-in" value={editing.value} onChange={e => setEditing(v => ({ ...v, value: e.target.value }))}
                placeholder={editing.key.includes("price") ? "e.g. 14.00" : editing.key === "trial_days" ? "e.g. 7" : "value…"}
                style={{ fontFamily: "var(--fm)", fontSize: 14 }} autoFocus
                onKeyDown={e => e.key === "Enter" && !saving && saveEdit()} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-out" onClick={() => setEditing(null)}>{t("common.cancel")}</button>
              <button className="btn btn-teal" style={{ flex: 1 }} onClick={saveEdit} disabled={saving}>
                {saving ? t("common.saving") : t("pricing.saveBtn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CARD PAYMENTS (STRIPE) ───────────────────────────────────────────────────

const PLT_ICON = { stripe: "💳", flutterwave: "🌊", apple_iap: "🍎", google_play: "▶️", manual: "⚡", scholarship: "🎓" };
const PLAN_LABEL = (p = "") => p.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const PROVIDER_LABEL = { mpesa: "M-Pesa", mtn_momo: "MTN MoMo", airtel_money: "Airtel Money", vodacom_mpesa: "Vodacom M-Pesa" };
const getTxRef = (sub) => {
  const labels = { flutterwave: "Flutterwave Tx Ref", stripe: "Stripe Price ID", apple_iap: "RevenueCat ID", google_play: "RevenueCat ID" };
  const values = { flutterwave: sub.flutterwaveTxId, stripe: sub.stripePriceId, apple_iap: sub.revenuecatSubscriberId, google_play: sub.revenuecatSubscriberId };
  const label = labels[sub.platform] ?? "Transaction Ref";
  const value = values[sub.platform] ?? sub.flutterwaveTxId ?? sub.stripePriceId ?? sub.revenuecatSubscriberId ?? null;
  return { label, value };
};

function StripeDetailModal({ sub, onClose }) {
  const { t } = useLang();
  const fmtDate  = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const platColor = PLT_COLORS_CP[sub.platform] ?? "var(--ghost)";
  const platLabel = PLT_LABELS_CP[sub.platform] ?? sub.platform ?? "—";
  const isFlw     = sub.platform === "flutterwave";
  const isStripe  = sub.platform === "stripe";

  const InfoRow = ({ label, value, mono }) => (
    <div style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 14px", border: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 700, flexShrink: 0, paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, fontFamily: mono ? "var(--fm)" : "var(--fb)", color: "var(--ink)", textAlign: "right", wordBreak: "break-all" }}>{value ?? "—"}</div>
    </div>
  );

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 10, fontWeight: 800, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: "1px", marginTop: 6, marginBottom: 4, paddingLeft: 2 }}>{children}</div>
  );

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="modal-ttl">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>{PLT_ICON[sub.platform] ?? "💳"}</span>
            Subscription Details
          </div>
          <CloseX onClick={onClose} />
        </div>

        {/* User card */}
        <div style={{ display: "flex", gap: 14, alignItems: "center", padding: 16, background: "var(--panel-dark)", borderRadius: 12, marginBottom: 16 }}>
          {sub.user?.avatarUrl
            ? <img src={sub.user.avatarUrl} alt="" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
            : <div style={{ width: 48, height: 48, borderRadius: "50%", background: `${platColor}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 22 }}>{PLT_ICON[sub.platform] ?? "💳"}</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--fd)", fontSize: 15, fontWeight: 900, color: "white", marginBottom: 2 }}>{sub.user?.name ?? "Unknown"}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>{sub.user?.email ?? "—"}</div>
            {sub.user?.childProfile && (
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", marginTop: 3 }}>
                👶 {sub.user.childProfile.childName}{sub.user.childProfile.ageMonths ? ` · ${sub.user.childProfile.ageMonths} months` : ""}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <Badge status={sub.status} />
            <span style={{ fontSize: 9, background: platColor, color: "#fff", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>{platLabel}</span>
          </div>
        </div>

        {/* Subscription */}
        <SectionLabel>Subscription</SectionLabel>
        <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Plan"        value={PLAN_LABEL(sub.plan)} />
          <InfoRow label="Status"      value={sub.status} />
          <InfoRow label="Platform"    value={platLabel} />
          {sub.amount != null && <InfoRow label="Amount Charged" value={`${sub.currency ?? "USD"} ${Number(sub.amount).toFixed(2)}`} />}
          {sub.promoCode && <InfoRow label="Promo Code" value={sub.promoCode} mono />}
          {(sub.questionAddonUsd > 0) && <InfoRow label="Question Add-on" value={`+$${sub.questionAddonUsd}/renewal`} />}
        </div>

        {/* Billing period */}
        <SectionLabel>Billing Period</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Period Start"  value={fmtDate(sub.periodStart)} />
          <InfoRow label="Period End"    value={fmtDate(sub.periodEnd)} />
          <InfoRow label="Created"       value={fmtDate(sub.createdAt)} />
          {sub.cancelledAt && <InfoRow label="Cancelled At" value={fmtDateTime(sub.cancelledAt)} />}
        </div>

        {/* Card info (Flutterwave tokenized) */}
        {(sub.cardLast4 || sub.cardBrand) && (
          <>
            <SectionLabel>Card Used</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              {sub.cardBrand && <InfoRow label="Card Brand" value={sub.cardBrand} />}
              {sub.cardLast4 && <InfoRow label="Last 4 Digits" value={`•••• ${sub.cardLast4}`} mono />}
            </div>
          </>
        )}

        {/* Mobile Money */}
        {(sub.mobileMoneyProvider || sub.mobileMoneyPhone) && (
          <>
            <SectionLabel>Mobile Money</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              <InfoRow label="Provider" value={PROVIDER_LABEL[sub.mobileMoneyProvider] ?? sub.mobileMoneyProvider} />
              <InfoRow label="Phone"    value={sub.mobileMoneyPhone} mono />
            </div>
          </>
        )}

        {/* Transaction references */}
        <SectionLabel>Transaction Reference</SectionLabel>
        <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
          {(() => { const { label, value } = getTxRef(sub); return value ? <InfoRow label={label} value={value} mono /> : null; })()}
          <InfoRow label="Record ID" value={sub.id} mono />
        </div>

        {/* Auto-renewal status */}
        {(sub.autoRenewAttemptedAt || sub.autoRenewFailedAt) && (
          <>
            <SectionLabel>Auto-Renewal</SectionLabel>
            <div style={{ display: "grid", gap: 6 }}>
              {sub.autoRenewAttemptedAt && <InfoRow label="Last Attempt" value={fmtDateTime(sub.autoRenewAttemptedAt)} />}
              {sub.autoRenewFailedAt    && <InfoRow label="Last Failure"  value={fmtDateTime(sub.autoRenewFailedAt)} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const PLT_COLORS_CP = { stripe: "#635bff", flutterwave: "#f5a623", apple_iap: "var(--ink)", google_play: "#34a853", manual: "var(--ghost)" };
const PLT_LABELS_CP = { stripe: "Stripe", flutterwave: "Flutterwave", apple_iap: "Apple IAP", google_play: "Google Play", manual: "Manual" };

export function PageCardPayments({ toast }) {
  const { t } = useLang();
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selSub, setSelSub] = useState(null);

  useEffect(() => { setPage(1); }, [status, plan, platform]);

  const params = {
    ...(platform && { platform }),
    ...(status && { status }),
    ...(plan && { plan }),
    page, limit: 20,
  };
  const { data, loading, reload } = useApi(() => paymentsApi.stripeList(params), [platform, status, plan, page]);

  let subs = data?.subscriptions ?? [];
  if (search) {
    const q = search.toLowerCase();
    subs = subs.filter(s => s.user?.name?.toLowerCase().includes(q) || s.user?.email?.toLowerCase().includes(q));
  }

  const stats = {
    total:     data?.total ?? subs.length,
    active:    subs.filter(s => s.status === "ACTIVE").length,
    cancelled: subs.filter(s => s.status === "CANCELLED").length,
    expired:   subs.filter(s => s.status === "EXPIRED").length,
  };

  const PLATFORM_TABS = [
    { key: "",             label: "All",          color: "var(--teal)" },
    { key: "flutterwave",  label: "Flutterwave",  color: "#f5a623" },
    { key: "stripe",       label: "Stripe",       color: "#635bff" },
  ];

  return (
    <div>
      <div className="sec-hd">
        <div>
          <div className="sec-ttl" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            💳 {t("cardpay.title")}
          </div>
          <div className="sec-sub">Stripe & Flutterwave subscriptions</div>
        </div>
        <button className="btn btn-out btn-sm" onClick={reload}><RefreshCw size={12} /> {t("pricing.refresh")}</button>
      </div>

      {/* Platform filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {PLATFORM_TABS.map(p => (
          <button key={p.key} onClick={() => setPlatform(p.key)} style={{
            padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${platform === p.key ? p.color : "var(--border)"}`,
            background: platform === p.key ? p.color : "var(--surface)",
            color: platform === p.key ? "#fff" : "var(--muted)",
            fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all .15s",
          }}>{p.label}</button>
        ))}
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        {[
          { cls: "sc-blue",  lbl: t("cardpay.total"),     val: stats.total,     sub: "All subscriptions",        filter: "" },
          { cls: "sc-teal",  lbl: t("cardpay.active"),    val: stats.active,    sub: t("cardpay.payingSubscribers"), filter: "ACTIVE" },
          { cls: "sc-rose",  lbl: t("cardpay.cancelled"), val: stats.cancelled, sub: t("cardpay.churned"),        filter: "CANCELLED" },
          { cls: "sc-amber", lbl: t("users.expired"),     val: stats.expired,   sub: "Past end date",             filter: "EXPIRED" },
        ].map(s => (
          <div key={s.lbl} className={`stat-card ${s.cls}`}
            onClick={() => setStatus(status === s.filter ? "" : s.filter)}
            style={{ cursor: "pointer", outline: status === s.filter ? "2px solid white" : "none" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
            onMouseLeave={e => e.currentTarget.style.transform = ""}>
            <div className="sc-lbl">{s.lbl}</div>
            <div className="sc-val">{s.val}</div>
            <div className="sc-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 260, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input placeholder={t("common.search")} value={search} onChange={e => setSearch(e.target.value)} style={{ border: "none", background: "none", outline: "none", fontFamily: "var(--fb)", fontSize: 13, flex: 1, color: "var(--ink)" }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)" }}><X size={12} /></button>}
        </div>
        <select className="f-sel" style={{ width: "auto" }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">{t("subs.allStatus")}</option>
          <option value="ACTIVE">✅ {t("cardpay.active")}</option>
          <option value="CANCELLED">❌ {t("cardpay.cancelled")}</option>
          <option value="EXPIRED">🕐 {t("users.expired")}</option>
        </select>
        <select className="f-sel" style={{ width: "auto" }} value={plan} onChange={e => setPlan(e.target.value)}>
          <option value="">{t("subs.allPlans")}</option>
          <option value="monthly">{t("pricing.monthlyPlan")}</option>
          <option value="quarterly">3-Month</option>
          <option value="annual">{t("pricing.annualPlan")}</option>
        </select>
        {(status || plan || search) && <button className="btn btn-out btn-sm" onClick={() => { setStatus(""); setPlan(""); setSearch(""); }}><RefreshCw size={12} /> {t("users.clear")}</button>}
      </div>

      {/* Subscriptions table */}
      <div className="card">
        <div className="card-hdr">
          <div className="card-ttl"><div className="card-ico">💳</div>Subscriptions ({subs.length})</div>
        </div>
        {loading ? <Spinner /> : subs.length === 0 ? (
          <div style={{ padding: 24 }}>
            <Empty icon="💳" message={t("cardpay.noStripeFound")} />
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("tbl.user")}</th>
                  <th>{t("tbl.plan")}</th>
                  <th>Platform</th>
                  <th>{t("tbl.status")}</th>
                  <th>{t("subs.periodEnd")}</th>
                  <th>{t("tbl.action")}</th>
                </tr>
              </thead>
              <tbody>
                {subs.map(s => (
                  <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => setSelSub(s)}>
                    <td>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{s.user?.name ?? "—"}</div>
                      <div style={{ fontSize: 10, color: "var(--ghost)" }}>{s.user?.email}</div>
                    </td>
                    <td><span className="badge bg-teal">{s.plan}</span></td>
                    <td>
                      <span className="badge" style={{ background: PLT_COLORS_CP[s.platform] ?? "var(--ghost)", color: "#fff", fontSize: 9 }}>
                        {PLT_LABELS_CP[s.platform] ?? s.platform ?? "—"}
                      </span>
                    </td>
                    <td><Badge status={s.status} /></td>
                    <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>
                      {s.periodEnd ? new Date(s.periodEnd).toLocaleDateString() : "—"}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn btn-out btn-xs" onClick={() => setSelSub(s)}><Eye size={10} /> {t("users.view")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selSub && <StripeDetailModal sub={selSub} onClose={() => setSelSub(null)} />}
    </div>
  );
}

// ─── PAYMENT TRANSACTIONS ─────────────────────────────────────────────────────

const TX_STATUS_COLOR = { PENDING: "#f59e0b", SUCCESS: "#22c55e", FAILED: "#ef4444", CANCELLED: "#6b7280" };
const TX_STATUS_BG    = { PENDING: "#f59e0b22", SUCCESS: "#22c55e22", FAILED: "#ef444422", CANCELLED: "#6b728022" };

function TxStatusBadge({ status }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: TX_STATUS_BG[status] ?? "var(--surface)", color: TX_STATUS_COLOR[status] ?? "var(--ghost)", border: `1px solid ${TX_STATUS_COLOR[status] ?? "var(--border)"}` }}>
      {status}
    </span>
  );
}

function TxDetailModal({ tx, onClose }) {
  const u   = tx.user ?? {};
  const sub = u.subscription ?? {};
  const cp  = u.childProfile ?? null;

  const fmtDate     = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const platColor = PLT_COLORS_CP[tx.platform] ?? "var(--ghost)";
  const platLabel = PLT_LABELS_CP[tx.platform] ?? tx.platform ?? "—";

  const InfoRow = ({ label, value, mono, full, highlight }) => (
    <div style={{ background: highlight ? "#ef444411" : "var(--surface)", borderRadius: 8, padding: "10px 14px", border: `1px solid ${highlight ? "#ef444433" : "var(--border)"}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, gridColumn: full ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 10, color: highlight ? "#ef4444" : "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 700, flexShrink: 0, paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, fontFamily: mono ? "var(--fm)" : "var(--fb)", color: highlight ? "#ef4444" : "var(--ink)", textAlign: "right", wordBreak: "break-all" }}>{value ?? "—"}</div>
    </div>
  );

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 10, fontWeight: 800, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: "1px", marginTop: 10, marginBottom: 4, paddingLeft: 2 }}>{children}</div>
  );

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        <div className="modal-ttl">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>{PLT_ICON[tx.platform] ?? "💳"}</span>
            Transaction Details
          </div>
          <CloseX onClick={onClose} />
        </div>

        {/* ── User hero card ─────────────────────────────────── */}
        <div style={{ display: "flex", gap: 14, alignItems: "center", padding: 16, background: "var(--panel-dark)", borderRadius: 12, marginBottom: 4 }}>
          {u.avatarUrl
            ? <img src={u.avatarUrl} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid var(--teal)" }} />
            : <div style={{ width: 52, height: 52, borderRadius: "50%", background: `${platColor}33`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 24 }}>{PLT_ICON[tx.platform] ?? "👤"}</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--fd)", fontSize: 16, fontWeight: 900, color: "white", marginBottom: 2 }}>{u.name ?? "Unknown"}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)", marginBottom: 2 }}>{u.email ?? "—"}</div>
            {u.phone && <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)" }}>📞 {u.phone}</div>}
            {cp && <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", marginTop: 2 }}>👶 {cp.childName}{cp.ageMonths ? ` · ${cp.ageMonths} months` : ""}</div>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
            <TxStatusBadge status={tx.status} />
            <span style={{ fontSize: 9, background: platColor, color: "#fff", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>{platLabel}</span>
            {u.isActive === false && <span style={{ fontSize: 9, background: "#ef444422", color: "#ef4444", padding: "2px 8px", borderRadius: 20, fontWeight: 700, border: "1px solid #ef444433" }}>DEACTIVATED</span>}
          </div>
        </div>

        {/* ── User details ───────────────────────────────────── */}
        <SectionLabel>User Information</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Full Name"       value={u.name} />
          <InfoRow label="Email"           value={u.email} mono />
          {u.phone && <InfoRow label="Phone" value={u.phone} mono />}
          <InfoRow label="Account Status"  value={u.isActive === false ? "Deactivated" : "Active"} />
          <InfoRow label="Subscription"    value={u.subscriptionStatus} />
          <InfoRow label="Role"            value={u.role} />
          <InfoRow label="Registered"      value={fmtDate(u.createdAt)} />
          <InfoRow label="Last Login"      value={fmtDateTime(u.lastLoginAt)} />
        </div>

        {/* ── Child profile ──────────────────────────────────── */}
        {cp && (
          <>
            <SectionLabel>Child Profile</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              <InfoRow label="Child Name" value={cp.childName} />
              <InfoRow label="Age"        value={cp.ageMonths ? `${cp.ageMonths} months` : null} />
            </div>
            {cp.challenges?.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                {cp.challenges.map(c => (
                  <span key={c} style={{ fontSize: 10, background: "var(--teal-dim)", color: "var(--teal)", padding: "3px 8px", borderRadius: 20, border: "1px solid var(--teal-lo)", fontWeight: 600 }}>{c.replace(/_/g, " ")}</span>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Current subscription ───────────────────────────── */}
        {sub.plan && (
          <>
            <SectionLabel>Current Subscription</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
              <InfoRow label="Plan"         value={PLAN_LABEL(sub.plan)} />
              <InfoRow label="Status"       value={sub.status} />
              <InfoRow label="Platform"     value={PLT_LABELS_CP[sub.platform] ?? sub.platform} />
              <InfoRow label="Period End"   value={fmtDate(sub.periodEnd)} />
              {sub.periodStart  && <InfoRow label="Period Start" value={fmtDate(sub.periodStart)} />}
              {sub.cancelledAt  && <InfoRow label="Cancelled At" value={fmtDateTime(sub.cancelledAt)} />}
              {sub.cardBrand    && <InfoRow label="Card"         value={`${sub.cardBrand} •••• ${sub.cardLast4 ?? "—"}`} />}
              {sub.mobileMoneyProvider && <InfoRow label="Mobile Money" value={`${SUB_PROVIDER_LABEL[sub.mobileMoneyProvider] ?? sub.mobileMoneyProvider} · ${sub.mobileMoneyPhone ?? "—"}`} />}
            </div>
          </>
        )}

        {/* ── This transaction ───────────────────────────────── */}
        <SectionLabel>This Transaction</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
          <InfoRow label="Plan"     value={PLAN_LABEL(tx.plan)} />
          <InfoRow label="Platform" value={platLabel} />
          <InfoRow label="Amount"   value={tx.amount != null ? `${tx.currency ?? "USD"} ${Number(tx.amount).toFixed(2)}` : null} />
          <InfoRow label="Status"   value={tx.status} />
          <InfoRow label="Initiated" value={fmtDateTime(tx.initiatedAt)} />
          <InfoRow label="Resolved"  value={fmtDateTime(tx.resolvedAt)} />
        </div>

        {/* Failure reason */}
        {tx.status === "FAILED" && (
          <>
            <SectionLabel>Failure Reason</SectionLabel>
            <InfoRow label="Reason" value={tx.failureReason ?? "Unknown error"} full highlight />
          </>
        )}

        {/* ── References ─────────────────────────────────────── */}
        <SectionLabel>References</SectionLabel>
        <div style={{ display: "grid", gap: 6, marginBottom: 6 }}>
          {tx.txRef      && <InfoRow label={getTxRef({ platform: tx.platform, flutterwaveTxId: tx.txRef, stripePriceId: tx.txRef, revenuecatSubscriberId: tx.txRef }).label} value={tx.txRef} mono full />}
          {tx.gatewayTxId && <InfoRow label="Gateway Tx ID" value={tx.gatewayTxId} mono full />}
          <InfoRow label="Transaction Record ID" value={tx.id} mono full />
          {u.id && <InfoRow label="User ID" value={u.id} mono full />}
        </div>

        {/* ── Raw gateway response ───────────────────────────── */}
        {tx.gatewayResponse && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 10, fontWeight: 700, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: "1px", cursor: "pointer", userSelect: "none", padding: "6px 2px" }}>Raw Gateway Response</summary>
            <pre style={{ marginTop: 6, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 12, fontSize: 10, fontFamily: "var(--fm)", color: "var(--ink)", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {JSON.stringify(tx.gatewayResponse, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export function PageTransactions({ toast, search: externalSearch }) {
  const [status,   setStatus]   = useState("");
  const [platform, setPlatform] = useState("");
  const [plan,     setPlan]     = useState("");
  const [from,     setFrom]     = useState("");
  const [to,       setTo]       = useState("");
  const [search,   setSearch]   = useState(externalSearch ?? "");
  const [page,     setPage]     = useState(1);
  const [selTx,    setSelTx]    = useState(null);
  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);

  const debSearch = useDebounce(search, 400);
  const params    = { page, limit: 50, ...(status && { status }), ...(platform && { platform }), ...(plan && { plan }), ...(from && { from }), ...(to && { to }), ...(debSearch && { search: debSearch }) };
  const { data, loading, reload } = useApi(() => paymentsApi.listTransactions(params), [page, status, platform, plan, from, to, debSearch]);

  const txs        = data?.transactions ?? [];
  const total      = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 50));
  const statsArr   = data?.stats ?? [];

  const statCount = (s) => statsArr.find(x => x.status === s)?._count?.status ?? 0;

  const reset = () => { setStatus(""); setPlatform(""); setPlan(""); setFrom(""); setTo(""); setSearch(""); setPage(1); };

  const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const STAT_CARDS = [
    { label: "Total",     value: total,               color: "var(--teal)"   },
    { label: "Success",   value: statCount("SUCCESS"), color: "#22c55e"       },
    { label: "Pending",   value: statCount("PENDING"), color: "#f59e0b"       },
    { label: "Failed",    value: statCount("FAILED"),  color: "#ef4444"       },
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontFamily: "var(--fd)", fontSize: 22, fontWeight: 900, margin: 0 }}>📋 Payment Transactions</h2>
          <div style={{ fontSize: 12, color: "var(--ghost)", marginTop: 3 }}>Append-only log of every payment attempt across all gateways</div>
        </div>
        <button className="btn btn-out btn-sm" onClick={reload}><RefreshCw size={12} /> Refresh</button>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {STAT_CARDS.map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: "14px 18px", textAlign: "center" }}>
            <div style={{ fontSize: 26, fontFamily: "var(--fd)", fontWeight: 900, color }}>{loading ? "…" : value}</div>
            <div style={{ fontSize: 11, color: "var(--ghost)", marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ padding: "14px 16px", marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <select className="f-sel" value={status}   onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ width: "auto" }}>
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILED">Failed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <select className="f-sel" value={platform} onChange={e => { setPlatform(e.target.value); setPage(1); }} style={{ width: "auto" }}>
          <option value="">All Platforms</option>
          <option value="flutterwave">Flutterwave</option>
          <option value="stripe">Stripe</option>
          <option value="apple_iap">Apple IAP</option>
          <option value="google_play">Google Play</option>
          <option value="manual">Manual</option>
        </select>
        <select className="f-sel" value={plan}     onChange={e => { setPlan(e.target.value); setPage(1); }} style={{ width: "auto" }}>
          <option value="">All Plans</option>
          <option value="premium_monthly">Premium Monthly</option>
          <option value="premium_quarterly">Premium Quarterly</option>
          <option value="premium_annual">Premium Annual</option>
          <option value="gold_monthly">Gold Monthly</option>
          <option value="gold_quarterly">Gold Quarterly</option>
          <option value="gold_annual">Gold Annual</option>
        </select>
        <input type="date" className="f-in" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} title="From date" style={{ width: 150 }} />
        <input type="date" className="f-in" value={to}   onChange={e => { setTo(e.target.value);   setPage(1); }} title="To date"   style={{ width: 150 }} />
        {(status || platform || plan || from || to || search) && (
          <button className="btn btn-out btn-sm" onClick={reset}>Clear</button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--ghost)" }}>{total} record{total !== 1 ? "s" : ""}</div>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>User</th>
                <th>Platform</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Failure Reason</th>
                <th>Initiated</th>
                <th>Resolved</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ textAlign: "center", padding: 32, color: "var(--ghost)" }}>Loading…</td></tr>}
              {!loading && txs.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", padding: 32, color: "var(--ghost)" }}>No transactions found</td></tr>}
              {txs.map(tx => (
                <tr key={tx.id} style={{ cursor: "pointer" }} onClick={() => setSelTx(tx)}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {tx.user?.avatarUrl
                        ? <img src={tx.user.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                        : <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>{PLT_ICON[tx.platform] ?? "💳"}</div>
                      }
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{tx.user?.name ?? "—"}</div>
                        <div style={{ fontSize: 10, color: "var(--ghost)" }}>{tx.user?.email ?? "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td><span style={{ fontSize: 10, background: `${PLT_COLORS_CP[tx.platform] ?? "var(--ghost)"}22`, color: PLT_COLORS_CP[tx.platform] ?? "var(--ghost)", padding: "3px 8px", borderRadius: 20, fontWeight: 700 }}>{PLT_LABELS_CP[tx.platform] ?? tx.platform}</span></td>
                  <td style={{ fontSize: 11 }}>{PLAN_LABEL(tx.plan)}</td>
                  <td style={{ fontSize: 12, fontWeight: 700, fontFamily: "var(--fm)" }}>{tx.amount != null ? `${tx.currency ?? "USD"} ${Number(tx.amount).toFixed(2)}` : "—"}</td>
                  <td><TxStatusBadge status={tx.status} /></td>
                  <td style={{ fontSize: 11, color: "#ef4444", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.failureReason ?? "—"}</td>
                  <td style={{ fontSize: 11, color: "var(--ghost)", whiteSpace: "nowrap" }}>{fmtDateTime(tx.initiatedAt)}</td>
                  <td style={{ fontSize: 11, color: "var(--ghost)", whiteSpace: "nowrap" }}>{fmtDateTime(tx.resolvedAt)}</td>
                  <td><button className="btn btn-out btn-sm" onClick={e => { e.stopPropagation(); setSelTx(tx); }}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
            <button className="btn btn-out btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ fontSize: 12, color: "var(--ghost)" }}>Page {page} / {totalPages}</span>
            <button className="btn btn-out btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {selTx && <TxDetailModal tx={selTx} onClose={() => setSelTx(null)} />}
    </div>
  );
}

// ─── MANUAL PAYMENTS PAGE ────────────────────────────────────────────────────

const MP_STATUS = {
  PENDING:  { label: "Pending",  color: "#d97706", bg: "#fef9c3", Icon: Clock },
  APPROVED: { label: "Approved", color: "#059669", bg: "#d1fae5", Icon: CheckCircle },
  REJECTED: { label: "Rejected", color: "#dc2626", bg: "#fee2e2", Icon: XCircle },
};

function MpBadge({ status }) {
  const cfg = MP_STATUS[status] ?? MP_STATUS.PENDING;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px",
      borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color,
    }}>
      <cfg.Icon size={11} /> {cfg.label}
    </span>
  );
}

function ManualPaymentDetailModal({ payment: p, onClose, onApprove, onReject, toast }) {
  const [view, setView]         = useState("detail"); // "detail" | "approve" | "reject"
  const [adminNotes, setAdminNotes] = useState("");
  const [reason, setReason]     = useState("");
  const [busy, setBusy]         = useState(false);

  const doApprove = async () => {
    if (!adminNotes.trim()) { toast("Please enter a comment before approving"); return; }
    setBusy(true);
    try {
      await manualPaymentsApi.approve(p.id, { adminNotes });
      toast("✅ Payment approved — subscription activated");
      onApprove();
      onClose();
    } catch (e) { toast(`❌ ${e.message}`); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!reason.trim()) { toast("Please enter a rejection reason"); return; }
    setBusy(true);
    try {
      await manualPaymentsApi.reject(p.id, { reason, adminNotes });
      toast("Receipt rejected — user will be notified");
      onReject();
      onClose();
    } catch (e) { toast(`❌ ${e.message}`); }
    finally { setBusy(false); }
  };

  const fmtDate = d => d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 540, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="modal-ttl">
          <span>Manual Payment — {p.txRef}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 4 }}><X size={16} /></button>
        </div>

        {view === "detail" && (
          <>
            {/* User info */}
            <div style={{ background: "var(--surface)", borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>{p.user?.name}</div>
              <div style={{ color: "var(--muted)" }}>{p.user?.email}</div>
              <div style={{ marginTop: 6 }}>
                <MpBadge status={p.status} />
              </div>
            </div>

            {/* Payment details grid */}
            {[
              { label: "Tx reference", value: p.txRef, mono: true },
              { label: "Plan",         value: p.plan?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) },
              { label: "Amount",       value: `${p.currency} ${Number(p.amount).toLocaleString()}` },
              { label: "Submitted",    value: fmtDate(p.createdAt) },
              { label: "Resolved",     value: fmtDate(p.resolvedAt) },
              { label: "Reviewer",     value: p.reviewer?.name ?? "—" },
              { label: "User notes",   value: p.notes || "—" },
              { label: "Admin notes",  value: p.adminNotes || "—" },
              ...(p.rejectionReason ? [{ label: "Rejection reason", value: p.rejectionReason, red: true }] : []),
            ].map(r => (
              <div key={r.label} style={{ display: "flex", gap: 12, fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: "var(--muted)", minWidth: 130, flexShrink: 0 }}>{r.label}</span>
                <span style={{
                  fontWeight: 600,
                  fontFamily: r.mono ? "var(--mono, monospace)" : undefined,
                  color: r.red ? "#dc2626" : undefined,
                }}>{r.value}</span>
              </div>
            ))}

            {/* Receipt */}
            {p.receiptUrl && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 6 }}>Payment receipt</div>
                {p.receiptUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                  <img
                    src={p.receiptUrl} alt="Receipt"
                    style={{ width: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 8, border: "1px solid var(--border)", cursor: "pointer" }}
                    onClick={() => window.open(p.receiptUrl, "_blank")}
                  />
                ) : (
                  <a href={p.receiptUrl} target="_blank" rel="noreferrer" style={{
                    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
                    borderRadius: 8, border: "1px solid var(--border)", fontSize: 13, color: "var(--teal)",
                    textDecoration: "none", fontWeight: 600,
                  }}>
                    <ExternalLink size={13} /> View receipt PDF
                  </a>
                )}
              </div>
            )}

            {/* Actions (only for PENDING) */}
            {p.status === "PENDING" && (
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button className="btn btn-out" style={{ flex: 1, color: "#dc2626", borderColor: "#dc2626" }} onClick={() => setView("reject")}>
                  <XCircle size={13} /> Reject
                </button>
                <button className="btn btn-teal" style={{ flex: 1 }} onClick={() => setView("approve")}>
                  <CheckCircle size={13} /> Approve & Activate
                </button>
              </div>
            )}
          </>
        )}

        {view === "approve" && (
          <>
            <div style={{ textAlign: "center", padding: "16px 0 20px" }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#d1fae5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <CheckCircle size={24} color="#059669" />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Approve payment?</div>
              <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>
                This will activate <strong>{p.user?.name}'s</strong> subscription for plan{" "}
                <strong>{p.plan?.replace(/_/g, " ")}</strong> and send them a confirmation email.
              </div>
            </div>
            <div className="f-grp">
              <label className="f-lbl">Comment *</label>
              <textarea className="f-ta" rows={3} value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="e.g. Receipt verified, bank confirmed transfer" />
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button className="btn btn-out" style={{ flex: 1 }} onClick={() => setView("detail")} disabled={busy}>Cancel</button>
              <button className="btn btn-teal" style={{ flex: 1 }} onClick={doApprove} disabled={busy || !adminNotes.trim()}>
                <CheckCircle size={13} /> {busy ? "Activating…" : "Confirm approval"}
              </button>
            </div>
          </>
        )}

        {view === "reject" && (
          <>
            <div style={{ textAlign: "center", padding: "16px 0 20px" }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <XCircle size={24} color="#dc2626" />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Reject this receipt?</div>
              <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5 }}>The user will see your rejection reason and can submit a new receipt.</div>
            </div>
            <div className="f-grp">
              <label className="f-lbl">Comment *</label>
              <textarea className="f-ta" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Receipt image is unclear — please resubmit with a clearer photo" />
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button className="btn btn-out" style={{ flex: 1 }} onClick={() => setView("detail")} disabled={busy}>Cancel</button>
              <button className="btn btn-out" style={{ flex: 1, color: "#dc2626", borderColor: "#dc2626" }} onClick={doReject} disabled={busy || !reason.trim()}>
                <XCircle size={13} /> {busy ? "Rejecting…" : "Confirm rejection"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PageManualPayments({ toast, hasPermission }) {
  const [status, setStatus]     = useState("ALL");
  const [search, setSearch]     = useState("");
  const [page, setPage]         = useState(1);
  const [selected, setSelected] = useState(null);
  const [fullDetail, setFullDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const debSearch = useDebounce(search, 400);
  const limit = 20;

  const params = {
    page, limit,
    ...(status !== "ALL" && { status }),
    ...(debSearch && { search: debSearch }),
  };

  const { data, loading, reload } = useApi(() => manualPaymentsApi.list(params), [page, status, debSearch]);

  const items      = data?.items      ?? [];
  const total      = data?.total      ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const openDetail = async (item) => {
    setLoadingDetail(true);
    setSelected(item);
    try {
      const d = await manualPaymentsApi.getById(item.id);
      setFullDetail(d.payment);
    } catch { toast("Failed to load payment detail"); setSelected(null); }
    finally { setLoadingDetail(false); }
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 22, margin: 0 }}>Manual Payments</h1>
          <p style={{ color: "var(--muted)", fontSize: 13, margin: "4px 0 0" }}>
            Bank-transfer receipts submitted by users for manual review
          </p>
        </div>
        <button className="btn btn-out btn-sm" onClick={reload}><RefreshCw size={13} /></button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" }} />
          <input
            className="f-in" style={{ paddingLeft: 32, margin: 0 }}
            placeholder="Search by name, email or reference…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        {["ALL", "PENDING", "APPROVED", "REJECTED"].map(s => (
          <button
            key={s}
            className={`btn btn-sm ${status === s ? "btn-teal" : "btn-out"}`}
            onClick={() => { setStatus(s); setPage(1); }}
          >
            {s === "ALL" ? "All" : MP_STATUS[s]?.label ?? s}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: "hidden", padding: 0 }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--muted)" }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🧾</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>No manual payments</div>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>
              {status === "PENDING" ? "No receipts are waiting for review." : "No payments match your filters."}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
                  {["Reference", "User", "Plan", "Amount", "Status", "Submitted", ""].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id} style={{ borderBottom: "1px solid var(--border)", background: p.status === "PENDING" ? "var(--amber-lo, #fffbeb)" : undefined }}>
                    <td style={{ padding: "12px 14px", fontFamily: "var(--mono, monospace)", fontWeight: 700, fontSize: 12, color: "var(--teal)" }}>{p.txRef}</td>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ fontWeight: 600 }}>{p.user?.name}</div>
                      <div style={{ color: "var(--muted)", fontSize: 11 }}>{p.user?.email}</div>
                    </td>
                    <td style={{ padding: "12px 14px" }}>{p.plan?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</td>
                    <td style={{ padding: "12px 14px", fontWeight: 600 }}>{p.currency} {Number(p.amount).toLocaleString()}</td>
                    <td style={{ padding: "12px 14px" }}><MpBadge status={p.status} /></td>
                    <td style={{ padding: "12px 14px", color: "var(--muted)" }}>{fmtDate(p.createdAt)}</td>
                    <td style={{ padding: "12px 14px" }}>
                      <button className="btn btn-out btn-xs" onClick={() => openDetail(p)}>
                        <Eye size={11} /> Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Showing {items.length} of {total}</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-out btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
              <button className="btn btn-teal btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && fullDetail && (
        <ManualPaymentDetailModal
          payment={fullDetail}
          onClose={() => { setSelected(null); setFullDetail(null); }}
          onApprove={reload}
          onReject={reload}
          toast={toast}
        />
      )}
      {selected && loadingDetail && (
        <div className="modal-ov open"><div className="modal" style={{ textAlign: "center", padding: 48 }}>Loading…</div></div>
      )}
    </div>
  );
}
